// ─────────────────────────────────────────────────────────────────────────────
// Birdie Brains v3 — now with streaks + habit tasks
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  pointerWithin,
  closestCorners,
  MeasuringStrategy,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Canvas from "./Canvas";

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_KEY;
const supabase          = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg: "#0f0f14", surface: "#16161f", card: "#1c1c28", border: "#2a2a3d",
  accent: "#7c6af7", accentSoft: "#a99bf9",
  green: "#4ade80", orange: "#fb923c", red: "#f87171", yellow: "#fbbf24",
  text: "#e8e8f0", muted: "#7878a0",
};

const PRIORITIES = {
  now:     { label: "🔥 Do Now",   color: C.red,    bg: "#2d1a1a" },
  next:    { label: "⚡ Do Next",  color: C.yellow, bg: "#1f1c0a" },
  later:   { label: "🌙 Do Later", color: C.accent, bg: "#1a1830" },
  someday: { label: "💭 Someday",  color: C.muted,  bg: "#1a1a26" },
};

// The same default columns KanbanBoard uses when a board has none saved
const DEFAULT_KANBAN_COLUMNS = [
  { id: "future", label: "💭 Future / Ideas", color: "accentSoft" },
  { id: "progress", label: "⚡ In Progress", color: "yellow" },
  { id: "shipped", label: "✅ Shipped", color: "green" },
];

// Resolve a board's real columns, preferring what the board view in THIS
// browser most recently saved (localStorage — written synchronously on every
// column change), then the config saved in Supabase, then the defaults.
const boardColumnsFor = (board) => {
  if (!board) return [];
  try {
    const local = JSON.parse(localStorage.getItem("birdie_kanban_cols_" + board.id) || "null");
    if (Array.isArray(local) && local.length > 0) return local;
  } catch { /* corrupt localStorage — fall through */ }
  if (Array.isArray(board.columns_config) && board.columns_config.length > 0) {
    return board.columns_config;
  }
  return DEFAULT_KANBAN_COLUMNS;
};

// Every board gets its own emoji, shown separately from its title (like a
// Notion page icon) — this is what new boards default to before anyone
// changes it.
const DEFAULT_BOARD_EMOJI = "📋";

// Splits a leading emoji off the front of a title, e.g. "🚀 Main Roadmap" ->
// { emoji: "🚀", rest: "Main Roadmap" }. Used both when creating a board (in
// case someone types an emoji themselves) and to migrate older boards that
// don't have a separate emoji slot yet — a title that doesn't cleanly start
// with one just falls through untouched, with the default emoji assigned
// alongside it instead of guessing.
const EMOJI_PREFIX_RE = /^(\p{Extended_Pictographic}(?:\u200d\p{Extended_Pictographic})*\uFE0F?)\s*/u;
const splitLeadingEmoji = (title) => {
  const m = (title || "").match(EMOJI_PREFIX_RE);
  if (!m) return { emoji: null, rest: title || "" };
  return { emoji: m[1], rest: title.slice(m[0].length) };
};

// The board-icon field is an emoji slot, not a text field — whatever gets
// typed or pasted into it, keep only the first emoji found and drop
// everything else (so plain letters just never appear at all).
const EMOJI_ONLY_RE = /\p{Extended_Pictographic}(?:\u200d\p{Extended_Pictographic})*\uFE0F?/u;
const onlyEmoji = (value) => {
  const m = (value || "").match(EMOJI_ONLY_RE);
  return m ? m[0] : "";
};

// A small curated set for the board-icon popover, not the full Unicode emoji
// list \u2014 click one and it's applied immediately. The "type/paste any emoji"
// box underneath covers anything not in here.
const BOARD_EMOJI_CHOICES = [
  "\uD83D\uDCCB", "\uD83D\uDE80", "\uD83D\uDCA1", "\uD83D\uDC97", "\uD83D\uDD25", "\uD83C\uDFA8", "\uD83E\uDDE0", "\u2B50\uFE0F",
  "\uD83D\uDCB0", "\uD83D\uDC40", "\uD83D\uDCAC", "\uD83D\uDD14", "\uD83E\uDDE9", "\uD83C\uDFAE", "\uD83D\uDEDC", "\uD83D\uDEE0\uFE0F",
  "\uD83D\uDC51", "\uD83E\uDD84", "\u26A1\uFE0F", "\u2708\uFE0F", "\uD83D\uDC95", "\uD83C\uDF40", "\uD83C\uDFC6", "\uD83D\uDCE1",
  "\u203C\uFE0F", "\uD83D\uDCE2", "\uD83C\uDF08", "\uD83D\uDC8E", "\uD83D\uDC8C", "\uD83C\uDF3B", "\uD83D\uDC7D", "\uD83D\uDD2E",
  "\uD83D\uDCDA", "\uD83D\uDCC5", "\uD83C\uDFE0", "\uD83C\uDF89", "\uD83C\uDF38", "\uD83E\uDD42", "\uD83E\uDD51", "\uD83C\uDF37",
];

// Click-to-pick emoji popover, shared by both places a board's icon can be
// changed (the dashboard tile and the in-board header). Positions itself
// relative to whatever wraps it, so the caller just needs `position:
// relative` on that wrapper.
function EmojiPickerPopover({ onPick, onClose }) {
  const [customInput, setCustomInput] = useState("");
  const popoverRef = useRef(null);
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [onClose]);
  return (
    <div
      ref={popoverRef}
      onClick={e => e.stopPropagation()}
      style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 50, background: C.card, border: "1px solid " + C.border, borderRadius: 10, padding: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.3)", width: 224 }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 2, marginBottom: 8 }}>
        {BOARD_EMOJI_CHOICES.map(choice => (
          <button
            key={choice}
            onClick={() => onPick(choice)}
            title={choice}
            style={{ background: "none", border: "none", fontSize: 17, lineHeight: 1, cursor: "pointer", borderRadius: 6, padding: 5 }}
            onMouseEnter={e => e.currentTarget.style.background = C.bg}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >{choice}</button>
        ))}
      </div>
      <input
        value={customInput}
        onChange={e => setCustomInput(onlyEmoji(e.target.value))}
        onKeyDown={e => {
          if (e.key === "Enter" && customInput) onPick(customInput);
          if (e.key === "Escape") onClose();
        }}
        placeholder="Or type/paste any emoji…"
        style={{ width: "100%", boxSizing: "border-box", fontSize: 13, background: C.bg, color: C.text, border: "1px solid " + C.border, borderRadius: 6, padding: "5px 8px", outline: "none" }}
      />
    </div>
  );
}

// `sample: true` marks these as local-only placeholder content for an empty
// task list — they're deliberately excluded from the Supabase sync below so
// they never get persisted (and can't later get duplicated onto a real
// account via a guest merge, or onto any brand-new account at all).
const makeSampleTasks = () => {
  const base = Date.now();
  return [
    { id: `${base}-1`, text: "Reply to one message you've been putting off", priority: "now",     done: false, project: "General", is_habit: false, last_completed_date: null, sort_order: 0, created_at: new Date().toISOString(), sample: true },
    { id: `${base}-5`, text: "Refill water bottle",                             priority: "next",    done: false, project: "General", is_habit: true,  last_completed_date: null, sort_order: 1, created_at: new Date().toISOString(), sample: true },
    { id: `${base}-2`, text: "Write down 3 things you want to get done today",  priority: "next",    done: false, project: "General", is_habit: false, last_completed_date: null, sort_order: 2, created_at: new Date().toISOString(), sample: true },
    { id: `${base}-3`, text: "Tidy up one small area",                          priority: "later",   done: false, project: "General", is_habit: false, last_completed_date: null, sort_order: 3, created_at: new Date().toISOString(), sample: true },
    { id: `${base}-4`, text: "Learn something new for 10 minutes",              priority: "someday", done: false, project: "General", is_habit: false, last_completed_date: null, sort_order: 4, created_at: new Date().toISOString(), sample: true },
  ];
};

// ── Shared styles ─────────────────────────────────────────────────────────────
const inputStyle = {
  background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
  color: C.text, fontSize: 14, padding: "9px 13px", outline: "none",
  fontFamily: "inherit", width: "100%", boxSizing: "border-box",
};
// Shared grip styling for draggable rows in the Nest task list — mirrors the
// kanban card grip geometry exactly: the card's left padding is only 4px and
// the grip tucks 3px further, so it sits flush against the card's edge with
// a tight 7px gap to the content after it.
const nestHandleStyle = {
  color: C.muted, cursor: "grab", flexShrink: 0,
  fontSize: 13, lineHeight: 1, padding: "6px 3px", margin: "-6px -6px -6px -3px",
  userSelect: "none", touchAction: "none",
};

// Kanban cards use paddingLeft: 4 so the grip hugs the edge — same here.
// The right side stays tight too so the pill and action icons sit close
// to the card's edge.
const nestRowPadding = "10px 8px 10px 4px";

// Shared edit/delete buttons so task rows and board-card rows look identical
const rowEditBtn = {
  background: "transparent", border: "none", color: C.muted,
  cursor: "pointer", fontSize: 14, padding: "0 4px", lineHeight: 1,
};
const rowDeleteBtn = {
  background: "transparent", border: "none", color: C.red,
  cursor: "pointer", fontSize: 16, padding: "0 4px", lineHeight: 1,
};

// A <select> with its native arrow hidden and a small chevron placed just
// inside the right edge, so the arrow doesn't sit awkwardly against the border.
function NiceSelect({ value, onChange, width = 160, accent = C.border, children }) {
  return (
    <div style={{ position: "relative", width, flexShrink: 0 }}>
      <select value={value} onChange={onChange} style={{
        ...inputStyle, width: "100%", cursor: "pointer",
        appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
        paddingRight: 30, borderColor: accent,
      }}>
        {children}
      </select>
      <span style={{
        position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none", color: C.muted, fontSize: 10, lineHeight: 1,
      }}>▾</span>
    </div>
  );
}
function btnStyle(color) {
  return {
    background: color + "22", border: `1px solid ${color}60`, color,
    borderRadius: 8, padding: "9px 18px", fontSize: 14, cursor: "pointer",
    fontWeight: 600, fontFamily: "inherit",
  };
}

// ── Streak helpers ────────────────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().slice(0, 10);
const yesterdayStr = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

// ── Auth screen ───────────────────────────────────────────────────────────────
// Doubles as (a) a standalone full-page login for the anon-sign-in-failed
// fallback, and (b) a "sign in to save" panel opened from within the app by
// a guest. In the guest case, submitting doesn't create a brand new session —
// it either upgrades the current anonymous session in place (same uid, so
// existing boards/tasks stay attached automatically) or, when the guest
// actually already has an account, hands off to that account and brings the
// guest's data along via a one-time merge ticket. See create_merge_ticket /
// redeem_merge_ticket in Supabase.
function AuthScreen({ onClose, fullPage = false, initialError = null }) {
  const [mode, setMode]       = useState("login");
const [email, setEmail]     = useState("");
const [pass, setPass]       = useState("");
const [error, setError]     = useState(initialError || "");
const [loading, setLoading] = useState(false);
const [resetSent, setResetSent] = useState(false);

  // Stash a merge ticket for the *current* anonymous session before handing off
  // to a different, already-existing account, so that account's next load can
  // pull the guest's board/tasks over. No-op for a real (non-guest) user.
  const startMergeTicket = async () => {
    const { data: { user: current } } = await supabase.auth.getUser();
    if (!current?.is_anonymous) return;
    const { data: token, error: err } = await supabase.rpc("create_merge_ticket");
    if (err) { console.error("create merge ticket:", err.message); return; }
    sessionStorage.setItem("birdie_merge_token", token);
  };

  const submit = async () => {
    setError(""); setLoading(true);
    if (mode === "signup") {
      // Upgrade the current guest session into a permanent one — same uid.
      const { error: err } = await supabase.auth.updateUser({ email, password: pass });
      setLoading(false);
      if (err) {
        if (/registered|exists/i.test(err.message)) {
          setMode("login");
          setError("That email already has a Birdie Brains account — log in and we'll bring your guest board over.");
        } else {
          setError(err.message);
        }
        return;
      }
      onClose && onClose();
      return;
    }
    // Logging into a (possibly different, already-existing) account.
    await startMergeTicket();
    const { error: err } = await supabase.auth.signInWithPassword({ email, password: pass });
    setLoading(false);
    if (err) { sessionStorage.removeItem("birdie_merge_token"); setError(err.message); return; }
    onClose && onClose();
  };

  // OAuth is never ambiguous the way email/password is — a Twitch/Google
  // identity either already has an account or it doesn't — and
  // signInWithOAuth already handles both by itself (creating a new account
  // or signing into the existing one), in a single round-trip. An earlier
  // version tried linkIdentity() first and only fell back to this on
  // failure, which meant every guest whose Twitch/Google already belonged
  // to their real account — i.e. anyone actually using their own established
  // account — had to approve the OAuth prompt twice. Always going straight
  // through here avoids that; the merge (if this guest session prepared
  // one) picks up whatever account results either way.
  const signInWithOAuthProvider = async (provider) => {
    setError("");
    const { data: { user: current } } = await supabase.auth.getUser();
    if (current?.is_anonymous) await startMergeTicket();
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider, options: { redirectTo: window.location.origin },
    });
    if (err) { sessionStorage.removeItem("birdie_merge_token"); setError(err.message); }
  };

  const signInWithTwitch = () => signInWithOAuthProvider("twitch");
  const signInWithGoogle = () => signInWithOAuthProvider("google");

  const sendReset = async () => {
    setError(""); setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://birdiebrains.com/reset-password",
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setResetSent(true);
  };

  const card = (
      <div style={{
        position: "relative",
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
        padding: 40, width: "100%", maxWidth: 380,
        display: "flex", flexDirection: "column", gap: 20,
      }}>
        {onClose && (
          <button onClick={onClose} aria-label="Close" style={{
            position: "absolute", top: 12, right: 12, background: "transparent",
            border: "none", color: C.muted, fontSize: 18, cursor: "pointer", lineHeight: 1,
          }}>×</button>
        )}
        <div>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase" }}>
            🐦 Birdie Brains
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text, marginTop: 4 }}>
            {mode === "login" ? "Sign in to save 👋" : mode === "forgot" ? "Reset password 🔑" : "Create account ✨"}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button onClick={signInWithTwitch} style={{
          background: "#9146ff22", border: "1px solid #9146ff60",
          color: "#9146ff", borderRadius: 8, padding: "10px 18px",
          fontSize: 14, cursor: "pointer", fontWeight: 600,
          fontFamily: "inherit", display: "flex", alignItems: "center",
          justifyContent: "center", gap: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#9146ff">
            <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
          </svg>
          Continue with Twitch
        </button>

        <button onClick={signInWithGoogle} style={{
          background: "#ffffff15", border: "1px solid #ffffff30",
          color: C.text, borderRadius: 8, padding: "10px 18px",
          fontSize: 14, cursor: "pointer", fontWeight: 600,
          fontFamily: "inherit", display: "flex", alignItems: "center",
          justifyContent: "center", gap: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ color: C.muted, fontSize: 12 }}>or</span>
          <div style={{ flex: 1, height: 1, background: C.border }} />
        </div>

        {mode === "forgot" ? (
          resetSent ? (
            <div style={{ color: C.green, fontSize: 14, textAlign: "center", lineHeight: 1.6 }}>
              ✅ Check your email! We sent a reset link to <strong>{email}</strong>
            </div>
          ) : (
            <>
              <input value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Your email address" type="email" style={inputStyle} />
              {error && <div style={{ color: C.red, fontSize: 13 }}>{error}</div>}
              <button onClick={sendReset} disabled={loading} style={btnStyle(C.accent)}>
                {loading ? "..." : "Send reset link"}
              </button>
            </>
          )
        ) : (
          <>
            <input value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Email" type="email" style={inputStyle} />
            <input value={pass} onChange={e => setPass(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="Password" type="password" style={inputStyle} />
            {error && <div style={{ color: C.red, fontSize: 13 }}>{error}</div>}
            <button onClick={submit} disabled={loading} style={btnStyle(C.accent)}>
              {loading ? "..." : mode === "login" ? "Log in" : "Sign up"}
            </button>
            {mode === "login" && (
              <div style={{ textAlign: "center" }}>
                <span
                  onClick={() => setMode("forgot")}
                  style={{ color: C.accentSoft, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}
                >
                  Forgot your password?
                </span>
              </div>
            )}
          </>
        )}
        <div style={{ color: C.muted, fontSize: 13, textAlign: "center" }}>
          {mode === "forgot" ? (
            <span onClick={() => { setMode("login"); setResetSent(false); setError(""); }}
              style={{ color: C.accentSoft, cursor: "pointer", textDecoration: "underline" }}>
              ← Back to login
            </span>
          ) : (
            <>
              {mode === "login" ? "No account? " : "Already have one? "}
              <span onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }}
                style={{ color: C.accentSoft, cursor: "pointer", textDecoration: "underline" }}>
                {mode === "login" ? "Sign up" : "Log in"}
              </span>
            </>
          )}
        </div>
      </div>
  );

  if (fullPage) {
    return (
      <div style={{
        minHeight: "100vh", background: "#111111", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
      }}>
        {card}
      </div>
    );
  }
  return card;
}

// ── Task Row ──────────────────────────────────────────────────────────────────
// A board card shown inside the Nest task list. Looks like a TaskRow, but carries
// a tag naming its board and routes its actions back to the board.
// ── Nest task list drag and drop ──────────────────────────────────────────────
// Rows are prefixed so a board card can never collide with a task id.
const TASK_PREFIX = "ntask-";
const BCARD_PREFIX = "nbcard-";

function SortableNestRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    position: "relative",
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

// Lets an empty priority group still accept a dropped row
function PriorityDropZone({ priorityKey, children }) {
  const { setNodeRef } = useDroppable({ id: "npri-" + priorityKey });
  return <div ref={setNodeRef} style={{ minHeight: 8 }}>{children}</div>;
}

function BoardCardRow({ card, columnLabel, doneColumn, onComplete, onRemove, onSave, dragHandleProps, onOpenBoard, flash, boards = [] }) {
  // Matches the card's board by name to show that board's own icon here — a
  // custom one (a star, whatever) if it's been set, the default clipboard
  // otherwise, same as the board itself shows.
  const boardEmoji = boards.find(b => b.name === card.project)?.emoji || DEFAULT_BOARD_EMOJI;
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(card.text);
  const [editPriority, setEditPriority] = useState(card.nest_priority || "next");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showBoardTip, setShowBoardTip] = useState(false); // instant hover tooltip for the board icon
  const removeTimer = useRef(null);
  const armRemove = () => {
    setConfirmRemove(true);
    clearTimeout(removeTimer.current);
    removeTimer.current = setTimeout(() => setConfirmRemove(false), 2500);
  };
  useEffect(() => () => clearTimeout(removeTimer.current), []);
  const p = PRIORITIES[card.nest_priority] || PRIORITIES.next;

  const commit = () => {
    const trimmed = editText.trim();
    if (trimmed && onSave) {
      onSave(card, { text: trimmed, nest_priority: editPriority });
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <div
        onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) commit(); }}
        style={{
          background: C.card, border: `1px solid ${C.accent}`, borderRadius: 8,
          padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <input value={editText} onChange={e => setEditText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
          autoFocus style={{ ...inputStyle, fontSize: 14 }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <NiceSelect value={editPriority} onChange={e => setEditPriority(e.target.value)}>
            {Object.entries(PRIORITIES).map(([k, pr]) =>
              <option key={k} value={k}>{pr.label}</option>)}
          </NiceSelect>
          <span style={{ color: C.muted, fontSize: 11 }}>
            {boardEmoji} {card.project}{columnLabel ? ` · ${columnLabel}` : ""}
          </span>
        </div>
        <div style={{ color: C.muted, fontSize: 10 }}>
          Edits here update the card on its board too.
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={commit} style={btnStyle(C.green)}>Save</button>
          <button onClick={() => setEditing(false)} style={btnStyle(C.muted)}>Cancel</button>
        </div>
      </div>
    );
  }

  const actions = (
    <div style={{ display: "flex", gap: 4, flexShrink: 0, alignItems: "center" }}>
      {/* Board link: instant custom tooltip (no browser hover delay), shown below the icon */}
      <span
        style={{ position: "relative", display: "inline-flex" }}
        onMouseEnter={() => setShowBoardTip(true)}
        onMouseLeave={() => setShowBoardTip(false)}
      >
        <button
          onClick={() => onOpenBoard && onOpenBoard(card)}
          onFocus={() => setShowBoardTip(true)}
          onBlur={() => setShowBoardTip(false)}
          aria-label={`On board ${card.project}`}
          style={{ ...rowEditBtn, cursor: onOpenBoard ? "pointer" : "default" }}
        >{boardEmoji}</button>
        {showBoardTip && (
          <span style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "6px 10px", whiteSpace: "nowrap", pointerEvents: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
          }}>
            <span style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.text }}>
              {card.project}
            </span>
            {onOpenBoard && (
              <span style={{ display: "block", fontSize: 11, color: C.muted, marginTop: 2 }}>
                Click to view on board →
              </span>
            )}
          </span>
        )}
      </span>
      <button onClick={() => { setEditText(card.text); setEditPriority(card.nest_priority || "next"); setEditing(true); }}
        title="Edit" style={rowEditBtn}>✏️</button>
      {confirmRemove ? (
        <button
          onClick={() => onRemove(card)}
          title="Click to confirm — the card stays on its board"
          style={{
            background: C.red, border: "none", color: "#fff", borderRadius: 6,
            padding: "3px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer",
            fontFamily: "inherit", flexShrink: 0,
          }}
        >Remove?</button>
      ) : (
        <button onClick={armRemove} title="Remove from tasks (card stays on the board)"
          style={rowDeleteBtn}>×</button>
      )}
    </div>
  );

  return (
    <div className={"nest-row" + (flash ? " nest-row-saved-glow" : "")} style={{
      background: p.bg,
      border: `1px solid ${p.color}40`,
      borderRadius: 8, padding: nestRowPadding,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <span {...(dragHandleProps || {})} className="nest-drag-handle" title="Drag to reorder" style={nestHandleStyle}>⠿</span>
      <button
        onClick={() => onComplete(card)}
        title={doneColumn ? `Complete — moves to "${doneColumn.label}"` : "Complete — card stays in its column"}
        style={{
          width: 20, height: 20, borderRadius: 5, flexShrink: 0,
          border: `2px solid ${p.color}`, background: "transparent",
          cursor: "pointer", padding: 0,
        }}
      />
      <span style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 14, lineHeight: 1.4, wordBreak: "break-word" }}>
        {card.text}
      </span>
      {actions}
    </div>
  );
}

function TaskRow({ task, onToggle, onDelete, onSave, dragHandleProps, boards = [], onSendToBoard, flash }) {
  const [editing, setEditing]           = useState(false);
  const [editText, setEditText]         = useState(task.text);
  const [editPriority, setEditPriority] = useState(task.priority);
  const [editBoardId, setEditBoardId]   = useState("");
  // Deleting takes two clicks: × turns into a "Delete?" confirm that reverts
  // on its own after a moment if left alone
  const [confirmDelete, setConfirmDelete] = useState(false);
  const confirmTimer = useRef(null);
  const armDelete = () => {
    setConfirmDelete(true);
    clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setConfirmDelete(false), 2500);
  };
  useEffect(() => () => clearTimeout(confirmTimer.current), []);
  const p = PRIORITIES[task.priority];

  const commit = () => {
    const text = editText.trim();
    if (!text) { setEditing(false); return; }
    if (editBoardId && onSendToBoard) {
      // The board choice is applied only now, on save — the row becomes a
      // linked board card so no onSave needed
      onSendToBoard(task, editBoardId, { text, priority: editPriority });
    } else {
      onSave(task.id, { text, priority: editPriority });
    }
    setEditing(false);
  };

  const cancel = () => { setEditing(false); setEditBoardId(""); setConfirmDelete(false); };

  if (editing) {
    return (
      <div
        // Clicking anywhere outside the edit card saves it (Escape/Cancel discards)
        onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) commit(); }}
        style={{
          background: C.card, border: `1px solid ${C.accent}`, borderRadius: 8,
          padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <input value={editText} onChange={e => setEditText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
          autoFocus style={{ ...inputStyle, fontSize: 14 }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <NiceSelect value={editPriority} onChange={e => setEditPriority(e.target.value)}>
            {Object.entries(PRIORITIES).map(([k, pr]) =>
              <option key={k} value={k}>{pr.label}</option>)}
          </NiceSelect>
          {/* Picking a board here is just a pending choice — nothing happens
              until Save. On save the task moves to that board's first column
              and stays in the Nest as a linked board card. */}
          {onSendToBoard && (
            boards.length > 0 ? (
              <NiceSelect value={editBoardId} accent={editBoardId ? C.green : C.green + "60"}
                onChange={e => setEditBoardId(e.target.value)}>
                <option value="">→ Add to board…</option>
                {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </NiceSelect>
            ) : (
              <span style={{ color: C.muted, fontSize: 11, fontStyle: "italic" }}>
                (no boards found to send to)
              </span>
            )
          )}
        </div>
        {editBoardId && (
          <div style={{ color: C.green, fontSize: 11 }}>
            Will be added to "{boards.find(b => String(b.id) === String(editBoardId))?.name}" when you save.
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={commit} style={btnStyle(C.green)}>Save</button>
          {confirmDelete ? (
            <button onClick={() => onDelete(task.id)} style={{
              ...btnStyle(C.red), background: C.red, color: "#fff", borderColor: C.red,
            }}>Delete?</button>
          ) : (
            <button onClick={armDelete} style={btnStyle(C.red)}>Delete</button>
          )}
          <button onClick={cancel} style={btnStyle(C.muted)}>Cancel</button>
        </div>
      </div>
    );
  }

  const actions = (
    <div style={{ display: "flex", gap: 2, flexShrink: 0, alignItems: "center" }}>
      {!task.done && (
        <button onClick={() => { setEditBoardId(""); setConfirmDelete(false); setEditing(true); }} title="Edit" style={rowEditBtn}>✏️</button>
      )}
      {confirmDelete ? (
        <button
          onClick={() => onDelete(task.id)}
          title="Click to confirm"
          style={{
            background: C.red, border: "none", color: "#fff", borderRadius: 6,
            padding: "3px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer",
            fontFamily: "inherit", flexShrink: 0,
          }}
        >Delete?</button>
      ) : (
        <button onClick={armDelete} title="Delete task" style={rowDeleteBtn}>×</button>
      )}
    </div>
  );

  return (
    <div className={"nest-row" + (flash ? " nest-row-saved-glow" : "")} style={{
      background: task.done ? C.surface : p.bg,
      border: `1px solid ${task.done ? C.border : p.color + "40"}`,
      // Done rows have no drag handle, so they keep fuller left padding
      borderRadius: 8, padding: task.done ? "10px 8px 10px 14px" : nestRowPadding,
      display: "flex", alignItems: "center", gap: 10,
      opacity: task.done ? 0.5 : 1, transition: "opacity 0.2s",
    }}>
      {!task.done && (
        <span {...(dragHandleProps || {})} className="nest-drag-handle" title="Drag to reorder" style={nestHandleStyle}>⠿</span>
      )}
      <button onClick={() => onToggle(task.id)} style={{
        width: 20, height: 20, borderRadius: 5, flexShrink: 0,
        border: `2px solid ${task.done ? C.green : p.color}`,
        background: task.done ? C.green : "transparent",
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        color: "#000", fontSize: 12,
      }}>{task.done ? "✓" : ""}</button>

      <span style={{
        flex: 1, minWidth: 0, color: C.text, fontSize: 14, lineHeight: 1.4, wordBreak: "break-word",
        textDecoration: task.done ? "line-through" : "none",
      }}>{task.text}</span>

      {actions}
    </div>
  );
}

// ── Task History Modal ────────────────────────────────────────────────────────
function TaskHistoryModal({ tasks, onClose }) {
  const [search, setSearch] = useState("");
  const allDone = tasks
    .filter(t => t.done)
    .sort((a, b) => (b.last_completed_date || "").localeCompare(a.last_completed_date || ""));

  const filtered = allDone.filter(t =>
    t.text.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = filtered.reduce((acc, t) => {
    const date = t.last_completed_date || "Unknown";
    if (!acc[date]) acc[date] = [];
    acc[date].push(t);
    return acc;
  }, {});

  const formatDate = (d) => {
    if (d === "Unknown") return "Unknown date";
    if (d === todayStr()) return "Today";
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (d === yesterday.toISOString().slice(0,10)) return "Yesterday";
    return new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "#00000080",
      zIndex: 100, display: "flex", alignItems: "flex-start",
      justifyContent: "center", padding: "60px 16px 32px", overflowY: "auto",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 16, padding: 24, width: "100%", maxWidth: 480,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>Task History</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.muted, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search completed tasks..."
          style={{ ...inputStyle, marginBottom: 16 }}
          autoFocus
        />

        {filtered.length === 0 && (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 32 }}>
            {search ? "No tasks match that search." : "No completed tasks yet."}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {Object.entries(grouped).map(([date, dateTasks]) => (
            <div key={date}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>
                {formatDate(date)} <span style={{ color: C.border }}>· {dateTasks.length}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {dateTasks.map(t => (
                  <div key={t.id} style={{
                    background: C.card, border: `1px solid ${C.border}`,
                    borderRadius: 8, padding: "9px 12px",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <span style={{ fontSize: 12, color: C.green, flexShrink: 0 }}>✓</span>
                    <span style={{ fontSize: 14, color: C.muted, flex: 1, textDecoration: "line-through" }}>{t.text}</span>
                    {t.is_habit && (
                      <span style={{ fontSize: 10, color: C.green, background: C.green + "18", border: `1px solid ${C.green}40`, borderRadius: 20, padding: "2px 7px", flexShrink: 0 }}>↻ daily</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Task Board ────────────────────────────────────────────────────────────────
function TaskBoard({
  tasks, setTasks,
  boardCards = [],
  onCompleteBoardCard, onRemoveBoardCard, onSetBoardCardPriority,
  onPreviewBoardCardPriority, onSaveBoardCard,
  columnLabelFor, findDoneColumn,
  boards = [], onSendTaskToBoard, onOpenBoard,
}) {
  const [input, setInput]             = useState("");
  const [filter, setFilter]           = useState("all");
  const [showFilter, setShowFilter]   = useState(false);
  const [showDone, setShowDone]       = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const add = () => {
    if (!input.trim()) return;
    // New tasks go to the top of Do Next: a sort_order below every existing
    // task sorts first within its group
    const minOrder = tasks.length ? Math.min(...tasks.map(t => t.sort_order ?? 0)) : 0;
    setTasks(prev => [...prev, {
      id: Date.now().toString(), text: input.trim(),
      priority: "next", done: false, project: "General",
      is_habit: false, last_completed_date: null,
      sort_order: minOrder - 1, created_at: new Date().toISOString(),
    }]);
    setInput("");
  };

  const toggle = id => setTasks(prev => prev.map(t => {
    if (t.id !== id) return t;
    const nowDone = !t.done;
    return { ...t, done: nowDone, last_completed_date: nowDone ? todayStr() : null };
  }));

  const remove = async (id) => {
    setTasks(prev => prev.filter(t => t.id !== id));
    await supabase.from("tasks").delete().eq("id", id);
  };
  const [flashId, setFlashId] = useState(null); // briefly highlight the row just saved/added
  const flashRow = (id) => { setFlashId(id); setTimeout(() => setFlashId(c => (c === id ? null : c)), 1600); };
  const save = (id, fields) => { setTasks(prev => prev.map(t => t.id === id ? { ...t, ...fields } : t)); flashRow(id); };

  // Dailies live only in the Dailies section, so they never appear here
  const doneToday    = tasks.filter(t => t.done && !t.is_habit && t.last_completed_date === todayStr());
  const allDoneCount = tasks.filter(t => t.done && !t.is_habit).length;
  const activeTasks  = tasks.filter(t => !t.done && !t.is_habit)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const visible      = activeTasks.filter(t =>
    filter === "all" ? true :
    t.priority === filter
  );
  const grouped = Object.keys(PRIORITIES).reduce((acc, k) => {
    acc[k] = visible.filter(t => t.priority === k);
    return acc;
  }, {});

  // Board cards join the same priority groups, so everything you're working on
  // shows in one list. They keep their own handlers since they live on a board.
  const groupedBoardCards = Object.keys(PRIORITIES).reduce((acc, k) => {
    acc[k] = boardCards.filter(c =>
      (c.nest_priority || "next") === k &&
      (filter === "all" || filter === k)
    );
    return acc;
  }, {});

  const groupCount = (k) => (grouped[k]?.length || 0) + (groupedBoardCards[k]?.length || 0);
  const anyVisible = Object.keys(PRIORITIES).some(k => groupCount(k) > 0);

  // ── Drag and drop ──
  const [activeNestId, setActiveNestId] = useState(null);
  // Where the row STARTED — the live preview moves rows between groups
  // mid-drag, so the origin has to be remembered separately to know whether
  // a category change needs persisting on drop.
  const nestDragOrigin = useRef(null);
  const nestSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Prefer whatever the pointer is actually inside; fall back to nearest
  // corners. The two refs implement the stabilizer from dnd-kit's own
  // multi-container demo: right after a row jumps groups the layout shifts
  // under the pointer, and without briefly freezing the drop target this
  // causes an endless flip-flop between adjacent groups.
  const lastOverId = useRef(null);
  const recentlyMovedGroups = useRef(false);
  const nestCollision = (args) => {
    if (recentlyMovedGroups.current && lastOverId.current) {
      // Layout is still settling from a group change — keep the last target
      return [{ id: lastOverId.current }];
    }
    const within = pointerWithin(args);
    const collisions = within.length > 0 ? within : closestCorners(args);
    if (collisions.length > 0) lastOverId.current = collisions[0].id;
    return collisions;
  };

  // Once the lists have re-rendered after a group change, release the freeze
  useEffect(() => {
    requestAnimationFrame(() => { recentlyMovedGroups.current = false; });
  }, [tasks, boardCards]);

  // Tasks and board cards share one ordered list per group. Tasks order by
  // sort_order; board cards by nest_sort_order (cards without one float to
  // the top, matching how flagged cards have always appeared).
  const mergedRowsFor = (key) => {
    const rows = [
      ...(groupedBoardCards[key] || []).map(c => ({ type: "card", obj: c, ord: c.nest_sort_order ?? -1, tie: 0 })),
      ...(grouped[key] || []).map(t => ({ type: "task", obj: t, ord: t.sort_order ?? 0, tie: 1 })),
    ];
    return rows.sort((a, b) => (a.ord - b.ord) || (a.tie - b.tie));
  };
  const rowIdOf = (row) => (row.type === "card" ? BCARD_PREFIX : TASK_PREFIX) + row.obj.id;
  const rowIdsFor = (key) => mergedRowsFor(key).map(rowIdOf);

  // Which priority group does a row id currently sit in?
  const groupOf = (rowId) => {
    if (typeof rowId === "string" && rowId.startsWith("npri-")) return rowId.replace("npri-", "");
    for (const k of Object.keys(PRIORITIES)) {
      if (rowIdsFor(k).includes(rowId)) return k;
    }
    return null;
  };

  // Live preview: moving a row into a different group mid-drag makes the
  // destination list actually contain it — inserted at the hovered spot so
  // the surrounding rows barely shift.
  const handleNestDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const fromGroup = groupOf(active.id);
    const toGroup = groupOf(over.id);
    if (!fromGroup || !toGroup || fromGroup === toGroup) return;

    const isBoardCard = String(active.id).startsWith(BCARD_PREFIX);
    const rawId = String(active.id).replace(BCARD_PREFIX, "").replace(TASK_PREFIX, "");

    // Preview position: right at the hovered row, or at the end for the zone
    const destRows = mergedRowsFor(toGroup);
    const overRow = destRows.find(r => rowIdOf(r) === String(over.id));
    const ord = overRow
      ? overRow.ord
      : (destRows.length > 0 ? Math.max(...destRows.map(r => r.ord)) + 1 : 0);

    // Freeze the drop target until the re-render settles (see nestCollision)
    recentlyMovedGroups.current = true;

    if (isBoardCard) {
      if (onPreviewBoardCardPriority) {
        const card = boardCards.find(c => String(c.id) === rawId);
        if (card) onPreviewBoardCardPriority(card, toGroup, ord);
      }
    } else {
      setTasks(prev => prev.map(t =>
        String(t.id) === rawId ? { ...t, priority: toGroup, sort_order: ord } : t
      ));
    }
  };

  // On drop: settle the final position, stamp fresh order numbers for the
  // destination group, and persist everything (task priority rides the
  // debounced sync; board cards get explicit writes).
  const handleNestDragEnd = (event) => {
    const { active, over } = event;
    setActiveNestId(null);
    const originGroup = nestDragOrigin.current;
    nestDragOrigin.current = null;
    lastOverId.current = null;
    recentlyMovedGroups.current = false;
    if (!over) return;

    // The preview already moved the row, so its CURRENT group is the destination
    const toGroup = groupOf(active.id) || groupOf(over.id);
    if (!originGroup || !toGroup) return;

    const isBoardCard = String(active.id).startsWith(BCARD_PREFIX);
    const rawId = String(active.id).replace(BCARD_PREFIX, "").replace(TASK_PREFIX, "");

    // Final order within the destination group
    const rows = mergedRowsFor(toGroup);
    const ids = rows.map(rowIdOf);
    const oldIdx = ids.indexOf(active.id);
    if (oldIdx === -1) return;
    let newIdx = ids.indexOf(String(over.id));
    if (newIdx === -1) newIdx = oldIdx; // dropped on the group zone — keep slot
    const reordered = oldIdx === newIdx ? rows : arrayMove(rows, oldIdx, newIdx);

    // Stamp sequential order across the destination group only
    const taskOrder = {};
    const cardStamps = [];
    reordered.forEach((row, i) => {
      if (row.type === "task") {
        if ((row.obj.sort_order ?? 0) !== i) taskOrder[row.obj.id] = i;
      } else if ((row.obj.nest_sort_order ?? -1) !== i) {
        cardStamps.push({ card: row.obj, i });
      }
    });

    if (Object.keys(taskOrder).length > 0) {
      setTasks(prev => prev.map(t =>
        taskOrder[t.id] !== undefined ? { ...t, sort_order: taskOrder[t.id] } : t
      ));
    }
    cardStamps.forEach(({ card, i }) => {
      if (onSaveBoardCard) onSaveBoardCard(card, { nest_sort_order: i });
    });

    // Persist the moved board card's category change (the preview only
    // changed it locally)
    if (isBoardCard && originGroup !== toGroup && onSetBoardCardPriority) {
      const card = boardCards.find(c => String(c.id) === rawId);
      if (card) onSetBoardCardPriority({ ...card, nest_priority: originGroup }, toGroup);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Grip hover behavior — same as kanban cards: faint at rest, clear on row hover */}
      <style>{`
        .nest-drag-handle { opacity: 0.3; transition: opacity 0.12s; }
        .nest-row:hover .nest-drag-handle { opacity: 0.75; }
        .nest-drag-handle:active { cursor: grabbing; }
        @keyframes nestSavedGlow {
          0%   { box-shadow: 0 0 0 2px ${C.orange}; }
          100% { box-shadow: 0 0 0 2px ${C.orange}00; }
        }
        .nest-row-saved-glow { animation: nestSavedGlow 1.6s ease-out; }
      `}</style>
      {showHistory && <TaskHistoryModal tasks={tasks} onClose={() => setShowHistory(false)} />}

      {/* Add task */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder="Add a task..."
            style={{ ...inputStyle, flex: 1 }} />
          <button onClick={add} style={btnStyle(C.accent)}>+ Add</button>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
          <button onClick={() => { setShowFilter(f => !f); if (showFilter) setFilter("all"); }}
            style={{ background: "transparent", border: "none", color: showFilter ? C.accentSoft : C.muted, fontSize: 12, cursor: "pointer", padding: "4px 6px" }}>
            {showFilter ? "Filter ▴" : "Filter ▾"}
          </button>
        </div>
        {showFilter && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              ["all",    "All",      C.muted],
              ...Object.entries(PRIORITIES).map(([k, p]) => [k, p.label, p.color]),
            ].map(([key, label, color]) => (
              <button key={key} onClick={() => setFilter(key)} style={{
                background: filter === key ? color + "22" : "transparent",
                border: `1px solid ${filter === key ? color : C.border}`,
                color: filter === key ? color : C.muted,
                borderRadius: 20, padding: "4px 10px", fontSize: 12, cursor: "pointer",
              }}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {/* Active task groups */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <DndContext
        sensors={nestSensors}
        collisionDetection={nestCollision}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={e => { setActiveNestId(e.active.id); nestDragOrigin.current = groupOf(e.active.id); }}
        onDragOver={handleNestDragOver}
        onDragEnd={handleNestDragEnd}
        onDragCancel={() => { setActiveNestId(null); nestDragOrigin.current = null; lastOverId.current = null; recentlyMovedGroups.current = false; }}
      >
        {Object.entries(PRIORITIES).map(([key, p]) =>
          // While dragging, show every group so an empty one is still a target
          (groupCount(key) > 0 || activeNestId) && (
            <div key={key}>
              <div style={{ color: p.color, fontSize: 13, fontWeight: 700, marginBottom: 8, letterSpacing: 1 }}>
                {p.label} <span style={{ color: C.muted, fontWeight: 400 }}>({groupCount(key)})</span>
              </div>
              <PriorityDropZone priorityKey={key}>
                <SortableContext items={rowIdsFor(key)} strategy={verticalListSortingStrategy}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, minHeight: activeNestId ? 36 : 0 }}>
                    {activeNestId && groupCount(key) === 0 && (
                      <div style={{
                        border: `1px dashed ${p.color}55`, borderRadius: 8,
                        padding: "8px 12px", color: C.muted, fontSize: 11,
                        textAlign: "center",
                      }}>Drop here</div>
                    )}
                    {mergedRowsFor(key).map(row => (
                      <SortableNestRow key={rowIdOf(row)} id={rowIdOf(row)}>
                        {({ dragHandleProps }) => row.type === "card" ? (
                          <BoardCardRow
                            card={row.obj}
                            columnLabel={columnLabelFor ? columnLabelFor(row.obj.project, row.obj.status) : null}
                            doneColumn={findDoneColumn ? findDoneColumn(row.obj.project) : null}
                            onComplete={onCompleteBoardCard}
                            onRemove={onRemoveBoardCard}
                            onSave={(c, fields) => { onSaveBoardCard(c, fields); flashRow(c.id); }}
                            dragHandleProps={dragHandleProps}
                            onOpenBoard={onOpenBoard}
                            flash={flashId === row.obj.id}
                            boards={boards}
                          />
                        ) : (
                          <TaskRow
                            task={row.obj}
                            onToggle={toggle}
                            onDelete={remove}
                            onSave={save}
                            dragHandleProps={dragHandleProps}
                            boards={boards}
                            onSendToBoard={async (t, bId, f) => { const newId = await onSendTaskToBoard(t, bId, f); if (newId) flashRow(newId); }}
                            flash={flashId === row.obj.id}
                          />
                        )}
                      </SortableNestRow>
                    ))}
                  </div>
                </SortableContext>
              </PriorityDropZone>
            </div>
          )
        )}

        {/* Floating copy of the dragged row — same feel as the kanban board */}
        <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
          {activeNestId ? (() => {
            const isCard = String(activeNestId).startsWith(BCARD_PREFIX);
            const raw = String(activeNestId).replace(BCARD_PREFIX, "").replace(TASK_PREFIX, "");
            const obj = isCard
              ? boardCards.find(c => String(c.id) === raw)
              : tasks.find(t => String(t.id) === raw);
            if (!obj) return null;
            const pr = PRIORITIES[(isCard ? obj.nest_priority : obj.priority) || "next"] || PRIORITIES.next;
            return (
              <div style={{
                background: pr.bg, border: `1px solid ${pr.color}66`, borderRadius: 8,
                padding: "10px 14px 10px 10px", display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 14px 32px rgba(0,0,0,0.55)", transform: "rotate(1.5deg)", cursor: "grabbing",
              }}>
                <span style={{ ...nestHandleStyle, opacity: 0.6 }}>⠿</span>
                <span style={{ color: C.text, fontSize: 14, lineHeight: 1.4 }}>{obj.text}</span>
                {isCard && (
                  <span style={{
                    background: C.accent + "22", color: C.accentSoft, border: `1px solid ${C.accent}55`,
                    borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 600,
                    whiteSpace: "nowrap", flexShrink: 0, letterSpacing: 0.5,
                  }}>{boards.find(b => b.name === obj.project)?.emoji || DEFAULT_BOARD_EMOJI} {obj.project}</span>
                )}
              </div>
            );
          })() : null}
        </DragOverlay>
      </DndContext>
      {!anyVisible && tasks.filter(t => !t.done).length === 0 && boardCards.length === 0 && (
          <div style={{ color: C.muted, textAlign: "center", padding: 32, fontSize: 14 }}>
            Nothing here yet — you're either caught up or haven't added tasks. Both are fine. 🌿
          </div>
        )}
      </div>

      {/* Done today — collapsible */}
      {doneToday.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <button onClick={() => setShowDone(s => !s)} style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: C.green, fontSize: 13, fontWeight: 700, padding: 0, fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              ✅ Done today ({doneToday.length}) {showDone ? "▾" : "▴"}
            </button>
            {allDoneCount > doneToday.length && (
              <button onClick={() => setShowHistory(true)} style={{
                background: "transparent", border: "none",
                color: C.accentSoft, fontSize: 12, cursor: "pointer",
                fontFamily: "inherit", padding: 0,
              }}>view all history →</button>
            )}
          </div>
          {showDone && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {doneToday.map(t =>
                <TaskRow key={t.id} task={t} onToggle={toggle} onDelete={remove} onSave={save} />)}
            </div>
          )}
        </div>
      )}

      {doneToday.length === 0 && allDoneCount > 0 && (
        <div style={{ textAlign: "right" }}>
          <button onClick={() => setShowHistory(true)} style={{
            background: "transparent", border: "none",
            color: C.accentSoft, fontSize: 12, cursor: "pointer",
            fontFamily: "inherit", padding: 0,
          }}>view task history →</button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Brain Dump — daily pages, saved to Supabase (v5: Write / Browse + search)
//
// Drop-in replacement for the BrainDump() function in App.jsx.
// Reuses helpers already in App.jsx:
//   C, supabase, btnStyle, PRIORITIES, boardColumnsFor, todayStr, NiceSelect
//
// No SQL or parent changes needed beyond what you already did:
//   - birdie_brain_dump table exists
//   - the tab passes user:  <BrainDump onConvert={addTask} boards={appBoards} user={user} />
//
// New: a Write / Browse toggle. Browse lists every past day newest-first with a
// preview + word count, and a search box that filters across all pages and
// highlights the match. Tap a card to open that day.
// ─────────────────────────────────────────────────────────────────────────────

function BrainDump({ onConvert, boards = [], user }) {
  const [pages, setPages]     = useState({});          // { "YYYY-MM-DD": content }
  const [active, setActive]   = useState(() => {       // the day being viewed (local date)
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [text, setText]       = useState("");
  const [status, setStatus]   = useState("saved");     // "saved" | "saving"
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState("write");     // "write" | "browse"
  const [query, setQuery]     = useState("");          // browse search
  const [pick, setPick]       = useState("");          // the highlighted text you chose
  const [selPriority, setSelPriority] = useState("");  // pending task priority
  const [selBoard, setSelBoard]       = useState("");  // pending board id
  const [flash, setFlash]     = useState("");          // brief "Added ✓" confirmation
  const [moodByDate, setMoodByDate] = useState({});    // "YYYY-MM-DD" -> mood index (from Check In)
  const [titles, setTitles] = useState({});            // "YYYY-MM-DD" -> custom page name, if renamed
  const [editingTitleDate, setEditingTitleDate] = useState(null); // date whose title is being edited
  const [titleInput, setTitleInput] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(""); // date awaiting delete confirmation
  const saveTimer = useRef(null);
  const flashTimer = useRef(null);
  const confirmTimer = useRef(null);
  const knownDates = useRef(new Set());                // days that exist in the DB
  const rowIds = useRef({});                            // "YYYY-MM-DD" -> row id (for reliable updates)
  const ta = useRef();

  // ── date helpers (local calendar, no timezone drift) ──────────────────────
  const pad = (n) => String(n).padStart(2, "0");
  const keyFromDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const localToday = () => keyFromDate(new Date()); // "today" in the user's local time, not UTC
  const parseDay = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
  const shiftDay = (s, delta) => { const d = parseDay(s); d.setDate(d.getDate() + delta); return keyFromDate(d); };
  const WD = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const MO = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const MOODS = ["😩", "😕", "😐", "🙂", "🤩"]; // matches the Check In on the Nest
  const weekdayOf = (s) => WD[parseDay(s).getDay()];
  const longDate  = (s) => { const d = parseDay(s); return `${MO[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`; };

  const isToday = active === localToday();
  const wordsIn = (t) => (t && t.trim() ? t.trim().split(/\s+/).length : 0);
  const wordCount = wordsIn(text);

  // ── load every page for this user, once ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const map = {};
      const titleMap = {};
      try {
        const uid = user?.id || (await supabase.auth.getUser()).data.user?.id;
        if (uid) {
          let [pagesRes, reflRes] = await Promise.all([
            supabase.from("birdie_brain_dump").select("id, entry_date, text, title").eq("user_id", uid).order("entry_date", { ascending: false }),
            supabase.from("reflections").select("date, text").eq("user_id", uid),
          ]);
          if (pagesRes.error && /title/i.test(pagesRes.error.message)) {
            // The title column hasn't been added in Supabase yet — load
            // without it so pages still show (just not renamed yet).
            console.warn("title column missing in birdie_brain_dump — run in Supabase SQL editor: alter table birdie_brain_dump add column title text;");
            pagesRes = await supabase.from("birdie_brain_dump").select("id, entry_date, text").eq("user_id", uid).order("entry_date", { ascending: false });
          }
          if (pagesRes.error) console.error("brain dump load:", pagesRes.error.message);
          (pagesRes.data || []).forEach((r) => {
            map[r.entry_date] = r.text || "";
            if (r.title) titleMap[r.entry_date] = r.title;
            knownDates.current.add(r.entry_date);
            rowIds.current[r.entry_date] = r.id;
          });
          // Fold any past reflections into that day's page. Non-destructive: the
          // reflections table is left untouched — this just brings the text into
          // the brain dump so each day reads as one place. The first time a day
          // is opened/edited, the merged text saves into birdie_brain_dump.
          (reflRes.data || []).forEach((r) => {
            const t = (r.text || "").trim();
            if (!t) return;
            const existing = map[r.date] || "";
            if (!existing.includes(t)) map[r.date] = existing ? `${existing}\n\n${t}` : t;
          });
        }
      } catch (e) { console.error("brain dump load:", e); }
      if (cancelled) return;
      const t = localToday();
      if (!(t in map)) map[t] = "";            // today's page always exists
      setPages(map);
      setTitles(titleMap);
      setActive(t);
      setText(map[t] || "");
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── load moods so Browse can show the face you logged each day ─────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uid = user?.id || (await supabase.auth.getUser()).data.user?.id;
        if (!uid) return;
        const { data } = await supabase
          .from("moods")
          .select("id, mood, created_at")
          .eq("user_id", uid)
          .order("created_at", { ascending: false });
        if (cancelled || !data) return;
        const map = {};
        data.forEach((e) => {
          const d = e.created_at ? keyFromDate(new Date(e.created_at)) : ""; // local day; latest per day wins (already desc)
          if (d && !(d in map)) map[d] = { id: e.id, mood: e.mood };
        });
        setMoodByDate(map);
      } catch (e) { console.error("brain dump mood load:", e); }
    })();
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── save one day's page — update the existing row if we have it, else insert.
  // Doesn't depend on a unique (user_id, date) constraint, so it works even if
  // the table was created without one.
  const persist = async (date, content) => {
    // don't spawn empty rows for days that were opened but never written in
    if (!content.trim() && !knownDates.current.has(date) && !rowIds.current[date]) { setStatus("saved"); return; }
    try {
      const uid = user?.id || (await supabase.auth.getUser()).data.user?.id;
      if (!uid) return;
      const existingId = rowIds.current[date];
      if (existingId) {
        const { error } = await supabase
          .from("birdie_brain_dump")
          .update({ text: content })
          .eq("id", existingId);
        if (error) { console.error("brain dump save:", error.message); return; }
      } else {
        const { data, error } = await supabase
          .from("birdie_brain_dump")
          .insert({ user_id: uid, entry_date: date, text: content })
          .select("id")
          .single();
        if (error) { console.error("brain dump save:", error.message); return; }
        if (data?.id) rowIds.current[date] = data.id;
      }
      knownDates.current.add(date);
      setStatus("saved");
    } catch (e) { console.error("brain dump save:", e); }
  };

  // Rename a page — the date stays exactly as it was, this just gives that
  // day a memorable name (e.g. "Apartment hunting") alongside it.
  const saveTitle = async (date) => {
    const trimmed = titleInput.trim();
    setEditingTitleDate(null);
    setTitles((prev) => {
      if (!trimmed) { const next = { ...prev }; delete next[date]; return next; }
      return { ...prev, [date]: trimmed };
    });
    try {
      const uid = user?.id || (await supabase.auth.getUser()).data.user?.id;
      if (!uid) return;
      const existingId = rowIds.current[date];
      if (existingId) {
        const { error } = await supabase.from("birdie_brain_dump").update({ title: trimmed || null }).eq("id", existingId);
        if (error) console.error("brain dump title save:", error.message);
      } else {
        // No row for this date yet — create one so the title has somewhere
        // to live (matters for a day that's never been auto-saved before).
        const { data, error } = await supabase
          .from("birdie_brain_dump")
          .insert({ user_id: uid, entry_date: date, text: pages[date] || "", title: trimmed || null })
          .select("id")
          .single();
        if (error) { console.error("brain dump title save:", error.message); return; }
        if (data?.id) rowIds.current[date] = data.id;
        knownDates.current.add(date);
      }
    } catch (e) { console.error("brain dump title save:", e); }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    setPages((prev) => ({ ...prev, [active]: v }));
    setStatus("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(active, v), 900);
  };

  const flush = async () => { clearTimeout(saveTimer.current); await persist(active, text); };

  // When you highlight text in the page, remember it as the thing to file.
  const onSelect = (e) => {
    const el = e.target;
    const sel = (el.value.slice(el.selectionStart, el.selectionEnd) || "").replace(/\s+/g, " ").trim();
    if (sel) setPick(sel);
  };

  const clearPick = () => { setPick(""); setSelPriority(""); setSelBoard(""); };

  const goTo = async (date) => {
    await flush();
    setActive(date);
    setText(pages[date] || "");
    clearPick();
    setStatus("saved");
  };
  const goPrev  = () => goTo(shiftDay(active, -1));
  const goNext  = () => { if (!isToday) goTo(shiftDay(active, 1)); };
  const goToday = () => goTo(localToday());

  // open a day from Browse, then drop into Write mode
  const openDay = async (date) => { await goTo(date); setView("write"); setTimeout(() => ta.current?.focus(), 0); };
  const goBrowse = async () => { await flush(); clearPick(); setView("browse"); };

  // days that actually have writing, newest first
  const writtenDays = Object.keys(pages)
    .filter((d) => (pages[d] || "").trim())
    .sort()
    .reverse();

  const showFlash = (msg) => {
    setFlash(msg);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(""), 2600);
  };

  // Insert a card at the TOP of its column (one below the current min sort_order).
  const addCardTop = async (lineText, boardId) => {
    const board = boards.find((b) => b.id === boardId);
    if (!board) return false;
    const firstCol = boardColumnsFor(board)[0]?.id || "future";
    let minOrder = 0;
    try {
      const { data } = await supabase
        .from("birdie_kanban_cards")
        .select("sort_order")
        .eq("project", board.name)
        .eq("status", firstCol)
        .order("sort_order", { ascending: true })
        .limit(1);
      if (data && data.length) minOrder = data[0].sort_order ?? 0;
    } catch (e) { /* fall back to 0 */ }
    const { error } = await supabase.from("birdie_kanban_cards").insert([{
      id: "card-" + Date.now(),
      project: board.name,
      text: lineText,
      status: firstCol,
      user_id: user?.id || null,
      sort_order: minOrder - 1,
    }]);
    if (error) { console.error("addCardTop error:", error.message); return false; }
    return true;
  };

  const confirmAdd = async () => {
    if (!pick || (!selPriority && !selBoard)) return;
    const parts = [];
    if (selPriority) {
      onConvert(pick, selPriority);
      parts.push(`Tasks · ${PRIORITIES[selPriority]?.label || ""}`.trim());
    }
    if (selBoard) {
      const b = boards.find((x) => x.id === selBoard);
      const ok = await addCardTop(pick, selBoard);
      if (ok && b) parts.push(b.name);
    }
    if (parts.length) showFlash(`Added to ${parts.join(" and ")}`);
    clearPick();
  };

  // log / change today's mood (only today can be set correctly, since mood is
  // keyed to when it was logged)
  const handleMoodSelect = async (i) => {
    try {
      const uid = user?.id || (await supabase.auth.getUser()).data.user?.id;
      if (!uid) return;
      const t = localToday();
      const existing = moodByDate[t];
      if (existing?.id) await supabase.from("moods").delete().eq("id", existing.id);
      const entry = { id: Date.now().toString(), user_id: uid, mood: i, created_at: new Date().toISOString() };
      const { error } = await supabase.from("moods").insert(entry);
      if (error) { console.error("mood select:", error.message); return; }
      setMoodByDate((prev) => ({ ...prev, [t]: { id: entry.id, mood: i } }));
    } catch (e) { console.error("mood select:", e); }
  };

  // delete a day's page — first tap asks for confirmation, second tap deletes.
  // Removes the brain dump row and any old reflection folded into that day.
  const doDelete = async (date) => {
    clearTimeout(confirmTimer.current);
    setConfirmDelete("");
    try {
      const uid = user?.id || (await supabase.auth.getUser()).data.user?.id;
      const id = rowIds.current[date];
      if (id) {
        const { error } = await supabase.from("birdie_brain_dump").delete().eq("id", id);
        if (error) { console.error("brain dump delete:", error.message); return; }
      }
      if (uid) await supabase.from("reflections").delete().eq("user_id", uid).eq("date", date);
    } catch (e) { console.error("brain dump delete:", e); return; }
    delete rowIds.current[date];
    knownDates.current.delete(date);
    const isTodayDate = date === localToday();
    setPages((prev) => { const next = { ...prev }; if (isTodayDate) next[date] = ""; else delete next[date]; return next; });
    setTitles((prev) => { const next = { ...prev }; delete next[date]; return next; });
    if (active === date) {
      const t = localToday();
      setActive(t);
      setText(isTodayDate ? "" : (pages[t] || ""));
    }
  };
  const requestDelete = (date) => {
    if (confirmDelete === date) { doDelete(date); return; }
    setConfirmDelete(date);
    clearTimeout(confirmTimer.current);
    confirmTimer.current = setTimeout(() => setConfirmDelete(""), 3000);
  };

  // ── browse helpers: preview snippet + match highlighting ──────────────────
  const preview = (content, q) => {
    const flat = (content || "").replace(/\s+/g, " ").trim();
    if (q) {
      const i = flat.toLowerCase().indexOf(q.toLowerCase());
      if (i >= 0) {
        const start = Math.max(0, i - 30);
        return (start > 0 ? "…" : "") + flat.slice(start, start + 170) + (flat.length > start + 170 ? "…" : "");
      }
    }
    return flat.slice(0, 170) + (flat.length > 170 ? "…" : "");
  };
  const highlight = (str, q) => {
    if (!q) return str;
    const out = [];
    const lower = str.toLowerCase(); const ql = q.toLowerCase();
    let from = 0, idx;
    while ((idx = lower.indexOf(ql, from)) !== -1) {
      if (idx > from) out.push(str.slice(from, idx));
      out.push(<mark key={idx} style={{ background: C.accent + "55", color: C.text, borderRadius: 3, padding: "0 2px" }}>{str.slice(idx, idx + q.length)}</mark>);
      from = idx + q.length;
    }
    out.push(str.slice(from));
    return out;
  };

  const arrowBtn = {
    background: C.surface, border: `1px solid ${C.border}`, color: C.text,
    width: 30, height: 30, borderRadius: 8, cursor: "pointer",
    fontSize: 16, lineHeight: 1, fontFamily: "inherit", flexShrink: 0,
  };
  const segStyle = (on) => ({
    background: on ? C.accent + "22" : "transparent",
    border: `1px solid ${on ? C.accent + "66" : C.border}`,
    color: on ? C.text : C.muted, borderRadius: 8, padding: "6px 16px",
    fontSize: 13, fontWeight: on ? 700 : 500, cursor: "pointer", fontFamily: "inherit",
  });

  if (loading) {
    return <div style={{ color: C.muted, fontSize: 14, padding: "40px 0", textAlign: "center" }}>Opening your pages…</div>;
  }

  const canAdd = !!(selPriority || selBoard);
  const q = query.trim();
  const browseDays = q
    ? writtenDays.filter((d) => (pages[d] || "").toLowerCase().includes(q.toLowerCase()))
    : writtenDays;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* ── Write / Browse toggle ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => setView("write")} style={segStyle(view === "write")}>✍️ Write</button>
        <button onClick={goBrowse} style={segStyle(view === "browse")}>📚 Browse</button>
        {view === "browse" && (
          <span style={{ color: C.muted, fontSize: 12, marginLeft: "auto" }}>
            {writtenDays.length} {writtenDays.length === 1 ? "page" : "pages"}
          </span>
        )}
      </div>

      {view === "write" ? (
        <>
          {/* ── Day header + status ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={goPrev} style={arrowBtn} title="Previous day">‹</button>
              <div>
                <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase" }}>
                  {titles[active] ? longDate(active) : (isToday ? "Today" : weekdayOf(active))}
                </div>
                {editingTitleDate === active ? (
                  <input
                    autoFocus
                    value={titleInput}
                    onChange={(e) => setTitleInput(e.target.value)}
                    onBlur={() => saveTitle(active)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveTitle(active); if (e.key === "Escape") setEditingTitleDate(null); }}
                    placeholder="Name this page..."
                    style={{ fontSize: 18, fontWeight: 800, color: C.text, background: C.bg, border: "1px solid " + C.accent, borderRadius: 6, padding: "1px 6px", outline: "none", marginTop: 1, width: 220 }}
                  />
                ) : (
                  <div
                    onClick={() => { setTitleInput(titles[active] || ""); setEditingTitleDate(active); }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}
                  >
                    <span style={{ fontSize: 18, fontWeight: 800, marginTop: 1 }}>{titles[active] || longDate(active)}</span>
                    <span style={{ fontSize: 11, opacity: 0.5 }}>✏️</span>
                  </div>
                )}
              </div>
              <button onClick={goNext} disabled={isToday}
                style={{ ...arrowBtn, opacity: isToday ? 0.35 : 1, cursor: isToday ? "default" : "pointer" }}
                title="Next day">›</button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ color: C.muted, fontSize: 12 }}>{wordCount} {wordCount === 1 ? "word" : "words"}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 6, color: C.muted, fontSize: 12 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: status === "saving" ? C.yellow : C.green, display: "inline-block" }} />
                {status === "saving" ? "Saving" : "Saved"}
              </span>
            </div>
          </div>

          {/* quick nav: back to today */}
          {!isToday && (
            <button onClick={goToday} style={{ ...btnStyle(C.accent), padding: "6px 14px", fontSize: 13, alignSelf: "flex-start" }}>
              ← Back to today
            </button>
          )}

          {/* Mood check-in for the day */}
          {(isToday || moodByDate[active]) && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span style={{ color: C.muted, fontSize: 13, whiteSpace: "nowrap" }}>How are you feeling?</span>
              <div style={{ display: "flex", gap: 4 }}>
                {MOODS.map((m, i) => {
                  const chosen = moodByDate[active]?.mood === i;
                  return (
                    <button
                      key={i}
                      onClick={isToday ? () => handleMoodSelect(i) : undefined}
                      disabled={!isToday}
                      title={isToday ? "" : "Mood is logged for today only"}
                      style={{
                        fontSize: 22,
                        background: chosen ? C.accent + "30" : "transparent",
                        border: `2px solid ${chosen ? C.accent : "transparent"}`,
                        borderRadius: 8, padding: "2px 5px", lineHeight: 1,
                        cursor: isToday ? "pointer" : "default",
                        opacity: isToday || chosen ? 1 : 0.35,
                        transition: "all 0.15s",
                      }}
                    >{m}</button>
                  );
                })}
              </div>
            </div>
          )}

          {/* the page */}
          <textarea
            ref={ta}
            value={text}
            onChange={onChange}
            onBlur={flush}
            onSelect={onSelect}
            autoFocus
            placeholder={isToday
              ? "What's on your mind this morning?\n\nDon't filter, don't organize — just get it all down. It saves as you type."
              : "This page is empty. Start writing whenever you like."}
            style={{
              minHeight: "48vh", background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12, color: C.text, fontSize: 16, padding: "20px 22px",
              resize: "vertical", outline: "none", fontFamily: "inherit", lineHeight: 1.85,
              width: "100%", boxSizing: "border-box", caretColor: C.accent,
            }}
          />

          {/* highlight → choose Tasks and/or Board, then Add */}
          {pick ? (
            <div style={{
              background: C.card, border: `1px solid ${C.accent}55`, borderRadius: 10,
              padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10,
            }}>
              <div>
                <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>
                  Turn into a task and / or card
                </div>
                <div style={{ fontSize: 14, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  “{pick.length > 80 ? pick.slice(0, 80) + "…" : pick}”
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <NiceSelect value={selPriority} onChange={(e) => setSelPriority(e.target.value)}
                  width={150} accent={selPriority ? C.accent : C.border}>
                  <option value="">Task: none</option>
                  {Object.entries(PRIORITIES).map(([key, p]) => (
                    <option key={key} value={key}>{p.label}</option>
                  ))}
                </NiceSelect>
                {boards.length > 0 && (
                  <NiceSelect value={selBoard} onChange={(e) => setSelBoard(e.target.value)}
                    width={170} accent={selBoard ? C.green : C.border}>
                    <option value="">Board: none</option>
                    {boards.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </NiceSelect>
                )}
                <button
                  onClick={confirmAdd}
                  disabled={!canAdd}
                  style={{
                    ...btnStyle(C.accent), padding: "7px 18px", fontSize: 13,
                    opacity: canAdd ? 1 : 0.4, cursor: canAdd ? "pointer" : "default",
                  }}
                >
                  Add
                </button>
                <button
                  onClick={clearPick}
                  title="Clear highlight"
                  style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 2px", marginLeft: "auto" }}
                >×</button>
              </div>
            </div>
          ) : flash ? (
            <div style={{ fontSize: 13, color: C.green, display: "flex", alignItems: "center", gap: 6 }}>
              ✓ {flash}
            </div>
          ) : (
            text.trim() && (
              <div style={{ fontSize: 12.5, color: C.muted }}>
                Tip: highlight any line to turn it into a task or board card.
              </div>
            )
          )}

          {/* Delete this day's page */}
          {text.trim() && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => requestDelete(active)}
                style={{
                  background: confirmDelete === active ? "#e06b5e" : "transparent",
                  border: `1px solid ${confirmDelete === active ? "#e06b5e" : C.border}`,
                  color: confirmDelete === active ? "#fff" : C.muted,
                  borderRadius: 8, padding: "5px 12px", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                {confirmDelete === active ? "Tap again to delete" : "🗑 Delete this page"}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          {/* ── Browse: search + list of past pages ── */}
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search your pages…"
            style={{
              background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
              color: C.text, fontSize: 14, padding: "10px 14px", outline: "none",
              fontFamily: "inherit", width: "100%", boxSizing: "border-box", caretColor: C.accent,
            }}
          />

          {writtenDays.length === 0 ? (
            <div style={{ color: C.muted, fontSize: 14, padding: "30px 0", textAlign: "center" }}>
              No pages yet. Switch to Write and get some ideas down — they'll show up here.
            </div>
          ) : browseDays.length === 0 ? (
            <div style={{ color: C.muted, fontSize: 14, padding: "30px 0", textAlign: "center" }}>
              No pages mention “{q}”.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {browseDays.map((d) => {
                const wc = wordsIn(pages[d]);
                const confirming = confirmDelete === d;
                return (
                  <div
                    key={d}
                    style={{
                      background: C.card, border: `1px solid ${confirming ? "#e06b5e" : C.border}`,
                      borderRadius: 10, padding: "13px 15px",
                      display: "flex", flexDirection: "column", gap: 6, width: "100%",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div
                        onClick={() => openDay(d)}
                        style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 9, flex: 1, minWidth: 0, textAlign: "left" }}
                      >
                        <div style={{ minWidth: 0 }}>
                          {editingTitleDate === d ? (
                            <input
                              autoFocus
                              value={titleInput}
                              onChange={(e) => setTitleInput(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={() => saveTitle(d)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveTitle(d); if (e.key === "Escape") setEditingTitleDate(null); }}
                              placeholder="Name this page..."
                              style={{ fontSize: 15, fontWeight: 700, color: C.text, background: C.bg, border: "1px solid " + C.accent, borderRadius: 6, padding: "2px 6px", outline: "none", width: "80%" }}
                            />
                          ) : (
                            <span
                              onClick={(e) => { e.stopPropagation(); setTitleInput(titles[d] || ""); setEditingTitleDate(d); }}
                              style={{ cursor: "pointer" }}
                            >
                              {/* Date leads normally; once a page is named, the name takes the
                                  lead spot and the date shifts down to sit right before the
                                  weekday label instead. */}
                              <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{titles[d] || longDate(d)}</span>{" "}
                              <span style={{ fontSize: 10, opacity: 0.5 }}>✏️</span>
                              {titles[d] && <span style={{ fontSize: 12, color: C.muted, fontWeight: 400, marginLeft: 6 }}>{longDate(d)}</span>}
                              <span style={{ fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase", marginLeft: 8 }}>
                                {d === localToday() ? "Today" : weekdayOf(d)}
                              </span>
                            </span>
                          )}
                        </div>
                        {moodByDate[d] && (
                          <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }} title="Your mood that day">{MOODS[moodByDate[d].mood]}</span>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: 12, color: C.muted }}>{wc} {wc === 1 ? "word" : "words"}</span>
                        <button
                          onClick={() => requestDelete(d)}
                          title="Delete this page"
                          style={{
                            background: confirming ? "#e06b5e" : "transparent",
                            border: `1px solid ${confirming ? "#e06b5e" : C.border}`,
                            color: confirming ? "#fff" : C.muted,
                            borderRadius: 6, padding: "3px 8px", fontSize: 12, cursor: "pointer",
                            fontFamily: "inherit", whiteSpace: "nowrap",
                          }}
                        >{confirming ? "Delete?" : "🗑"}</button>
                      </div>
                    </div>
                    <button
                      onClick={() => openDay(d)}
                      style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left", width: "100%" }}
                    >
                      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                        {highlight(preview(pages[d], q), q)}
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Focus Timer ───────────────────────────────────────────────────────────────
// ── OBS Stream Overlay ────────────────────────────────────────────────────────
// Loaded at /timer-overlay?key=YOUR_KEY in an OBS browser source.
// Reads the shared timer row from Supabase and counts down locally from `ends_at`,
// so the display stays smooth while only polling every couple of seconds.
function TimerOverlay() {
  const params = new URLSearchParams(window.location.search);
  const shareKey = params.get("key");
  const showTask = params.get("task") !== "0";
  const size = parseInt(params.get("size") || "72", 10);
  const color = "#" + (params.get("color") || "ffffff");

  const [row, setRow] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [notFound, setNotFound] = useState(false);

  // Poll Supabase for state changes (pause / stop / new session)
  useEffect(() => {
    if (!shareKey) return;
    let cancelled = false;
    const fetchState = async () => {
      const { data, error } = await supabase
        .rpc("get_timer_by_key", { p_key: shareKey });
      if (cancelled) return;
      if (error) { console.error("overlay fetch:", error.message); return; }
      // The function returns a set, so take the first (and only) row
      const found = Array.isArray(data) ? data[0] : data;
      if (!found) { setNotFound(true); return; }
      setNotFound(false);
      setRow(found);
    };
    fetchState();
    const id = setInterval(fetchState, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [shareKey]);

  // Local ticking so the countdown is smooth between polls
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const fmt = (s) => {
    const safe = Math.max(0, Math.floor(s));
    return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
  };

  // Work out the number to display
  let secondsLeft = 0;
  let isLive = false;
  if (row) {
    if (row.running && !row.paused && row.ends_at) {
      secondsLeft = (new Date(row.ends_at).getTime() - now) / 1000;
      isLive = true;
    } else if (row.remaining != null) {
      // Paused, or idle — show the frozen value
      secondsLeft = row.remaining;
      isLive = row.running;
    }
  }

  const wrap = {
    minHeight: "100vh", margin: 0, background: "transparent",
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    fontFamily: "'DM Sans','Segoe UI',sans-serif",
  };

  if (!shareKey) {
    return <div style={{ ...wrap, color: "#fff" }}><span style={{ fontSize: 18 }}>Missing ?key= in the overlay URL</span></div>;
  }
  if (notFound) {
    return <div style={{ ...wrap, color: "#fff" }}><span style={{ fontSize: 18 }}>Timer not found — check your overlay key</span></div>;
  }

  return (
    <div style={wrap}>
      <style>{`html,body,#root{background:transparent !important;margin:0;padding:0;}`}</style>
      <span style={{
        fontSize: size, fontWeight: 800, color,
        fontVariantNumeric: "tabular-nums", lineHeight: 1,
        textShadow: "0 2px 14px rgba(0,0,0,0.65)",
        opacity: row && row.running ? 1 : 0.55,
      }}>
        {fmt(secondsLeft)}
      </span>
      {showTask && row?.task ? (
        <span style={{
          marginTop: 10, fontSize: Math.round(size * 0.24), color,
          opacity: 0.85, textShadow: "0 2px 10px rgba(0,0,0,0.65)",
          maxWidth: "90vw", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {row.task}
        </span>
      ) : null}
      {row && row.paused && (
        <span style={{ marginTop: 8, fontSize: Math.round(size * 0.2), color, opacity: 0.7, letterSpacing: 2, textShadow: "0 2px 10px rgba(0,0,0,0.65)" }}>
          PAUSED
        </span>
      )}
      {!isLive && !row?.paused && (
        <span style={{ marginTop: 8, fontSize: Math.round(size * 0.18), color, opacity: 0.5, letterSpacing: 2, textShadow: "0 2px 10px rgba(0,0,0,0.65)" }}>
          READY
        </span>
      )}
    </div>
  );
}

function FocusTimer({ timerState, setTimerState, onComplete, overlayKey }) {
  const PRESETS = [
    { label: "Quick Sprint", mins: 10,  emoji: "⚡" },
    { label: "Pomodoro",     mins: 25,  emoji: "🍅" },
    { label: "Deep Focus",   mins: 45,  emoji: "🧘" },
    { label: "Custom",       mins: null, emoji: "⚙️" },
  ];

  const { selected, customMins, task, running, paused, remaining, sessions, muted, confetti } = timerState;
  const set = (patch) => setTimerState(prev => ({ ...prev, ...patch }));

  const totalSecs = () => (PRESETS[selected].mins ?? (Number(customMins) || 25)) * 60;
  const start = () => set({ remaining: totalSecs(), running: true, paused: false });
  const pause = () => set({ paused: !paused });
  const stop  = () => setTimerState(prev => ({ ...prev, running: false, paused: false, remaining: null }));

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const progress = remaining !== null ? 1 - remaining / totalSecs() : 0;
  const r = 80, circ = 2 * Math.PI * r;

  // ── Editable session count ──
  const [editingSessions, setEditingSessions] = useState(false);
  const [copiedOverlay, setCopiedOverlay] = useState(false);
  const [sessionInput, setSessionInput] = useState("");

  const commitSessionEdit = () => {
    const val = parseInt(sessionInput, 10);
    if (!isNaN(val) && val >= 0) set({ sessions: Math.min(val, 99) });
    setEditingSessions(false);
  };

  // ── Editable timer length — click the big time to type minutes ──
  const [editingTime, setEditingTime] = useState(false);
  const [timeInput, setTimeInput]     = useState("");

  const commitTimeEdit = () => {
    const val = parseInt(timeInput, 10);
    if (!isNaN(val) && val >= 1) {
      const mins = Math.min(val, 180);
      // If the typed value matches a preset, select it; otherwise use Custom
      const presetIdx = PRESETS.findIndex(p => p.mins === mins);
      if (presetIdx !== -1) set({ selected: presetIdx });
      else set({ selected: 3, customMins: mins });
    }
    setEditingTime(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, position: "relative" }}>
      {confetti.length > 0 && (
        <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 999, overflow: "hidden" }}>
          {confetti.map(p => (
            <div key={p.id} style={{
              position: "absolute",
              left: `calc(50% + ${p.vx * 1.2}px)`, top: `calc(40% + ${p.vy * 1.2}px)`,
              width: p.shape === "circle" ? p.size : p.size * 1.5, height: p.size,
              borderRadius: p.shape === "circle" ? "50%" : 2, background: p.color,
              transform: `rotate(${p.rotation}deg)`,
              animation: `confettiFall 2.6s ease-out forwards`, opacity: 1,
            }} />
          ))}
        </div>
      )}
      <style>{`@keyframes confettiFall { 0% { transform: translate(0,0) rotate(0deg); opacity:1; } 100% { transform: translate(0,200px) rotate(720deg); opacity:0; } }`}</style>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {PRESETS.map((p, i) => (
          <button key={i} onClick={() => { set({ selected: i }); stop(); }} style={{
            background: selected === i ? C.accent + "22" : C.card,
            border: `1px solid ${selected === i ? C.accent : C.border}`,
            color: selected === i ? C.accentSoft : C.muted,
            borderRadius: 8, padding: "6px 14px", fontSize: 13, cursor: "pointer",
          }}>{p.emoji} {p.label} {p.mins && `(${p.mins}m)`}</button>
        ))}
      </div>

      {selected === 3 && !running && (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="text" inputMode="numeric"
            value={customMins}
            onChange={e => {
              const digits = e.target.value.replace(/\D/g, "");
              set({ customMins: digits === "" ? "" : Math.min(parseInt(digits, 10), 180) });
            }}
            onBlur={() => { if (!customMins || customMins < 1) set({ customMins: 25 }); }}
            style={{ ...inputStyle, width: 80, textAlign: "center" }}
          />
          <span style={{ color: C.muted, fontSize: 14 }}>minutes</span>
        </div>
      )}

      <div style={{ position: "relative", width: 200, height: 200 }}>
        <svg width={200} height={200} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={100} cy={100} r={r} fill="none" stroke={C.border} strokeWidth={6} />
          <circle cx={100} cy={100} r={r} fill="none" stroke={C.accent} strokeWidth={6}
            strokeDasharray={circ} strokeDashoffset={circ * (1 - progress)}
            strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s linear" }} />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
          {editingTime ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
              <input
                autoFocus
                type="text" inputMode="numeric"
                value={timeInput}
                onChange={e => setTimeInput(e.target.value.replace(/\D/g, ""))}
                onBlur={commitTimeEdit}
                onKeyDown={e => { if (e.key === "Enter") commitTimeEdit(); if (e.key === "Escape") setEditingTime(false); }}
                style={{
                  width: 76, fontSize: 32, fontWeight: 800, textAlign: "center",
                  background: C.bg, border: `1px solid ${C.accent}`, borderRadius: 8,
                  color: C.text, padding: "2px 6px", outline: "none", fontFamily: "inherit",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
              <span style={{ color: C.muted, fontSize: 13 }}>min</span>
            </div>
          ) : (
            <span
              onClick={() => {
                if (running || paused) return;
                setTimeInput(String(PRESETS[selected].mins ?? customMins ?? 25));
                setEditingTime(true);
              }}
              title={running || paused ? undefined : "Click to set minutes"}
              style={{
                fontSize: 38, fontWeight: 800, color: C.text, fontVariantNumeric: "tabular-nums",
                cursor: running || paused ? "default" : "pointer", userSelect: "none",
              }}
            >
              {running || paused ? fmt(remaining) : fmt(totalSecs())}
            </span>
          )}
          {/* Session count — click to edit */}
          {editingSessions ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ color: C.muted, fontSize: 12 }}>🍅 ×</span>
              <input
                autoFocus
                type="text" inputMode="numeric"
                value={sessionInput}
                onChange={e => setSessionInput(e.target.value.replace(/\D/g, ""))}
                onBlur={commitSessionEdit}
                onKeyDown={e => { if (e.key === "Enter") commitSessionEdit(); if (e.key === "Escape") setEditingSessions(false); }}
                style={{ width: 38, fontSize: 12, background: C.bg, border: `1px solid ${C.accent}`, borderRadius: 4, color: C.text, padding: "1px 4px", outline: "none", textAlign: "center", fontFamily: "inherit" }}
              />
            </div>
          ) : (
            <span
              onClick={() => { setSessionInput(String(sessions)); setEditingSessions(true); }}
              title="Click to edit"
              style={{ color: C.muted, fontSize: 12, cursor: "pointer", userSelect: "none", opacity: sessions > 0 ? 1 : 0.35 }}
            >
              🍅 ×{sessions}
            </span>
          )}
        </div>
      </div>

      {!running && (
        <input value={task} onChange={e => set({ task: e.target.value })}
          placeholder="What are you focusing on?"
          style={{ ...inputStyle, maxWidth: 320, textAlign: "center" }} />
      )}
      {running && task && (
        <div style={{ color: C.accentSoft, fontSize: 14, textAlign: "center", fontStyle: "italic" }}>
          Focusing on: {task}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        {!running
          ? <button onClick={start} style={{ ...btnStyle(C.green), padding: "12px 28px", fontSize: 16, fontWeight: 700 }}>▶ Lock In</button>
          : <>
              <button onClick={pause} style={btnStyle(paused ? C.green : C.orange)}>
                {paused ? "▶ Resume" : "⏸ Pause"}
              </button>
              <button onClick={stop} style={btnStyle(C.muted)}>⏹ Stop</button>
            </>}
        <button onClick={() => set({ muted: !muted })} style={{
          background: "transparent", border: `1px solid ${C.border}`,
          borderRadius: 8, padding: "9px 12px", cursor: "pointer", fontSize: 16,
        }}>{muted ? "🔇" : "🔔"}</button>
      </div>

      {!running && sessions > 0 && (
        <div style={{ color: C.green, fontSize: 13 }}>
          🎉 {sessions} session{sessions > 1 ? "s" : ""} completed today!
        </div>
      )}

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, maxWidth: 360, width: "100%" }}>
        <p style={{ color: C.muted, fontSize: 12, margin: 0, lineHeight: 1.6 }}>
          💡 <strong style={{ color: C.text }}>ADHD tip:</strong> Even a 10-minute sprint counts.
          Starting is the hardest part — once you begin, momentum builds.
          If you get distracted, just restart the timer. No judgment.
        </p>
      </div>

      {/* ── OBS stream overlay ── */}
      {overlayKey && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, maxWidth: 360, width: "100%", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 14 }}>🎥</span>
            <strong style={{ color: C.text, fontSize: 13 }}>Stream overlay</strong>
          </div>
          <p style={{ color: C.muted, fontSize: 11, margin: "0 0 10px 0", lineHeight: 1.6 }}>
            Add this as a <strong style={{ color: C.text }}>Browser source</strong> in OBS
            (1920×1080, transparent background) to show the countdown on stream.
          </p>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              readOnly
              value={`${window.location.origin}/timer-overlay?key=${overlayKey}`}
              onFocus={e => e.target.select()}
              style={{ flex: 1, minWidth: 0, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted, fontSize: 11, padding: "6px 8px", fontFamily: "monospace", outline: "none" }}
            />
            <button
              onClick={() => {
                navigator.clipboard?.writeText(`${window.location.origin}/timer-overlay?key=${overlayKey}`);
                setCopiedOverlay(true);
                setTimeout(() => setCopiedOverlay(false), 1800);
              }}
              style={{ background: copiedOverlay ? C.green : C.accent, border: "none", color: "#fff", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, transition: "background 0.15s", minWidth: 58 }}
            >{copiedOverlay ? "Copied!" : "Copy"}</button>
          </div>
          <p style={{ color: C.muted, fontSize: 10, margin: "8px 0 0 0", lineHeight: 1.6, opacity: 0.8 }}>
            Optional URL settings: <code>&size=120</code> for bigger text,
            {" "}<code>&color=4ade80</code> for a custom colour,
            {" "}<code>&task=0</code> to hide the task name.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Dailies Section ───────────────────────────────────────────────────────────
function DailiesSection({ tasks, setTasks }) {
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText]   = useState("");
  const [newDaily, setNewDaily]   = useState("");

  const habits    = tasks.filter(t => t.is_habit);
  const doneCount = habits.filter(t => t.done).length;

  const addDaily = () => {
    const text = newDaily.trim();
    if (!text) return;
    setTasks(prev => [...prev, {
      id: Date.now().toString(), text, priority: "next", done: false,
      project: "General", is_habit: true, last_completed_date: null,
      sort_order: -1, created_at: new Date().toISOString(),
    }]);
    setNewDaily("");
  };

  const toggleDone = (id) => setTasks(prev => prev.map(t => {
    if (t.id !== id) return t;
    const nowDone = !t.done;
    return { ...t, done: nowDone, last_completed_date: nowDone ? todayStr() : null };
  }));

  const startEdit  = (t) => { setEditingId(t.id); setEditText(t.text); };
  const cancelEdit = ()  => { setEditingId(null); setEditText(""); };

  const saveEdit = (id) => {
    if (editText.trim()) setTasks(prev => prev.map(t => t.id === id ? { ...t, text: editText.trim() } : t));
    cancelEdit();
  };

  const removeHabit = async (id) => {
    setTasks(prev => prev.filter(t => t.id !== id));
    await supabase.from("tasks").delete().eq("id", id);
    cancelEdit();
  };

  const unhabit = (id) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, is_habit: false, done: false, last_completed_date: null } : t));
    cancelEdit();
  };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.green}40`, borderRadius: 10, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ color: C.green, fontSize: 13, fontWeight: 700 }}>↻ Dailies</div>
        <div style={{ color: C.muted, fontSize: 12 }}>{doneCount}/{habits.length} done</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {habits.map(t => {
          if (editingId === t.id) {
            return (
              <div key={t.id} style={{
                background: C.bg, border: `1px solid ${C.accent}`,
                borderRadius: 8, padding: "10px 12px",
                display: "flex", flexDirection: "column", gap: 10,
              }}>
                <input
                  value={editText}
                  onChange={e => setEditText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveEdit(t.id); if (e.key === "Escape") cancelEdit(); }}
                  autoFocus
                  style={{ ...inputStyle, fontSize: 14 }}
                />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => saveEdit(t.id)}    style={btnStyle(C.green)}>Save</button>
                  <button onClick={() => unhabit(t.id)}     style={btnStyle(C.yellow)}>Remove repeat</button>
                  <button onClick={() => removeHabit(t.id)} style={btnStyle(C.red)}>Delete</button>
                  <button onClick={cancelEdit}              style={btnStyle(C.muted)}>Cancel</button>
                </div>
              </div>
            );
          }
          return (
            <div key={t.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 10px", borderRadius: 8,
              background: t.done ? C.green + "10" : C.bg,
              border: `1px solid ${t.done ? C.green + "40" : C.border}`,
              opacity: t.done ? 0.65 : 1, transition: "all 0.15s",
            }}>
              <div onClick={() => toggleDone(t.id)} style={{
                width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                // Buttons default to border-box but divs don't — without this
                // the border made this box render 24px vs the tasks' 20px
                boxSizing: "border-box",
                border: `2px solid ${t.done ? C.green : C.green + "60"}`,
                background: t.done ? C.green : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#000", fontSize: 12, cursor: "pointer", transition: "all 0.15s",
              }}>{t.done ? "✓" : ""}</div>
              <span onClick={() => toggleDone(t.id)} style={{
                flex: 1, fontSize: 14, color: C.text, cursor: "pointer",
                textDecoration: t.done ? "line-through" : "none",
              }}>{t.text}</span>
              <button onClick={() => startEdit(t)} title="Edit" style={rowEditBtn}>✏️</button>
            </div>
          );
        })}

        {/* Dailies are added right here now, not from the task list */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={newDaily}
            onChange={e => setNewDaily(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addDaily()}
            placeholder="Add a daily reminder..."
            style={{ ...inputStyle, flex: 1, fontSize: 13, padding: "7px 11px" }}
          />
          <button onClick={addDaily} style={{ ...btnStyle(C.green), padding: "7px 14px", fontSize: 13 }}>+ Add</button>
        </div>
      </div>
    </div>
  );
}

// ── Daily Planner ─────────────────────────────────────────────────────────────
function DailyPlanner({ tasks, setTasks, user, boards = [], onOpenBoard }) {
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  // ── Board cards flagged into the Nest ──
  // These are the real cards, not copies. They're merged into the task list below
  // so everything you're working on appears in one place.
  const [boardCards, setBoardCards] = useState([]);

  const loadBoardCards = () => {
    if (!user) return;
    supabase.from("birdie_kanban_cards")
      .select("*")
      .eq("in_nest", true)
      .then(({ data, error }) => {
        if (error) { console.error("nest cards:", error.message); return; }
        setBoardCards(data || []);
      });
  };

  useEffect(loadBoardCards, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh when the tab regains focus, so board edits show up here
  useEffect(() => {
    window.addEventListener("focus", loadBoardCards);
    return () => window.removeEventListener("focus", loadBoardCards);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const columnsFor = (projectName) => boardColumnsFor(boards.find(x => x.name === projectName));
  const columnLabelFor = (projectName, statusId) => {
    const col = columnsFor(projectName).find(c => c.id === statusId);
    return col ? col.label : null;
  };
  // Completing a Nest task sends its card to the board's "done" column — one
  // whose name reads as done (Done, Shipped, …) if there is one, otherwise the
  // far-right column, whatever it's named. The × never moves a card: it only
  // takes it off the Nest and leaves it in its current column.
  const findDoneColumn = (projectName) => {
    const cols = columnsFor(projectName);
    if (!cols || cols.length === 0) return null;
    return cols.find(c => /done|shipped|complete|finished|✅/i.test(c.label || "")) || cols[cols.length - 1];
  };

  // Send a Nest task to a board: it becomes a real card in the board's first
  // (leftmost) column, and stays visible in the Nest as a linked board card,
  // in the same spot the task was in.
  const sendTaskToBoard = async (task, boardId, fields = {}) => {
    const board = boards.find(b => String(b.id) === String(boardId));
    if (!board || !user) return;
    const cols = boardColumnsFor(board);
    const firstCol = cols[0]?.id || "future";
    // New cards go to the TOP of the column: one below the current lowest
    // sort_order in that (project, column). The board normalises these on load.
    let minOrder = 0;
    try {
      const { data: topRows } = await supabase
        .from("birdie_kanban_cards")
        .select("sort_order")
        .eq("project", board.name)
        .eq("status", firstCol)
        .order("sort_order", { ascending: true })
        .limit(1);
      if (topRows && topRows.length) minOrder = topRows[0].sort_order ?? 0;
    } catch (e) { /* fall back to 0 */ }
    // Diagnostic — shows exactly which column list was resolved and where the
    // card is being placed. If a card ever lands in the wrong column, this
    // one console line explains why.
    console.log(
      `[send-to-board] "${board.name}" columns: [${cols.map(c => c.label).join(" | ")}] → inserting into "${cols[0]?.label || firstCol}"`
    );
    const newCard = {
      id: "card-" + Date.now(),
      project: board.name,
      text: fields.text || task.text,
      status: firstCol,
      user_id: user.id,
      sort_order: minOrder - 1,
      in_nest: true,
      nest_priority: fields.priority || task.priority || "next",
      nest_sort_order: task.sort_order ?? 0,
    };
    let { error } = await supabase.from("birdie_kanban_cards").insert([newCard]);
    if (error && /nest_sort_order/i.test(error.message)) {
      // The nest_sort_order column hasn't been added in Supabase yet — insert
      // without it so the feature still works (the row will show at the top
      // of its category instead of in place).
      console.warn("nest_sort_order column missing in birdie_kanban_cards — run in Supabase SQL editor: alter table birdie_kanban_cards add column nest_sort_order integer;");
      const { nest_sort_order, ...fallbackCard } = newCard;
      ({ error } = await supabase.from("birdie_kanban_cards").insert([fallbackCard]));
    }
    if (error) { console.error("send task to board:", error.message); return; }
    // Remove the original task — the board card replaces it
    setTasks(prev => prev.filter(t => t.id !== task.id));
    await supabase.from("tasks").delete().eq("id", task.id);
    setBoardCards(prev => [...prev, newCard]);
    return newCard.id;
  };

  const completeBoardCard = async (card) => {
    const doneCol = findDoneColumn(card.project);
    const patch = { in_nest: false, nest_priority: null };
    if (doneCol && doneCol.id !== card.status) patch.status = doneCol.id;
    setBoardCards(prev => prev.filter(c => c.id !== card.id));
    const { error } = await supabase.from("birdie_kanban_cards").update(patch).eq("id", card.id);
    if (error) { console.error("complete from nest:", error.message); loadBoardCards(); }
  };

  const removeBoardCard = async (card) => {
    setBoardCards(prev => prev.filter(c => c.id !== card.id));
    const { error } = await supabase.from("birdie_kanban_cards")
      .update({ in_nest: false, nest_priority: null }).eq("id", card.id);
    if (error) { console.error("unpin:", error.message); loadBoardCards(); }
  };

  // Local-only, used while dragging so the preview doesn't hit the database
  // on every pointer move. The real write happens on drop.
  const previewBoardCardPriority = (card, priority, ord) => {
    setBoardCards(prev => prev.map(c =>
      c.id === card.id
        ? { ...c, nest_priority: priority, ...(ord !== undefined ? { nest_sort_order: ord } : {}) }
        : c
    ));
  };

  // Editing a board card from the Nest writes straight back to the card, so the
  // board shows the same change.
  const saveBoardCard = async (card, fields) => {
    setBoardCards(prev => prev.map(c => c.id === card.id ? { ...c, ...fields } : c));
    const { error } = await supabase.from("birdie_kanban_cards")
      .update(fields).eq("id", card.id);
    if (error) { console.error("save board card:", error.message); loadBoardCards(); }
  };

  const setBoardCardPriority = async (card, priority) => {
    if (priority === card.nest_priority) return;
    setBoardCards(prev => prev.map(c => c.id === card.id ? { ...c, nest_priority: priority } : c));
    const { error } = await supabase.from("birdie_kanban_cards")
      .update({ nest_priority: priority }).eq("id", card.id);
    if (error) { console.error("set board card priority:", error.message); loadBoardCards(); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ color: C.muted, fontSize: 13 }}>{today}</div>
        <div style={{ color: C.text, fontSize: 22, fontWeight: 800, marginTop: 2 }}>Mission Control 🚀</div>
      </div>

      {/* Dailies — always visible so new dailies can be added here */}
      <DailiesSection tasks={tasks} setTasks={setTasks} />

      {/* Tasks */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>📋 My Tasks</div>
        <TaskBoard
          tasks={tasks}
          setTasks={setTasks}
          boardCards={boardCards}
          onCompleteBoardCard={completeBoardCard}
          onRemoveBoardCard={removeBoardCard}
          onSetBoardCardPriority={setBoardCardPriority}
          onPreviewBoardCardPriority={previewBoardCardPriority}
          onSaveBoardCard={saveBoardCard}
          columnLabelFor={columnLabelFor}
          findDoneColumn={findDoneColumn}
          boards={boards}
          onSendTaskToBoard={sendTaskToBoard}
          onOpenBoard={onOpenBoard}
        />
      </div>
    </div>
  );
}

// ── Community Tasks ───────────────────────────────────────────────────────────
// ── OBS Community Tasks Overlay ───────────────────────────────────────────────
// Loaded at /community-overlay in an OBS browser source.
// Designed for readability over video: solid-ish dark panel, large heavy text,
// and a hard cap on visible rows so it never overflows the frame.
function CommunityOverlay() {
  const params = new URLSearchParams(window.location.search);
  const maxHeight = parseInt(params.get("height") || "420", 10);
  const speed     = parseInt(params.get("speed") || "18", 10);   // pixels per second
  const scrollMode = params.get("scroll") !== "0";               // on by default
  const fontSize  = parseInt(params.get("size") || "19", 10);
  const nameSize  = parseInt(params.get("namesize") || String(fontSize + 4), 10);
  const width     = parseInt(params.get("width") || "380", 10);
  const align     = params.get("align") || "left";      // left | right
  const showDone  = params.get("done") !== "0";
  const title     = params.get("title") ?? "Community Tasks";
  const accent    = "#" + (params.get("accent") || "a99bf9");
  const panel     = params.get("panel") !== "0";

  const [tasks, setTasks] = useState([]);
  const listRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const load = () => {
      supabase.from("community_tasks").select("*").order("sort_order", { ascending: true })
        .then(({ data, error }) => {
          if (error) { console.error("overlay:", error.message); return; }
          if (data) setTasks(data);
        });
    };
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, []);

  // Build a flat render list: a header row per user, then their tasks
  const grouped = tasks.reduce((acc, t) => {
    if (!acc[t.username]) acc[t.username] = [];
    acc[t.username].push(t);
    return acc;
  }, {});
  Object.keys(grouped).forEach(u => grouped[u].sort((a, b) => a.done - b.done));

  const rows = [];
  Object.entries(grouped).forEach(([username, userTasks]) => {
    const visible = showDone ? userTasks : userTasks.filter(t => !t.done);
    if (visible.length === 0) return;
    rows.push({ type: "user", key: "u-" + username, username });
    visible.forEach(t => rows.push({ type: "task", key: t.id, ...t }));
  });

  // Measure the real rendered height so the scroll speed stays constant
  // regardless of how many tasks there are
  useEffect(() => {
    if (!listRef.current) return;
    const measure = () => {
      if (listRef.current) setContentHeight(listRef.current.scrollHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(listRef.current);
    return () => ro.disconnect();
  }, [rows.length]);

  const openCount = tasks.filter(t => !t.done).length;

  // Scroll only kicks in when the list is actually taller than the window
  const needsScroll = scrollMode && contentHeight > maxHeight + 4;
  // Constant pixels-per-second so short and long lists crawl at the same pace
  const duration = needsScroll ? contentHeight / speed : 0;

  const renderRow = (row, dupe) => row.type === "user" ? (
    <div key={row.key + (dupe ? "-d" : "")} style={{
      color: accent, fontSize: nameSize, fontWeight: 800,
      letterSpacing: 0.2,
      marginTop: 16, textShadow: "0 2px 8px rgba(0,0,0,0.8)",
    }}>
      {row.username}
    </div>
  ) : (
    <div key={row.key + (dupe ? "-d" : "")} style={{
      display: "flex", alignItems: "flex-start", gap: 9,
      marginLeft: 12,
      color: row.done ? "rgba(255,255,255,0.45)" : "#fff",
      // Completed tasks shrink slightly so open tasks read as the priority
      fontSize: row.done ? Math.round(fontSize * 0.85) : fontSize,
      fontWeight: 600, lineHeight: 1.35,
      textShadow: "0 2px 8px rgba(0,0,0,0.8)",
      transition: "color 0.3s, opacity 0.3s",
    }}>
      {row.done ? (
        <span style={{ flexShrink: 0, fontSize: Math.round(fontSize * 0.68), marginTop: Math.round(fontSize * 0.2), lineHeight: 1 }}>✅</span>
      ) : (
        <span style={{
          flexShrink: 0,
          width: Math.round(fontSize * 0.7),
          height: Math.round(fontSize * 0.7),
          marginTop: Math.round(fontSize * 0.24),
          border: "2px solid rgba(255,255,255,0.9)",
          borderRadius: 3,
          background: "transparent",
          boxSizing: "border-box",
          boxShadow: "0 2px 6px rgba(0,0,0,0.7)",
        }} />
      )}
      <span style={{
        textDecoration: row.done ? "line-through" : "none",
        wordBreak: "break-word",
      }}>{row.task}</span>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh", margin: 0, background: "transparent",
      display: "flex", justifyContent: align === "right" ? "flex-end" : "flex-start",
      alignItems: "flex-start", padding: 24, boxSizing: "border-box",
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
    }}>
      <style>{`
        html,body,#root{background:transparent !important;margin:0;padding:0;}
        @keyframes taskCrawl {
          from { transform: translateY(0); }
          to   { transform: translateY(-${contentHeight}px); }
        }
        .task-crawl { animation: taskCrawl ${duration}s linear infinite; }
        /* Soften the top and bottom edges so rows fade in and out of view */
        .task-window-faded {
          -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 18px, #000 calc(100% - 18px), transparent 100%);
          mask-image: linear-gradient(to bottom, transparent 0, #000 18px, #000 calc(100% - 18px), transparent 100%);
        }
      `}</style>

      <div style={{
        width, maxWidth: "100%", boxSizing: "border-box",
        background: panel ? "rgba(12,12,18,0.62)" : "transparent",
        border: panel ? "1px solid rgba(255,255,255,0.10)" : "none",
        borderRadius: 14, padding: panel ? "16px 18px" : 0,
        backdropFilter: panel ? "blur(3px)" : "none",
      }}>
        {title && (
          <div style={{
            display: "flex", alignItems: "baseline", justifyContent: "space-between",
            gap: 10, marginBottom: 12,
          }}>
            <span style={{ color: "#fff", fontSize: fontSize + 3, fontWeight: 800, letterSpacing: 0.2, textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
              {title}
            </span>
            <span style={{ color: accent, fontSize: fontSize - 3, fontWeight: 700, textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
              {openCount} open
            </span>
          </div>
        )}

        {rows.length === 0 && (
          <div style={{ color: "rgba(255,255,255,0.65)", fontSize: fontSize - 2, fontWeight: 600, textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
            No tasks yet — type !task in chat
          </div>
        )}

        {rows.length > 0 && (
          <div
            className={needsScroll ? "task-window-faded" : ""}
            style={{ maxHeight, overflow: "hidden", position: "relative" }}
          >
            <div className={needsScroll ? "task-crawl" : ""}>
              <div ref={listRef} style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {rows.map(r => renderRow(r, false))}
              </div>
              {/* Duplicate copy so the loop is seamless */}
              {needsScroll && (
                <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 7 }}>
                  {rows.map(r => renderRow(r, true))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── About tab ─────────────────────────────────────────────────────────────────
// A hello from Birdie, what BirdieBrains is, and where to find Birdie elsewhere.
// The link colors match birdiefm.com; "All my links" goes to the full links page.
const ABOUT_LINKS = [
  { name: "Home",    note: "birdiefm.com",   url: "https://birdiefm.com",                color: "#ffc53d", emoji: "🏠" },
  { name: "Twitch",  note: "birdiefm",       url: "https://twitch.tv/birdiefm",          color: "#b78cff", emoji: "📺" },
  { name: "YouTube", note: "@birdiefm",      url: "https://www.youtube.com/@birdiefm",   color: "#ff6b85", emoji: "▶️" },
  { name: "Discord", note: "come hang out",  url: "https://discord.gg/KyGTdEJjNq",       color: "#7cc4ff", emoji: "💬" },
  { name: "Shop",    note: "treasures & oddities", url: "https://birdiefm.com/shop",     color: "#ff9cc6", emoji: "🛍️" },
];
const ALL_MY_LINKS = "https://links.birdiefm.com";

function AboutTab() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {/* Hello: the polaroid, then a little about Birdie and the app */}
      <div style={{ display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap",
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24 }}>
        <img src="https://birdiefm.com/birdie.jpg" alt="Polaroid of Birdie in fuzzy ears and face paint, making a peace sign"
          width="594" height="720"
          style={{ width: 150, height: "auto", transform: "rotate(-3deg)", borderRadius: 3,
            boxShadow: "0 12px 28px rgba(0,0,0,0.5)", margin: "4px auto" }} />
        <div style={{ flex: "1 1 280px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ color: C.text, fontSize: 22, fontWeight: 800 }}>Hi, I'm Birdie! 🐦</div>
          <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.65 }}>
            I built BirdieBrains for my own scattered brain: a place to dump thoughts, plan the day, focus, and actually get stuff done. It's free for anyone to use, so I hope it helps your brain too!
          </div>
          <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.65 }}>
            I'm a curious person making little apps, live broadcasts, beaded trinkets, and whatever catches my attention next. Come hang out on stream, where chat can add tasks with <strong style={{ color: C.accentSoft }}>!task</strong>.
          </div>
        </div>
      </div>

      {/* Where to find Birdie: a simple list, then a small "All my links" pill */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ color: C.text, fontSize: 16, fontWeight: 800 }}>Find me</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {ABOUT_LINKS.map((l) => (
            <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "baseline", gap: 10, alignSelf: "flex-start", textDecoration: "none", fontSize: 14 }}>
              <span>{l.emoji}</span>
              <span style={{ color: l.color, fontWeight: 700 }}>{l.name}</span>
              <span style={{ color: C.muted, fontSize: 13 }}>{l.note}</span>
            </a>
          ))}
        </div>
        <a href={ALL_MY_LINKS} target="_blank" rel="noopener noreferrer"
          style={{ alignSelf: "flex-start", marginTop: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid transparent",
            // Soft rainbow outline, the same one DonoFM's boxes use: the dark fill sits inside, the rainbow shows through the border
            background: `linear-gradient(${C.bg}, ${C.bg}) padding-box, linear-gradient(160deg, rgba(229,119,159,.55), rgba(232,184,75,.55), rgba(95,211,160,.55), rgba(91,184,232,.55), rgba(138,123,232,.55)) border-box`,
            color: C.text, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
          ★ All my links ↗
        </a>
      </div>
    </div>
  );
}

function CommunityTasks({ user }) {
  const BROADCASTER = "birdiefm";
  const [tasks, setTasks] = useState([]);
  const [copiedOverlay, setCopiedOverlay] = useState(false);

  useEffect(() => {
    const loadTasks = () => {
      supabase.from("community_tasks").select("*").order("sort_order", { ascending: true })
        .then(({ data }) => { if (data) setTasks(data); });
    };
    loadTasks();
    const pollInterval = setInterval(loadTasks, 3000);
    const channel = supabase.channel("community_tasks")
      .on("postgres_changes", { event: "*", schema: "public", table: "community_tasks" }, loadTasks)
      .subscribe();
    return () => { clearInterval(pollInterval); supabase.removeChannel(channel); };
  }, []);

  const grouped = tasks.reduce((acc, t) => {
    if (!acc[t.username]) acc[t.username] = [];
    acc[t.username].push(t);
    return acc;
  }, {});
  Object.keys(grouped).forEach(u => { grouped[u].sort((a, b) => a.done - b.done); });

  const deleteTask = async (id) => { await supabase.from("community_tasks").delete().eq("id", id); };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{ color: C.text, fontSize: 22, fontWeight: 800 }}>🌎 Community Tasks</div>
        <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
          Twitch chat can add tasks with <strong style={{ color: C.accentSoft }}>!task</strong> and complete them with <strong style={{ color: C.accentSoft }}>!done</strong>
        </div>
      </div>
      {/* Chat commands, up top so viewers see them before the tasks */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
        <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.7, margin: 0 }}>
          <strong style={{ color: C.text }}>💬 Chat commands:</strong><br />
          <strong style={{ color: C.accentSoft }}>!task [task name]</strong> — add a task<br />
          <strong style={{ color: C.accentSoft }}>!tasks</strong> — show all tasks in chat<br />
          <strong style={{ color: C.accentSoft }}>!mytasks</strong> — see your own tasks in chat<br />
          <strong style={{ color: C.accentSoft }}>!done [number]</strong> — complete task by number<br />
          <strong style={{ color: C.accentSoft }}>!done [task name]</strong> — complete task by name<br />
          <strong style={{ color: C.accentSoft }}>!done all</strong> — complete all your tasks<br />
          <strong style={{ color: C.accentSoft }}>!clear</strong> — remove your own completed tasks<br />
          <strong style={{ color: C.accentSoft }}>!commands</strong> — show this list in chat<br />
          <strong style={{ color: C.accentSoft }}>!reset</strong> — clear completed tasks (mods only)<br />
          <strong style={{ color: C.accentSoft }}>!reset all</strong> — clear all tasks (mods only)
        </p>
      </div>
      {Object.keys(grouped).length === 0 && (
        <div style={{ color: C.muted, textAlign: "center", padding: 40, fontSize: 14 }}>
          No community tasks yet — tell your chat to type !task to add one! 🐦
        </div>
      )}
      {Object.entries(grouped).map(([username, userTasks]) => (
        <div key={username} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ color: C.accentSoft, fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{username}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(() => {
              let counter = 0;
              return userTasks.map(t => {
                if (!t.done) counter++;
                return (
                  <div key={t.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    color: t.done ? C.muted : C.text, fontSize: 14, opacity: t.done ? 0.6 : 1,
                  }}>
                    {!t.done && <span style={{ color: C.muted, fontSize: 12, background: C.border + "80", borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>{counter}</span>}
                    {t.done && <span style={{ fontSize: 12, flexShrink: 0 }}>✅</span>}
                    <span style={{ textDecoration: t.done ? "line-through" : "none", flex: 1 }}>{t.task}</span>
                    {(user?.user_metadata?.name?.toLowerCase() === BROADCASTER || user?.user_metadata?.full_name?.toLowerCase() === BROADCASTER) && (
                      <button onClick={() => deleteTask(t.id)} style={{ background: "transparent", border: "none", color: C.red, cursor: "pointer", fontSize: 16, padding: "0 4px", opacity: 0.5, flexShrink: 0 }}>×</button>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ))}

      {/* ── OBS stream overlay ── */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 14 }}>🎥</span>
          <strong style={{ color: C.text, fontSize: 13 }}>Stream overlay</strong>
        </div>
        <p style={{ color: C.muted, fontSize: 11, margin: "0 0 10px 0", lineHeight: 1.6 }}>
          Add this as a <strong style={{ color: C.text }}>Browser source</strong> in OBS
          (1920×1080, transparent background) to show community tasks on stream.
        </p>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            readOnly
            value={`${window.location.origin}/community-overlay`}
            onFocus={e => e.target.select()}
            style={{ flex: 1, minWidth: 0, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted, fontSize: 11, padding: "6px 8px", fontFamily: "monospace", outline: "none" }}
          />
          <button
            onClick={() => {
              navigator.clipboard?.writeText(`${window.location.origin}/community-overlay`);
              setCopiedOverlay(true);
              setTimeout(() => setCopiedOverlay(false), 1800);
            }}
            style={{ background: copiedOverlay ? C.green : C.accent, border: "none", color: "#fff", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0, transition: "background 0.15s", minWidth: 58 }}
          >{copiedOverlay ? "Copied!" : "Copy"}</button>
        </div>
        <p style={{ color: C.muted, fontSize: 10, margin: "8px 0 0 0", lineHeight: 1.7, opacity: 0.85 }}>
          Optional settings, added after a <code>?</code> and joined with <code>&amp;</code>:<br />
          <code>height=420</code> visible area before scrolling starts<br />
          <code>speed=18</code> scroll pixels per second (lower = slower)<br />
          <code>scroll=0</code> turn scrolling off entirely<br />
          <code>size=19</code> task text size &nbsp;·&nbsp; <code>namesize=23</code> chat name size<br />
          <code>width=380</code> panel width &nbsp;·&nbsp; <code>align=right</code> side of screen<br />
          <code>done=0</code> hide completed &nbsp;·&nbsp; <code>panel=0</code> no dark backdrop<br />
          <code>accent=a99bf9</code> name colour &nbsp;·&nbsp; <code>title=Chat%20Goals</code> heading
        </p>
      </div>
    </div>
  );
}

// ── Streak pill ───────────────────────────────────────────────────────────────
function StreakPill({ emoji, value, label, color }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6,
      background: color + "15", border: `1px solid ${color}40`,
      borderRadius: 10, padding: "4px 10px",
    }}>
      <span style={{ fontSize: 14 }}>{emoji}</span>
      <span style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{value}</span>
      <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
    </div>
  );
}

// ── Reset Password Screen ─────────────────────────────────────────────────────
function ResetPasswordScreen() {
  const [pass, setPass]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);

  const submit = async () => {
    if (pass !== confirm) { setError("Passwords don't match!"); return; }
    if (pass.length < 6)  { setError("Password must be at least 6 characters."); return; }
    setError(""); setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password: pass });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setDone(true);
    setTimeout(() => window.location.href = "/", 2500);
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, display: "flex",
      alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
    }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
        padding: 40, width: "100%", maxWidth: 380,
        display: "flex", flexDirection: "column", gap: 20,
      }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase" }}>
            🐦 Birdie Brains
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: C.text, marginTop: 4 }}>
            Set new password 🔑
          </div>
        </div>

        {done ? (
          <div style={{ color: C.green, fontSize: 14, textAlign: "center", lineHeight: 1.6 }}>
            ✅ Password updated! Redirecting you to login...
          </div>
        ) : (
          <>
            <input value={pass} onChange={e => setPass(e.target.value)}
              placeholder="New password" type="password" style={inputStyle} />
            <input value={confirm} onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => e.key === "Enter" && submit()}
              placeholder="Confirm new password" type="password" style={inputStyle} />
            {error && <div style={{ color: C.red, fontSize: 13 }}>{error}</div>}
            <button onClick={submit} disabled={loading} style={btnStyle(C.accent)}>
              {loading ? "..." : "Update password"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
const TABS = [
  { id: "today",     label: "🪺 Tasks",       title: "Mission Control 🚀" },
  { id: "dump",      label: "🧠 Brain Dump", title: "Brain Dump"      },
  { id: "focus",     label: "🍅 Focus",      title: "Focus Timer"     },
  { id: "canvas",    label: "🎨 Scratchpad",     title: "Scratchpad" },
  { id: "kanban",    label: "📋 Boards",     title: "Projects" },
  { id: "community", label: "🌎 Community",  title: "Community Tasks" },
  { id: "about",     label: "🐦 About",      title: "About" },
];

// ── Sortable wrapper for a kanban card ────────────────────────────────────────
// Provides the drag transform + a dedicated grip handle so dragging never fights
// with the card's click-to-expand / click-to-edit behaviour.
// Resize a textarea to exactly fit its content, so it never shows an inner scrollbar
function autoGrow(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

function SortableCard({ id, children, disabled }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    position: "relative",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners }, isDragging })}
    </div>
  );
}

// Droppable wrapper for a column body, so cards can be dropped into empty columns
// Sortable wrapper for a whole column
function SortableColumn({ id, children, disabled }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled, data: { type: "column" } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    flexShrink: 0,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners }, isDragging })}
    </div>
  );
}

// Lets a card be dropped anywhere on a collapsed column. Rendered as an invisible
// overlay covering the whole column, so the cursor registers no matter where it is.
function CollapsedColumnDrop({ colId }) {
  const { setNodeRef } = useDroppable({ id: "col-drop-" + colId });
  return (
    <div
      ref={setNodeRef}
      style={{
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        borderRadius: 12,
        pointerEvents: "none",
      }}
    />
  );
}

function ColumnDropZone({ colId, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: "col-drop-" + colId });
  return (
    <div ref={setNodeRef} style={{ minHeight: 40, borderRadius: 8, transition: "background 0.15s", background: isOver ? "rgba(139,124,246,0.10)" : "transparent" }}>
      {children}
    </div>
  );
}

function KanbanBoard({ user, focusCard, onFocusHandled, dataEpoch }) {
  const [currentProject, setCurrentProject] = useState(null);
  const [currentBoardId, setCurrentBoardId] = useState(null);
  const [boards, setBoards] = useState([]);
  // Dashboard tile order — local-only, same as the column-order pattern
  // (birdie_kanban_cols_<id>) already used elsewhere in this file, rather
  // than a synced column, since it's just a per-browser display preference.
  const [boardOrder, setBoardOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("birdie_board_order") || "[]"); }
    catch { return []; }
  });
  const [newBoardName, setNewBoardName] = useState("");
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);
  const [boardItems, setBoardItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const KANBAN_COLOR_OPTIONS = [
    { key: "accentSoft", label: "Purple" },
    { key: "green",      label: "Green"  },
    { key: "yellow",     label: "Yellow" },
    { key: "orange",     label: "Orange" },
    { key: "red",        label: "Red"    },
    { key: "accent",     label: "Violet" },
    { key: "muted",      label: "Grey"   },
  ];

  // ── BOARD RE-NAME AND PERSISTENCE STATES ──
  const [isEditingName, setIsEditingName] = useState(false);
  const [boardNameInput, setBoardNameInput] = useState("");
  const [editingBoardId, setEditingBoardId] = useState(null);
  const [editingEmojiBoardId, setEditingEmojiBoardId] = useState(null); // board id, whichever view it's edited from
  const [activeGearMenuBoardId, setActiveGearMenuBoardId] = useState(null); // dashboard tile whose gear dropdown is open

  const [columnInputs, setColumnInputs] = useState({});
  const [columns, setColumns] = useState([
    { id: "future", label: "💭 Future / Ideas", color: "accentSoft" },
    { id: "progress", label: "⚡ In Progress", color: "yellow" },
    { id: "shipped", label: "✅ Shipped", color: "green" }
  ]);

  // ── COLLAPSED / ARRANGE / EDIT ──
  const [collapsedColumns, setCollapsedColumns] = useState({});
  const [arrangingColumns, setArrangingColumns] = useState(false);
  const [editingColId, setEditingColId] = useState(null);
  const [renamingInMenuColId, setRenamingInMenuColId] = useState(null);
  const [colLabelInput, setColLabelInput] = useState("");
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState("");

  // Track which column has its gear menu open
  const [activeGearMenuColId, setActiveGearMenuColId] = useState(null);

  // ── CARD INTERACTION STATE ──
  const [editingCardId, setEditingCardId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [cardTextInput, setCardTextInput] = useState("");
  const [justMovedCardId, setJustMovedCardId] = useState(null);
  const flashMoved = (id) => {
    setJustMovedCardId(id);
    setTimeout(() => setJustMovedCardId(curr => (curr === id ? null : curr)), 1100);
  };
  const [focusedCardId, setFocusedCardId] = useState(null); // stronger, longer glow when opened from the Nest
  const flashFocus = (id) => {
    setFocusedCardId(id);
    setTimeout(() => setFocusedCardId(curr => (curr === id ? null : curr)), 3000);
  };
  const [subtleFlashId, setSubtleFlashId] = useState(null); // quick, gentle flash for task-status changes
  const flashSubtle = (id) => {
    setSubtleFlashId(id);
    setTimeout(() => setSubtleFlashId(curr => (curr === id ? null : curr)), 900);
  };

  // FLIP animation for card reordering — snapshots positions before, animates after re-render
  const cardRefs = useRef({});
  const nestChangedRef = useRef(null); // card whose task status changed during the current edit
  const prevEditingRef = useRef(null);

  // ── DRAG AND DROP ──
  const [activeDragId, setActiveDragId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);
  const sensors = useSensors(
    // 6px activation distance means a plain click still registers as a click,
    // so click-to-expand and click-to-edit keep working normally
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sticky horizontal scrollbar — mirrors the board scroll so it's always reachable
  const boardScrollRef = useRef(null);
  const stickyScrollRef = useRef(null);
  const syncingRef = useRef(false);
  const [scrollMeta, setScrollMeta] = useState({ width: 0, overflowing: false });
  const loadGeneration = useRef(0); // guards the boards/cards load effect against out-of-order responses

  useEffect(() => {
    const el = boardScrollRef.current;
    if (!el) return;
    const measure = () => {
      setScrollMeta({
        width: el.scrollWidth,
        overflowing: el.scrollWidth > el.clientWidth + 4,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [columns, boardItems, currentProject, collapsedColumns]);

  const syncFromBoard = () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (stickyScrollRef.current && boardScrollRef.current) {
      stickyScrollRef.current.scrollLeft = boardScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => { syncingRef.current = false; });
  };
  const syncFromSticky = () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    if (stickyScrollRef.current && boardScrollRef.current) {
      boardScrollRef.current.scrollLeft = stickyScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => { syncingRef.current = false; });
  };
  // Accepts either a theme key ("green") or a raw hex from the custom colour picker
  const colColor = (key) => {
    if (typeof key === "string" && key.startsWith("#")) return key;
    return C[key] || C.accentSoft;
  };

  // Auto-close gear menu when clicking anywhere else
  useEffect(() => {
    const handleOutsideClick = () => { setActiveGearMenuColId(null); setConfirmDeleteId(null); setRenamingInMenuColId(null); setActiveGearMenuBoardId(null); };
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  // ── LOAD DATA ──
  // Depends on user?.id too (not just dataEpoch, bumped after a guest→account
  // merge) — otherwise signing out mid-session (which swaps in a brand new
  // guest identity without bumping dataEpoch) would leave this component
  // showing whatever boards/cards belonged to the previous identity, since
  // nothing would ever trigger a refetch while it stays mounted.
  useEffect(() => {
    setCurrentProject(null);
    setCurrentBoardId(null);
    // Guards against a stale response overwriting fresher state — this
    // effect can genuinely fire more than once for the same mount (React
    // StrictMode double-invokes effects in dev specifically to catch bugs
    // like this one), and if an earlier invocation's fetch happens to
    // resolve *after* a newer one already rendered correctly, applying it
    // would silently wipe out whatever just loaded — including a board
    // someone created in the meantime. Only ever applied in dev; StrictMode
    // doesn't double-invoke in production builds.
    loadGeneration.current += 1;
    const myGeneration = loadGeneration.current;
    async function loadWorkspaceData() {
      setLoading(true);
      try {
        // No starter-board seeding here on purpose — a "select, then insert
        // if empty" step isn't atomic, and StrictMode's dev double-invoke (or
        // any other double-fire of this effect) could double-seed. Everyone,
        // guest or real, starts blank and creates their own first board.
        const { data: dbBoards, error: boardErr } = await supabase
          .from("birdie_kanban_boards").select("*").order("created_at", { ascending: true });
        if (boardErr) throw boardErr;
        if (loadGeneration.current !== myGeneration) return;

        const { data: dbCards, error: cardErr } = await supabase
          .from("birdie_kanban_cards").select("*").order("sort_order", { ascending: true }).order("created_at", { ascending: true });
        if (cardErr) throw cardErr;
        if (loadGeneration.current !== myGeneration) return;

        setBoards(dbBoards || []);

        // One-time migration: give any board that doesn't have its own emoji
        // slot yet a value — extracting a leading emoji already in its title
        // when there's cleanly one there (this is what turns "🚀 Main
        // Roadmap" into emoji 🚀 + title "Main Roadmap"), otherwise just the
        // default, leaving the title untouched rather than guessing at an
        // emoji buried somewhere else in it.
        const emojiFixes = (dbBoards || [])
          .filter(b => !b.emoji)
          .map(b => {
            const { emoji, rest } = splitLeadingEmoji(b.name);
            return { id: b.id, oldName: b.name, emoji: emoji || DEFAULT_BOARD_EMOJI, name: emoji ? rest : b.name };
          });
        if (emojiFixes.length > 0) {
          setBoards(prev => prev.map(b => {
            const fix = emojiFixes.find(f => f.id === b.id);
            return fix ? { ...b, emoji: fix.emoji, name: fix.name } : b;
          }));
          emojiFixes.forEach(async (fix) => {
            const { error } = await supabase.from("birdie_kanban_boards").update({ emoji: fix.emoji, name: fix.name }).eq("id", fix.id);
            if (error) {
              if (/emoji/i.test(error.message)) {
                console.warn("emoji column missing in birdie_kanban_boards — run in Supabase SQL editor: alter table birdie_kanban_boards add column emoji text;");
              } else {
                console.error("Error backfilling board emoji:", error.message);
              }
              return;
            }
            // Cards are linked by board name, not id — cascade the same way
            // a manual rename already does whenever the title actually changed.
            if (fix.name !== fix.oldName) {
              setBoardItems(prev => prev.map(item => item.project === fix.oldName ? { ...item, project: fix.name } : item));
              await supabase.from("birdie_kanban_cards").update({ project: fix.name }).eq("project", fix.oldName);
            }
          });
        }

        if (dbCards) {
          setBoardItems(dbCards);
          // Backfill distinct sort_order values per (project, status) group — older cards may
          // share the same default sort_order (e.g. all 0 or null), which makes reordering a no-op.
          const groups = {};
          dbCards.forEach(c => {
            const key = c.project + "||" + c.status;
            (groups[key] = groups[key] || []).push(c);
          });
          const fixes = [];
          Object.values(groups).forEach(group => {
            const distinct = new Set(group.map(c => c.sort_order ?? 0));
            if (distinct.size < group.length) {
              group.forEach((c, i) => { if (c.sort_order !== i) fixes.push({ id: c.id, sort_order: i }); });
            }
          });
          if (fixes.length > 0) {
            setBoardItems(prev => prev.map(item => {
              const fix = fixes.find(f => f.id === item.id);
              return fix ? { ...item, sort_order: fix.sort_order } : item;
            }));
            fixes.forEach(fix => {
              supabase.from("birdie_kanban_cards").update({ sort_order: fix.sort_order }).eq("id", fix.id)
                .then(({ error }) => { if (error) console.error("Error backfilling sort_order:", error.message); });
            });
          }
        }
      } catch (err) {
        console.error("Error loading workspace data:", err.message);
      } finally {
        if (loadGeneration.current === myGeneration) setLoading(false);
      }
    }
    loadWorkspaceData();
  }, [user?.id, dataEpoch]);

  // ── FOCUS A CARD (opened from the Nest's board icon) ──
  // First switch to the card's board, then scroll to it and flash it.
  useEffect(() => {
    if (!focusCard) return;
    const board = boards.find(b => b.name === focusCard.project);
    if (board && (currentProject !== board.name || currentBoardId !== board.id)) {
      setCurrentProject(board.name);
      setCurrentBoardId(board.id);
    }
  }, [focusCard, boards]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!focusCard || currentProject !== focusCard.project) return;
    let tries = 0, timer;
    const tryScroll = () => {
      const el = cardRefs.current[focusCard.id];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        flashMoved(focusCard.id);
        flashFocus(focusCard.id);
        onFocusHandled && onFocusHandled();
      } else if (tries < 20) {
        tries += 1;
        timer = setTimeout(tryScroll, 100);
      } else {
        onFocusHandled && onFocusHandled();
      }
    };
    timer = setTimeout(tryScroll, 150);
    return () => clearTimeout(timer);
  }, [focusCard, currentProject, boardItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flash a card orange only after its edit closes (Done / Enter / click-away),
  // and only if its task status was changed during that edit.
  useEffect(() => {
    const prev = prevEditingRef.current;
    prevEditingRef.current = editingCardId;
    if (prev && !editingCardId && nestChangedRef.current === prev) {
      flashSubtle(prev);
      nestChangedRef.current = null;
    }
  }, [editingCardId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── RESOLVE THIS BOARD'S COLUMNS ──
  // Depends on `boards` (not just currentBoardId) so that if this effect first
  // runs before the board list has finished loading, it re-resolves once real
  // data (and the board's real columns_config) is in — rather than getting
  // stuck on the hardcoded default fallback.
  useEffect(() => {
    if (!currentBoardId) return;
    const board = boards.find(b => b.id === currentBoardId);
    setColumns(boardColumnsFor(board || { id: currentBoardId }));
  }, [currentBoardId, boards]);

  // ── WHEN ENTERING A BOARD (reset transient per-board UI state) ──
  // Deliberately NOT dependent on `boards` — that list also changes on every
  // column edit (persistColumns updates it), and re-running this on every such
  // change would kick the user out of arranging/renaming columns mid-edit.
  useEffect(() => {
    if (!currentProject || !currentBoardId) return;
    setArrangingColumns(false);
    setEditingColId(null);
    setActiveGearMenuColId(null);
    setCollapsedColumns({});
  }, [currentProject, currentBoardId]);

  // ── REPAIR ORPHANED CARDS ──
  // If a card's status doesn't match any current column (e.g. a column was
  // reset/replaced at some point), the card still counts toward the board's
  // total but never renders in any column. Reassign any such cards to the
  // first column so they're never silently hidden.
  //
  // This resolves columns fresh from `board` itself rather than reading the
  // `columns` state above — that state can lag a render behind right after
  // switching boards, and reacting to a still-default `columns` here used to
  // let real cards in a board's *custom* columns look orphaned for one pass,
  // permanently reassigning them (in Supabase too) to the default "future"
  // column. Also requires `board` to actually be found — if `boards` hasn't
  // loaded yet, there's nothing real to repair against yet.
  useEffect(() => {
    if (!currentProject || !currentBoardId) return;
    const board = boards.find(b => b.id === currentBoardId);
    if (!board) return;
    const resolvedColumns = boardColumnsFor(board);
    const validStatuses = new Set(resolvedColumns.map(c => c.id));
    const firstColId = resolvedColumns[0]?.id;
    const orphans = boardItems.filter(item => item.project === currentProject && !validStatuses.has(item.status));
    if (orphans.length === 0 || !firstColId) return;
    setBoardItems(prev => prev.map(item =>
      (item.project === currentProject && !validStatuses.has(item.status))
        ? { ...item, status: firstColId }
        : item
    ));
    orphans.forEach(item => {
      supabase.from("birdie_kanban_cards").update({ status: firstColId }).eq("id", item.id)
        .then(({ error }) => { if (error) console.error("Error repairing orphaned card:", error.message); });
    });
  }, [currentProject, currentBoardId, boards]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistColumns = async (nextCols) => {
    setColumns(nextCols);
    if (!currentBoardId) return;
    localStorage.setItem("birdie_kanban_cols_" + currentBoardId, JSON.stringify(nextCols));
    const { error } = await supabase.from("birdie_kanban_boards").update({ columns_config: nextCols }).eq("id", currentBoardId);
    if (error) console.error("save columns to Supabase failed (board and database may be out of sync):", error.message);
    setBoards(prev => prev.map(b => b.id === currentBoardId ? { ...b, columns_config: nextCols } : b));
  };

  // ── COLUMN CONTROLS ──
  const addColumn = (position = "front") => {
    if (!currentBoardId) return;
    const usedColors = new Set(columns.map(col => col.color));
    const availableColor = KANBAN_COLOR_OPTIONS.find(opt => !usedColors.has(opt.key))?.key || "accentSoft";
    const newCol = { id: "col_" + Date.now(), label: "New Column", color: availableColor };
    const updatedCols = position === "front" ? [newCol, ...columns] : [...columns, newCol];
    persistColumns(updatedCols);
    setTimeout(() => { setColLabelInput("New Column"); setEditingColId(newCol.id); }, 50);
  };

  const deleteColumn = (colId) => {
    if (!window.confirm("Delete this column? Cards in it will also be removed.")) return;
    persistColumns(columns.filter(c => c.id !== colId));
    setBoardItems(prev => prev.filter(item => item.status !== colId));
    supabase.from("birdie_kanban_cards").delete().eq("status", colId).eq("project", currentProject);
  };

  const saveColLabel = (colId) => {
    const trimmed = colLabelInput.trim();
    if (!trimmed) { setEditingColId(null); return; }
    persistColumns(columns.map(c => c.id === colId ? { ...c, label: trimmed } : c));
    setEditingColId(null);
  };

  const saveColColor = (colId, colorKey) => {
    persistColumns(columns.map(c => c.id === colId ? { ...c, color: colorKey } : c));
  };

  // ── BOARD CRUD ──
  const addBoard = async () => {
    if (!newBoardName.trim()) return;
    // Guards against the exact race that made new boards vanish: creating one
    // while the initial (or a post-merge) fetch is still in flight let that
    // fetch's older, board-less response land after this one and wipe it out.
    // The button below is disabled during that window too, but this keeps
    // the function itself safe regardless of how it's triggered.
    if (loading) return;
    if (user?.is_anonymous && boards.length >= 1) return;
    const { data: { user: freshUser } } = await supabase.auth.getUser();
    const tempId = "board-" + Date.now();
    // If you typed your own emoji at the front, that becomes the board's
    // emoji instead of the default — same rule the migration above uses.
    const { emoji, rest } = splitLeadingEmoji(newBoardName.trim());
    const newBoard = { name: emoji ? rest : newBoardName.trim(), emoji: emoji || DEFAULT_BOARD_EMOJI, user_id: freshUser?.id || null };
    setBoards(prev => [...prev, { id: tempId, ...newBoard }]);
    setNewBoardName("");
    let { data, error } = await supabase.from("birdie_kanban_boards").insert([newBoard]).select();
    if (error && /emoji/i.test(error.message)) {
      // The emoji column hasn't been added in Supabase yet — insert without
      // it so board creation still works (the emoji just won't stick until
      // it's added).
      console.warn("emoji column missing in birdie_kanban_boards — run in Supabase SQL editor: alter table birdie_kanban_boards add column emoji text;");
      const { emoji: _drop, ...fallbackBoard } = newBoard;
      ({ data, error } = await supabase.from("birdie_kanban_boards").insert([fallbackBoard]).select());
    }
    if (error) {
      console.error("Error saving board:", error.message);
      setBoards(prev => prev.filter(b => b.id !== tempId));
    } else if (data && data[0]) {
      setBoards(prev => prev.map(b => b.id === tempId ? data[0] : b));
    }
  };

  const updateBoardName = async () => {
    if (!boardNameInput.trim() || boardNameInput.trim() === currentProject) {
      setIsEditingName(false); return;
    }
    const oldName = currentProject;
    const newName = boardNameInput.trim();
    setBoards(prev => prev.map(b => b.id === currentBoardId ? { ...b, name: newName } : b));
    setBoardItems(prev => prev.map(item => item.project === oldName ? { ...item, project: newName } : item));
    setCurrentProject(newName);
    setIsEditingName(false);
    await supabase.from("birdie_kanban_boards").update({ name: newName }).eq("id", currentBoardId);
    await supabase.from("birdie_kanban_cards").update({ project: newName }).eq("project", oldName);
  };

  const updateDashboardBoardName = async (boardId, oldName) => {
    if (!boardNameInput.trim() || boardNameInput.trim() === oldName) { setEditingBoardId(null); return; }
    const newName = boardNameInput.trim();
    setBoards(prev => prev.map(b => b.id === boardId ? { ...b, name: newName } : b));
    setBoardItems(prev => prev.map(item => item.project === oldName ? { ...item, project: newName } : item));
    setEditingBoardId(null);
    await supabase.from("birdie_kanban_boards").update({ name: newName }).eq("id", boardId);
    await supabase.from("birdie_kanban_cards").update({ project: newName }).eq("project", oldName);
  };

  const updateBoardEmoji = async (boardId, emoji) => {
    // Falls back to the default rather than leaving the board with no icon.
    const next = emoji || DEFAULT_BOARD_EMOJI;
    setEditingEmojiBoardId(null);
    setBoards(prev => prev.map(b => b.id === boardId ? { ...b, emoji: next } : b));
    const { error } = await supabase.from("birdie_kanban_boards").update({ emoji: next }).eq("id", boardId);
    if (error) console.error("Error saving board emoji:", error.message);
  };

  // Colors the board's title, same preset-swatches-plus-custom-wheel pattern
  // as a column's text color. Left open (not closed on pick) so you can try
  // a few in a row, matching the column menu's behavior.
  const updateBoardColor = async (boardId, colorKey) => {
    setBoards(prev => prev.map(b => b.id === boardId ? { ...b, color: colorKey } : b));
    const { error } = await supabase.from("birdie_kanban_boards").update({ color: colorKey }).eq("id", boardId);
    if (error) {
      if (/color/i.test(error.message)) {
        console.warn("color column missing in birdie_kanban_boards — run in Supabase SQL editor: alter table birdie_kanban_boards add column color text;");
      } else {
        console.error("Error saving board color:", error.message);
      }
    }
  };

  const deleteBoard = async (e, boardId, boardName) => {
    e.stopPropagation();
    if (!window.confirm('Delete "' + boardName + '"?')) return;
    setBoards(prev => prev.filter(b => b.id !== boardId));
    setBoardItems(prev => prev.filter(item => item.project !== boardName));
    await supabase.from("birdie_kanban_boards").delete().eq("id", boardId);
    await supabase.from("birdie_kanban_cards").delete().eq("project", boardName);
  };

  // ── CARD CRUD ──
  const addItem = async (columnId) => {
    const textToSave = columnInputs[columnId]?.trim();
    if (!textToSave || !currentProject) return;
    const { data: { user } } = await supabase.auth.getUser();
    const cardId = "card-" + Date.now();
    // New cards always go to the top: give it sort_order 0 and shift the rest down
    const newItem = { id: cardId, project: currentProject, text: textToSave, status: columnId, user_id: user?.id || null, sort_order: 0 };

    const existing = boardItems
      .filter(i => i.project === currentProject && i.status === columnId)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    setBoardItems(prev => [
      ...prev.map(i =>
        (i.project === currentProject && i.status === columnId)
          ? { ...i, sort_order: (i.sort_order ?? 0) + 1 }
          : i
      ),
      newItem,
    ]);
    setColumnInputs(prev => ({ ...prev, [columnId]: "" }));

    const { error } = await supabase.from("birdie_kanban_cards").insert([newItem]);
    if (error) {
      console.error("Error saving card:", error.message);
      setBoardItems(prev => prev.filter(item => item.id !== cardId));
      return;
    }
    // Persist the shifted order for the existing cards
    for (let i = 0; i < existing.length; i++) {
      const { error: e } = await supabase
        .from("birdie_kanban_cards")
        .update({ sort_order: i + 1 })
        .eq("id", existing[i].id);
      if (e) console.error("Error reordering after add:", e.message);
    }
  };

  // ── DRAG HANDLERS ──
  // Columns use ids prefixed "col-sort-" so they're never confused with card ids
  const isColumnDragId = (id) => typeof id === "string" && id.startsWith("col-sort-");

  // Nearest droppable to the *cursor*, measured to the edge of each rect.
  // dnd-kit's built-in fallbacks (closestCenter / closestCorners) measure the
  // dragged item's rectangle instead, which is much larger than the pointer and
  // can keep matching a column the cursor has already left.
  const nearestToPointer = (args) => {
    const p = args.pointerCoordinates;
    if (!p) return [];
    let best = null;
    let bestDist = Infinity;
    for (const container of args.droppableContainers) {
      const rect = args.droppableRects.get(container.id);
      if (!rect) continue;
      const clampedX = Math.max(rect.left, Math.min(p.x, rect.left + rect.width));
      const clampedY = Math.max(rect.top, Math.min(p.y, rect.top + rect.height));
      const dx = p.x - clampedX;
      const dy = p.y - clampedY;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) { bestDist = dist; best = container; }
    }
    return best ? [{ id: best.id }] : [];
  };

  // Only consider droppables that make sense for whatever is being dragged.
  // Without this, dragging a column can land on a card, which silently cancels the drop.
  const collisionDetectionStrategy = (args) => {
    const draggingColumn = isColumnDragId(args.active.id);
    const containers = args.droppableContainers.filter(c =>
      draggingColumn ? isColumnDragId(c.id) : !isColumnDragId(c.id)
    );
    const scoped = { ...args, droppableContainers: containers };

    // Columns follow the cursor too. Using the dragged column's rectangle instead
    // is unreliable here because collapsed columns are far narrower than expanded
    // ones, so the rect centre often sits well away from the pointer.
    if (draggingColumn) {
      const pointerHits = pointerWithin(scoped);
      return pointerHits.length > 0 ? pointerHits : nearestToPointer(scoped);
    }

    // Cards follow the cursor rather than the dragged card's rectangle
    const pointerHits = pointerWithin(scoped);
    const hits = pointerHits.length > 0 ? pointerHits : nearestToPointer(scoped);
    if (hits.length === 0) return hits;

    const isColDrop = (id) => typeof id === "string" && id.startsWith("col-drop-");

    // Prefer a specific card — that's what opens the insertion gap. Fall back to
    // the column droppable only when there's no card under the cursor (empty or
    // collapsed columns).
    const cardHit = hits.find(h => !isColDrop(h.id));
    return cardHit ? [cardHit] : hits;
  };

  const dragStartColRef = useRef(null);

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
    const card = boardItems.find(i => i.id === event.active.id);
    dragStartColRef.current = card ? card.status : null;
    // Close any open menus so the drag preview is clean
   
    setEditingCardId(null);
    setConfirmDeleteId(null);
    setActiveGearMenuColId(null);
  };

  // Track which column is being hovered, so it can highlight as a drop target.
  // `over` may be a card OR a column droppable, so resolve both back to a column id.
  const handleDragOver = (event) => {
    const { active, over } = event;
    if (isColumnDragId(active.id)) { setDragOverColId(null); return; }
    if (!over) { setDragOverColId(null); return; }

    const overCard = boardItems.find(i => i.id === over.id);
    const overColumnId = overCard
      ? overCard.status
      : (typeof over.id === "string" && over.id.startsWith("col-drop-")
          ? over.id.replace("col-drop-", "")
          : null);
    if (!overColumnId) { setDragOverColId(null); return; }

    setDragOverColId(overColumnId);

    // A sortable list can only open a gap for cards it actually contains, so a
    // card dragged from another column shows no preview. Moving it into the
    // destination list mid-drag makes the gap appear. The real save still happens
    // on drop; this is purely local state.
    const activeCard = boardItems.find(i => i.id === active.id);
    if (!activeCard) return;

    // Collapsed columns render no cards, so parking the card there removes it from
    // every visible list — which closes the gap left behind in the origin column.
    // Only the drag overlay stays visible, which is what we want. Column widths are
    // fixed, so the origin column shrinking can't shift anything under the cursor.
    if (collapsedColumns[overColumnId]) {
      if (activeCard.status !== overColumnId) {
        setBoardItems(prev => prev.map(item =>
          item.id === active.id
            ? { ...item, status: overColumnId, sort_order: -1 }
            : item
        ));
      }
      return;
    }

    if (activeCard.status === overColumnId) return;

    const destItems = boardItems
      .filter(i => i.project === currentProject && i.status === overColumnId && i.id !== active.id)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    let insertIdx;
    if (overCard) {
      insertIdx = destItems.findIndex(i => i.id === overCard.id);
      if (insertIdx === -1) insertIdx = destItems.length;
    } else {
      insertIdx = destItems.length;
    }

    const reordered = [...destItems];
    reordered.splice(insertIdx, 0, { ...activeCard, status: overColumnId });

    setBoardItems(prev => prev.map(item => {
      if (item.id === active.id) {
        return { ...item, status: overColumnId, sort_order: insertIdx };
      }
      if (item.project === currentProject && item.status === overColumnId) {
        const idx = reordered.findIndex(d => d.id === item.id);
        return idx === -1 ? item : { ...item, sort_order: idx };
      }
      return item;
    }));
  };

  // Persist a full column's ordering to Supabase
  const persistColumnOrder = async (items, statusId) => {
    for (let i = 0; i < items.length; i++) {
      const { error } = await supabase
        .from("birdie_kanban_cards")
        .update({ sort_order: i, status: statusId })
        .eq("id", items[i].id);
      if (error) console.error("Error persisting drag order:", error.message);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    setDragOverColId(null);

    // Dropped on nothing — undo any mid-drag column change so the card returns
    // to where it started rather than being stranded in a hovered column.
    if (!over) {
      const originCol = dragStartColRef.current;
      dragStartColRef.current = null;
      if (originCol) {
        setBoardItems(prev => prev.map(item =>
          item.id === active.id && item.status !== originCol
            ? { ...item, status: originCol }
            : item
        ));
      }
      return;
    }

    // ── COLUMN REORDER ──
    if (isColumnDragId(active.id)) {
      const fromId = active.id.replace("col-sort-", "");
      const fromIdx = columns.findIndex(c => c.id === fromId);
      if (fromIdx === -1) return;

      let toIdx;
      if (isColumnDragId(over.id)) {
        const toId = over.id.replace("col-sort-", "");
        toIdx = columns.findIndex(c => c.id === toId);
        if (toIdx === -1) return;
        if (active.id === over.id) return;
      } else {
        // Released past the last column (e.g. over the "+ Add Column" ghost) —
        // treat that as "move to the end" rather than cancelling the drop
        toIdx = columns.length - 1;
        if (fromIdx === toIdx) return;
      }

      const next = [...columns];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      persistColumns(next);
      return;
    }

    // ── CARD MOVE / REORDER ──
    const activeCard = boardItems.find(i => i.id === active.id);
    if (!activeCard) return;

    // Figure out the destination column: `over` is either another card or a column droppable
    const overCard = boardItems.find(i => i.id === over.id);
    const overColumnId = overCard
      ? overCard.status
      : (typeof over.id === "string" && over.id.startsWith("col-drop-")
          ? over.id.replace("col-drop-", "")
          : null);
    if (!overColumnId) return;

    // The card may already have been moved into the destination column during the
    // drag (that's what produces the gap preview), so its current status is not a
    // reliable source. Use the column it started in.
    const sourceColId = dragStartColRef.current ?? activeCard.status;
    dragStartColRef.current = null;

    // Cards currently in the destination column, in visual order
    const destItems = boardItems
      .filter(i => i.project === currentProject && i.status === overColumnId && i.id !== active.id)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    // Where to insert. Hovering a card drops at that card's position. Dropping on
    // a column itself goes to the end — except for collapsed columns, where the
    // card goes to the top so it's the first thing seen when the column reopens.
    const targetCollapsed = collapsedColumns[overColumnId];
    const insertIdx = overCard
      ? destItems.findIndex(i => i.id === overCard.id)
      : (targetCollapsed ? 0 : destItems.length);
    const finalIdx = insertIdx === -1 ? destItems.length : insertIdx;

    const newDestItems = [...destItems];
    newDestItems.splice(finalIdx, 0, { ...activeCard, status: overColumnId });

    // Optimistic local update
    setBoardItems(prev => prev.map(item => {
      if (item.id === active.id) {
        return { ...item, status: overColumnId, sort_order: finalIdx };
      }
      const idxInDest = newDestItems.findIndex(d => d.id === item.id);
      if (idxInDest !== -1 && item.status === overColumnId) {
        return { ...item, sort_order: idxInDest };
      }
      return item;
    }));

    flashMoved(active.id);

    // Persist destination column ordering
    await persistColumnOrder(newDestItems, overColumnId);

    // If the card left another column, resequence that column too
    if (sourceColId !== overColumnId) {
      const sourceItems = boardItems
        .filter(i => i.project === currentProject && i.status === sourceColId && i.id !== active.id)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      await persistColumnOrder(sourceItems, sourceColId);
    }
  };

  // Flag a card to also show in the Nest, with a priority. The card is never
  // copied — the Nest just displays cards carrying this flag. Passing null
  // removes it from the Nest again.
  const setNestPriority = async (card, priority) => {
    const patch = priority
      ? { in_nest: true, nest_priority: priority }
      : { in_nest: false, nest_priority: null };
    const prevState = { in_nest: card.in_nest, nest_priority: card.nest_priority };

    setBoardItems(prev => prev.map(i => i.id === card.id ? { ...i, ...patch } : i));
    nestChangedRef.current = card.id; // flash this card once its edit is closed
    const { error } = await supabase
      .from("birdie_kanban_cards")
      .update(patch)
      .eq("id", card.id);
    if (error) {
      console.error("set nest priority:", error.message);
      setBoardItems(prev => prev.map(i => i.id === card.id ? { ...i, ...prevState } : i));
    }
  };

  const saveCardText = async (id) => {
    const trimmed = cardTextInput.trim();
    if (!trimmed) return;
    setBoardItems(prev => prev.map(item => item.id === id ? { ...item, text: trimmed } : item));
    const { error } = await supabase.from("birdie_kanban_cards").update({ text: trimmed }).eq("id", id);
    if (error) console.error("Error saving card text:", error.message);
  };

  const deleteItem = async (id) => {
    setBoardItems(prev => prev.filter(item => item.id !== id));
    await supabase.from("birdie_kanban_cards").delete().eq("id", id);
  };

  const getProjectCount = (projectName) => boardItems.filter(item => item.project === projectName).length;
  const toggleColumnCollapse = (colId) => setCollapsedColumns(prev => ({ ...prev, [colId]: !prev[colId] }));

  // Boards in the user's chosen order, falling back to arrival order for any
  // board that order doesn't mention yet (a brand new board, or the very
  // first time this loads with nothing saved).
  const orderedBoards = [...boards].sort((a, b) => {
    const ai = boardOrder.indexOf(a.id), bi = boardOrder.indexOf(b.id);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const handleBoardDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = orderedBoards.map(b => b.id);
    const fromIdx = ids.indexOf(active.id);
    const toIdx = ids.indexOf(over.id);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = arrayMove(ids, fromIdx, toIdx);
    setBoardOrder(next);
    localStorage.setItem("birdie_board_order", JSON.stringify(next));
  };

  // ── VIEW A: Dashboard ─────────────────────────────────────────────────────
  if (!currentProject) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 760, margin: "0 auto", paddingLeft: 20, paddingRight: 20, boxSizing: "border-box" }}>
        <style>{`
          .board-title-text .edit-icon { opacity: 0 !important; transition: opacity 0.15s; }
          .board-title-text:hover .edit-icon { opacity: 0.6 !important; }
          .board-drag-handle { opacity: 0.3; transition: opacity 0.12s; }
          .board-tile:hover .board-drag-handle { opacity: 0.75; }
          .board-drag-handle:active { cursor: grabbing; }
        `}</style>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h3 style={{ color: C.text, margin: "0 0 4px 0", fontSize: 18 }}>Workspaces</h3>
            <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>Create or select a project board to manage pipelines.</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {user?.is_anonymous && boards.length >= 1 ? (
              <span style={{ color: C.muted, fontSize: 12 }}>Sign in to create more boards</span>
            ) : isCreatingBoard ? (
              <>
                <input
                  autoFocus
                  value={newBoardName}
                  onChange={e => setNewBoardName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") { addBoard(); setIsCreatingBoard(false); }
                    if (e.key === "Escape") { setNewBoardName(""); setIsCreatingBoard(false); }
                  }}
                  onBlur={() => { if (!newBoardName.trim()) setIsCreatingBoard(false); }}
                  placeholder="Board name..."
                  style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid " + C.accent, background: C.bg, color: C.text, fontSize: 13, outline: "none" }}
                />
                <button
                  onClick={() => { addBoard(); setIsCreatingBoard(false); }}
                  disabled={!newBoardName.trim()}
                  style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", cursor: newBoardName.trim() ? "pointer" : "default", fontWeight: 600, fontSize: 13, opacity: newBoardName.trim() ? 1 : 0.5 }}
                >Create</button>
              </>
            ) : (
              <button onClick={() => setIsCreatingBoard(true)} disabled={loading} title={loading ? "Loading your boards..." : undefined} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", cursor: loading ? "default" : "pointer", fontWeight: 600, fontSize: 13, opacity: loading ? 0.5 : 1 }}>+ Create Board</button>
            )}
          </div>
        </div>

        {loading ? <div style={{ color: C.muted, fontSize: 13, fontStyle: "italic" }}>Loading workspaces...</div> : (
          <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleBoardDragEnd}>
            <SortableContext items={orderedBoards.map(b => b.id)} strategy={rectSortingStrategy}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
                {orderedBoards.map(proj => (
                  <SortableCard key={proj.id} id={proj.id}>
                    {({ dragHandleProps, isDragging }) => (
                      <div className="board-tile" onClick={() => { if (editingBoardId !== proj.id) { setCurrentProject(proj.name); setCurrentBoardId(proj.id); } }} style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 12, padding: 20, cursor: "pointer", transition: "border-color 0.2s", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 100, position: "relative", opacity: isDragging ? 0.4 : 1 }} onMouseEnter={e => e.currentTarget.style.borderColor = C.accent} onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                        {/* Drag grip — flush against the left edge, vertically centered. Uses a
                            pair of vertical-ellipsis dots (naturally tall) instead of rotating
                            the ⠿ glyph used elsewhere — that one's dot pattern is nearly
                            symmetric, so a CSS rotate on it barely reads as "turned." */}
                        <span {...dragHandleProps} className="board-drag-handle" onClick={e => e.stopPropagation()} title="Drag to reorder" style={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)", color: C.muted, cursor: "grab", fontSize: 14, lineHeight: 1, letterSpacing: -1 }}>⋮⋮</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <span
                              onClick={e => { e.stopPropagation(); setEditingEmojiBoardId(editingEmojiBoardId === proj.id ? null : proj.id); }}
                              title="Change icon"
                              style={{ fontSize: 22, lineHeight: 1, cursor: "pointer" }}
                            >{proj.emoji || DEFAULT_BOARD_EMOJI}</span>
                            {editingEmojiBoardId === proj.id && (
                              <EmojiPickerPopover
                                onPick={emoji => updateBoardEmoji(proj.id, emoji)}
                                onClose={() => setEditingEmojiBoardId(null)}
                              />
                            )}
                          </div>
                          {editingBoardId === proj.id ? (
                            <input type="text" value={boardNameInput} onChange={e => setBoardNameInput(e.target.value)} onClick={e => e.stopPropagation()} onBlur={() => updateDashboardBoardName(proj.id, proj.name)} onKeyDown={e => { if (e.key === "Enter") updateDashboardBoardName(proj.id, proj.name); }} autoFocus style={{ fontSize: 15, fontWeight: 700, color: C.text, background: C.bg, border: "1px solid " + C.accent, borderRadius: 6, padding: "2px 6px", outline: "none", width: "80%" }} />
                          ) : (
                            <span onClick={(e) => { e.stopPropagation(); setBoardNameInput(proj.name); setEditingBoardId(proj.id); }} style={{ color: proj.color ? colColor(proj.color) : C.text, fontWeight: 700, fontSize: 15, cursor: "pointer", minWidth: 0 }}>{proj.name}</span>
                          )}
                        </div>

                        {/* Bottom row — card count on the left, gear + its dropdown on the
                            right. Keeping both off the title/emoji line up top avoids the gear
                            overlapping the title. */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                          <div style={{ color: C.muted, fontSize: 12 }}>{getProjectCount(proj.name)} cards</div>
                          <div style={{ position: "relative" }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setActiveGearMenuBoardId(activeGearMenuBoardId === proj.id ? null : proj.id); }}
                              title="Board actions"
                              style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: 0 }}
                              onMouseEnter={e => e.currentTarget.style.color = C.text}
                              onMouseLeave={e => e.currentTarget.style.color = C.muted}
                            >⚙️</button>
                            {activeGearMenuBoardId === proj.id && (
                              // Opens downward, outside the tile's own bottom edge, so it never
                              // covers the tile it belongs to.
                              <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 100, background: C.card, border: "1px solid " + C.border, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 6px 20px rgba(0,0,0,0.5)", width: 190 }}>
                                <button
                                  onClick={() => { setBoardNameInput(proj.name); setEditingBoardId(proj.id); setActiveGearMenuBoardId(null); }}
                                  style={{ background: "none", border: "none", color: C.text, textAlign: "left", cursor: "pointer", fontSize: 11, fontWeight: 600, padding: "2px 0", display: "flex", alignItems: "center", gap: 6 }}
                                >✏️ Rename Board</button>

                                <div style={{ height: "1px", background: C.border }} />

                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                  <span style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase" }}>Title Color</span>
                                  <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" }}>
                                    {["red", "yellow", "green"].map(key => (
                                      <button key={key} onClick={() => updateBoardColor(proj.id, key)} title={key} style={{ width: 16, height: 16, borderRadius: "50%", background: C[key], border: proj.color === key ? "2px solid " + C.text : "1px solid rgba(255,255,255,0.2)", cursor: "pointer", padding: 0, flexShrink: 0 }} />
                                    ))}
                                    {(proj.color || "").startsWith("#") && (
                                      <span title={proj.color} style={{ width: 16, height: 16, borderRadius: "50%", background: proj.color, border: "2px solid " + C.text, flexShrink: 0, display: "inline-block" }} />
                                    )}
                                    <label title="Custom color" style={{ width: 16, height: 16, borderRadius: "50%", cursor: "pointer", border: "1px solid rgba(255,255,255,0.2)", background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)", display: "inline-block", position: "relative", overflow: "hidden", flexShrink: 0 }}>
                                      <input type="color" value={(proj.color || "").startsWith("#") ? proj.color : "#8b7cf6"} onChange={e => updateBoardColor(proj.id, e.target.value)} style={{ opacity: 0, width: "100%", height: "100%", cursor: "pointer", border: "none", padding: 0 }} />
                                    </label>
                                  </div>
                                </div>

                                <div style={{ height: "1px", background: C.border }} />

                                <button onClick={(e) => { deleteBoard(e, proj.id, proj.name); setActiveGearMenuBoardId(null); }} style={{ background: "none", border: "none", color: C.red, textAlign: "left", cursor: "pointer", fontSize: 11, fontWeight: 600, padding: "2px 0", display: "flex", alignItems: "center", gap: 4 }}>🗑️ Delete Board</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </SortableCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    );
  }

// ── VIEW B: Board View ─────────────────────────────────────────────────────
  const filteredItems = boardItems.filter(item => item.project === currentProject);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%" }}>
      <style>{`
        .board-title-text .edit-icon { opacity: 0 !important; transition: opacity 0.15s; }
        .board-title-text:hover .edit-icon { opacity: 0.6 !important; }
        .col-gear-btn { background: none; border: none; cursor: pointer; color: ${C.muted}; opacity: 0.3; transition: opacity 0.12s, color 0.12s; font-size: 12px; padding: 0; }
        .kanban-col:hover .col-gear-btn { opacity: 0.8; }
        .col-gear-btn:hover { color: ${C.text} !important; opacity: 1 !important; }
        .col-edit-btn { background: none; border: none; cursor: pointer; padding: 1px 3px; font-size: 11px; line-height: 1; opacity: 0; transition: opacity 0.12s; color: ${C.muted}; }
        .kanban-col:hover .col-edit-btn { opacity: 0.5; }
        .kanban-col:hover .col-edit-btn:hover { opacity: 1; }
        
        /* The scrollbar track track margin mirrors the fluid clamp math exactly */
        .kanban-scroll-container::-webkit-scrollbar { height: 0; }
        .kanban-scroll-container { scrollbar-width: none; }
        /* Sticky mirror scrollbar pinned to the bottom of the viewport */
        .kanban-sticky-scroll::-webkit-scrollbar { height: 10px; }
        .kanban-sticky-scroll::-webkit-scrollbar-track { background: transparent; margin-left: clamp(20px, 5vw, 60px); margin-right: clamp(20px, 5vw, 60px); }
        .kanban-sticky-scroll::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 5px; }
        .kanban-sticky-scroll::-webkit-scrollbar-thumb:hover { background: ${C.muted}; }
        /* Whole card is clickable to expand its actions */
        .kanban-card:hover { border-color: ${C.border}; background: ${C.surface}; }
        .kanban-card-menu-open { border-color: ${C.accent} !important; box-shadow: 0 0 0 1px ${C.accent}50; }
        .card-action-icon {
          background: none; border: none; cursor: pointer; color: ${C.muted};
          font-size: 13px; padding: 2px 3px; line-height: 1; transition: color 0.12s;
        }
        .card-action-icon:hover:not(:disabled) { color: ${C.text}; }
        .card-action-icon:disabled { color: ${C.muted}30; cursor: default; }
        .card-action-icon-danger:hover:not(:disabled) { color: ${C.red} !important; }
        /* Drag grip — subtle at rest, clearer on hover */
        .card-drag-handle {
          color: ${C.muted}; opacity: 0.3; cursor: grab;
          font-size: 13px; line-height: 1.2; padding: 0; flex-shrink: 0;
          user-select: none; touch-action: none; transition: opacity 0.12s;
        }
        .kanban-card:hover .card-drag-handle { opacity: 0.75; }
        .card-drag-handle:active { cursor: grabbing; }
        /* Card action icons — hidden until the card is hovered */
        .card-hover-actions { opacity: 0; transition: opacity 0.12s; pointer-events: none; }
        /* Icons sit over the text, so their backdrop matches the hovered card */
        .kanban-card:hover .card-hover-actions {
          opacity: 1; pointer-events: auto;
          background: ${C.surface} !important;
          box-shadow: -8px 0 8px -2px ${C.surface} !important;
        }
        /* Column drag grip */
        .col-drag-handle {
          color: ${C.muted}; opacity: 0.35; cursor: grab; flex-shrink: 0;
          font-size: 12px; line-height: 1; padding: 0 1px;
          user-select: none; touch-action: none; transition: opacity 0.12s;
        }
        .kanban-col:hover .col-drag-handle { opacity: 0.8; }
        .col-drag-handle:active { cursor: grabbing; }
        @keyframes cardMovedFlash {
          0% { background: ${C.accent}30; }
          100% { background: ${C.card}; }
        }
        @keyframes cardSlideIn {
          from { opacity: 0; transform: translateX(-18px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .kanban-card-just-moved { animation: cardMovedFlash 1.1s ease-out, cardSlideIn 0.2s ease-out; }
        @keyframes cardFocusGlow {
          0%   { box-shadow: 0 0 0 2px ${C.orange}; }
          75%  { box-shadow: 0 0 0 2px ${C.orange}; }
          100% { box-shadow: 0 0 0 2px ${C.orange}00; }
        }
        .kanban-card-focus-glow { animation: cardFocusGlow 3s ease-out; }
        @keyframes cardFlashSubtle {
          0%   { box-shadow: 0 0 0 2px ${C.orange}bb; }
          100% { box-shadow: 0 0 0 2px ${C.orange}00; }
        }
        .kanban-card-flash-subtle { animation: cardFlashSubtle 0.9s ease-out; }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid " + C.border, paddingBottom: 12, width: "100%", maxWidth: 680, margin: "0 auto", paddingLeft: 20, paddingRight: 20, boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => { setCurrentProject(null); setCurrentBoardId(null); }} style={{ background: "transparent", border: "1px solid " + C.border, color: C.muted, borderRadius: 7, padding: "5px 11px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }} onMouseEnter={e => e.currentTarget.style.color = C.text} onMouseLeave={e => e.currentTarget.style.color = C.muted}>← Boards</button>
          <div style={{ position: "relative" }}>
            <span
              onClick={() => setEditingEmojiBoardId(editingEmojiBoardId === currentBoardId ? null : currentBoardId)}
              title="Change icon"
              style={{ fontSize: 18, lineHeight: 1, cursor: "pointer" }}
            >{boards.find(b => b.id === currentBoardId)?.emoji || DEFAULT_BOARD_EMOJI}</span>
            {editingEmojiBoardId === currentBoardId && (
              <EmojiPickerPopover
                onPick={emoji => updateBoardEmoji(currentBoardId, emoji)}
                onClose={() => setEditingEmojiBoardId(null)}
              />
            )}
          </div>
          {isEditingName ? (
            <input type="text" value={boardNameInput} onChange={e => setBoardNameInput(e.target.value)} onBlur={updateBoardName} onKeyDown={e => e.key === "Enter" && updateBoardName()} autoFocus style={{ fontSize: 16, fontWeight: 700, background: C.bg, color: C.text, border: "1px solid " + C.accent, borderRadius: 6, padding: "2px 8px", outline: "none" }} />
          ) : (
            <h3 className="board-title-text" onClick={() => { setBoardNameInput(currentProject); setIsEditingName(true); }} style={{ color: C.text, cursor: "pointer", margin: 0, fontSize: 16, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>{currentProject} <span className="edit-icon" style={{ fontSize: 11 }}>✏️</span></h3>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => addColumn("front")} style={{ background: "transparent", border: "1px solid " + C.border, color: C.muted, borderRadius: 7, padding: "5px 11px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, transition: "all 0.15s", whiteSpace: "nowrap" }} onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accentSoft; }} onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}>+ Add Column</button>
        </div>
      </div>

      {/* ── COLUMNS CONTAINER ── */}
      {/* Changed paddingLeft to a clamp function so it fluidly responds only on smaller window sizes */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        // The board re-centres itself as items shift, so droppable positions move
        // mid-drag. Re-measuring continuously keeps collision detection accurate —
        // without this, the rightmost column often can't be targeted.
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragCancel={(event) => {
          setActiveDragId(null);
          setDragOverColId(null);
          const originCol = dragStartColRef.current;
          dragStartColRef.current = null;
          if (originCol && event?.active) {
            setBoardItems(prev => prev.map(item =>
              item.id === event.active.id && item.status !== originCol
                ? { ...item, status: originCol }
                : item
            ));
          }
        }}
      >
      <div ref={boardScrollRef} onScroll={syncFromBoard} className="kanban-scroll-container" style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "safe center", minWidth: "100%", width: "100vw", marginLeft: "calc(-50vw + 50%)", overflowX: "auto", paddingLeft: "clamp(20px, 5vw, 60px)", paddingRight: "clamp(20px, 5vw, 60px)", paddingBottom: 40, boxSizing: "border-box" }}>
        <SortableContext items={columns.map(c => "col-sort-" + c.id)} strategy={horizontalListSortingStrategy}>
        {columns.map((col) => {
          const cc = colColor(col.color);
          const colItems = filteredItems.filter(item => item.status === col.id).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
          const isCollapsed = collapsedColumns[col.id];
          const isEditingThisCol = editingColId === col.id;
          const showGearMenu = activeGearMenuColId === col.id;
          const isDropTarget = dragOverColId === col.id;

          return (
            <SortableColumn key={col.id} id={"col-sort-" + col.id} disabled={isEditingThisCol}>
              {({ dragHandleProps: colDragHandleProps }) => (
            <div 
              className={`kanban-col ${arrangingColumns ? 'arrange-active' : ''}`}
              onClick={isCollapsed ? () => toggleColumnCollapse(col.id) : undefined}
              style={{ 
                background: isDropTarget
                  ? (isCollapsed ? "rgba(139,124,246,0.28)" : "rgba(139,124,246,0.07)")
                  : C.surface,
                cursor: isCollapsed ? "pointer" : "default", 
                border: isDropTarget
                  ? `2px solid ${C.accent}`
                  : (isCollapsed
                      ? `1px solid ${cc}`
                      : "1px solid " + (showGearMenu ? C.accent + "80" : C.border)),
                boxShadow: isDropTarget ? `0 0 0 3px ${C.accent}55` : "none",
                borderRadius: 12, 
                padding: isCollapsed ? "12px 6px" : "12px", 
                flex: isCollapsed ? "0 0 42px" : "0 0 190px", 
                width: isCollapsed ? 42 : 190, 
                transition: "all 0.2s ease-in-out", 
                boxSizing: "border-box", 
                height: isCollapsed ? 240 : "auto",
                minHeight: isCollapsed ? 240 : 150,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: isCollapsed ? 12 : 0,
                overflow: "visible"
              }}
            >
              
              {/* HEADER ROW */}
              <div 
                style={{ 
                  display: "flex", 
                  flexDirection: isCollapsed ? "column" : "row",
                  alignItems: "center", 
                  justifyContent: isCollapsed ? "flex-start" : "space-between", 
                  borderBottom: isCollapsed ? "none" : "1px solid " + C.border + "60", 
                  paddingBottom: isCollapsed ? 0 : 8, 
                  minHeight: 24,
                  gap: isCollapsed ? 10 : 0,
                  width: "100%"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: isCollapsed ? "column" : "row",
                    alignItems: "center",
                    gap: 5,
                    // `transform` (used to rotate the collapsed label below) only affects
                    // paint, not layout — this wrapper's overflow:hidden was clipping the
                    // label's pre-rotation width down to ~30px before rotation ever applied,
                    // which is why so little of the label survived. Not clipping here when
                    // collapsed lets the label lay out at its real width; the column itself
                    // stays narrow since rotation swaps the label's visual footprint.
                    overflow: isCollapsed ? "visible" : "hidden",
                    width: "100%"
                  }}
                >

                  {/* Expand / Collapse Toggle Button */}
                  <button onClick={(e) => { if (isCollapsed) e.stopPropagation(); toggleColumnCollapse(col.id); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 10, padding: 0, flexShrink: 0, transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.15s ease", display: "flex", alignItems: "center", justifyContent: "center" }}>▼</button>

                  {/* Column drag grip */}
                  {!isCollapsed && !isEditingThisCol && (
                    <span
                      {...colDragHandleProps}
                      className="col-drag-handle"
                      onClick={e => e.stopPropagation()}
                      title="Drag to reorder column"
                    >⠿</span>
                  )}

                  {/* Column Label */}
                  {isEditingThisCol ? (
                    <textarea
                      autoFocus
                      rows={1}
                      ref={el => { if (el && !el.dataset.sized) { el.dataset.sized = "1"; autoGrow(el); } }}
                      value={colLabelInput}
                      onChange={e => { setColLabelInput(e.target.value); autoGrow(e.target); }}
                      onBlur={() => saveColLabel(col.id)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveColLabel(col.id); } if (e.key === "Escape") setEditingColId(null); }}
                      onClick={e => e.stopPropagation()}
                      style={{ background: C.bg, color: C.text, border: "1px solid " + C.accent, borderRadius: 4, padding: "2px 4px", fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0, outline: "none", fontFamily: "inherit", lineHeight: 1.35, resize: "none", overflow: "hidden", boxSizing: "border-box" }}
                    />
                  ) : isCollapsed ? (
                    // `transform` only affects paint, not the space a flex sibling reserves —
                    // rotating the label span in place left it reserving just its own ~16px
                    // unrotated line-height in the column's flex layout while painting ~165px
                    // tall, so it overlapped the toggle arrow above it and got clipped by
                    // whatever sat above. This outer box reserves the real rotated footprint
                    // (165 tall, narrow wide) so the arrow/count/handle around it lay out
                    // correctly; the label itself is positioned absolutely (so it doesn't
                    // affect layout at all) and rotated within that reserved box. Laying the
                    // text out normally before rotating — rather than writing-mode: vertical-lr
                    // — also keeps the emoji aligned with the rest of the label: color emoji
                    // glyphs don't reliably rotate under text-orientation the way letters do.
                    <div style={{ position: "relative", width: 20, height: 165, flexShrink: 0 }}>
                      <span
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%) rotate(-90deg)",
                          width: 165,
                          textAlign: "center",
                          color: cc,
                          fontWeight: 700,
                          fontSize: 13,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col.label}
                      </span>
                    </div>
                  ) : (
                    <span
                      onClick={() => { setColLabelInput(col.label); setEditingColId(col.id); }}
                      style={{
                        color: cc,
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col.label}
                    </span>
                  )}
                </div>

                {/* Unified Settings Gear */}
                {!isCollapsed && (
                  <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 5, marginLeft: isEditingThisCol ? 10 : 0 }}>
                    {!isEditingThisCol && (
                      <span style={{ color: C.muted, fontSize: 11, fontWeight: 400 }}>({colItems.length})</span>
                    )}
                    <button className="col-gear-btn" onClick={(e) => { e.stopPropagation(); setRenamingInMenuColId(null); setActiveGearMenuColId(showGearMenu ? null : col.id); }} title="Column actions">⚙️</button>
                    
                    {/* Floating Dropdown Context Popover Menu */}
                    {showGearMenu && (
                      <div onClick={e => e.stopPropagation()} style={{ position: "absolute", top: 38, left: 12, right: 12, zIndex: 100, background: C.card, border: "1px solid " + C.border, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 6px 20px rgba(0,0,0,0.5)" }}>
                        
                        {/* 0. Rename — swaps in place, keeping the rest of the menu visible */}
                        {renamingInMenuColId === col.id ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            <span style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase" }}>Rename</span>
                            <textarea
                              autoFocus
                              rows={1}
                              ref={el => { if (el && !el.dataset.sized) { el.dataset.sized = "1"; autoGrow(el); } }}
                              value={colLabelInput}
                              onChange={e => { setColLabelInput(e.target.value); autoGrow(e.target); }}
                              onKeyDown={e => {
                                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveColLabel(col.id); setRenamingInMenuColId(null); }
                                if (e.key === "Escape") setRenamingInMenuColId(null);
                              }}
                              style={{ background: C.bg, color: C.text, border: "1px solid " + C.accent, borderRadius: 5, padding: "5px 7px", fontSize: 12, fontWeight: 600, width: "100%", outline: "none", fontFamily: "inherit", lineHeight: 1.35, resize: "none", overflow: "hidden", boxSizing: "border-box" }}
                            />
                            <button
                              onMouseDown={e => { e.preventDefault(); saveColLabel(col.id); setRenamingInMenuColId(null); }}
                              style={{ alignSelf: "flex-end", background: C.accent, border: "none", color: "#fff", borderRadius: 5, padding: "3px 10px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                            >Save</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setColLabelInput(col.label); setRenamingInMenuColId(col.id); }}
                            style={{ background: "none", border: "none", color: C.text, textAlign: "left", cursor: "pointer", fontSize: 11, fontWeight: 600, padding: "2px 0", display: "flex", alignItems: "center", gap: 6 }}
                          >✏️ Rename Column</button>
                        )}

                        <div style={{ height: "1px", background: C.border }} />

                        {/* 1. Header Text Color Swatches */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                          <span style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: "uppercase" }}>Text Color</span>
                          <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", alignItems: "center" }}>
                            {["red", "yellow", "green"].map(key => (
                              <button key={key} onClick={() => saveColColor(col.id, key)} title={key} style={{ width: 16, height: 16, borderRadius: "50%", background: C[key], border: col.color === key ? "2px solid " + C.text : "1px solid rgba(255,255,255,0.2)", cursor: "pointer", padding: 0, flexShrink: 0 }} />
                            ))}

                            {/* Currently-chosen custom colour, shown alongside the presets */}
                            {(col.color || "").startsWith("#") && (
                              <span
                                title={col.color}
                                style={{ width: 16, height: 16, borderRadius: "50%", background: col.color, border: "2px solid " + C.text, flexShrink: 0, display: "inline-block" }}
                              />
                            )}

                            {/* Colour wheel — always available */}
                            <label
                              title="Custom color"
                              style={{
                                width: 16, height: 16, borderRadius: "50%", cursor: "pointer",
                                border: "1px solid rgba(255,255,255,0.2)",
                                background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)",
                                display: "inline-block", position: "relative", overflow: "hidden", flexShrink: 0,
                              }}
                            >
                              <input
                                type="color"
                                value={(col.color || "").startsWith("#") ? col.color : "#8b7cf6"}
                                onChange={e => saveColColor(col.id, e.target.value)}
                                style={{ opacity: 0, width: "100%", height: "100%", cursor: "pointer", border: "none", padding: 0 }}
                              />
                            </label>
                          </div>
                        </div>

                        {/* 2. Text Action-Link for Deleting Column */}
                        {columns.length > 1 && (
                          <>
                            <div style={{ height: "1px", background: C.border }} />
                            <button onClick={() => { deleteColumn(col.id); setActiveGearMenuColId(null); }} style={{ background: "none", border: "none", color: C.red, textAlign: "left", cursor: "pointer", fontSize: 11, fontWeight: 600, padding: "2px 0", display: "flex", alignItems: "center", gap: 4 }}>🗑️ Delete Column</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isCollapsed && (
                  <span style={{ color: C.muted, fontWeight: 400, fontSize: 11 }}>({colItems.length})</span>
                )}
              </div>

              {/* COLUMN BODY */}
              {!isCollapsed && (
                <>
                  <div style={{ display: "flex", gap: 6, marginBottom: 4, marginTop: 10 }}>
                    <textarea
                      rows={1}
                      value={columnInputs[col.id] || ""}
                      onChange={e => { setColumnInputs(prev => ({ ...prev, [col.id]: e.target.value })); autoGrow(e.target); }}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          addItem(col.id);
                          // reset height after the text clears
                          const el = e.target;
                          requestAnimationFrame(() => { el.style.height = "auto"; });
                        }
                      }}
                      placeholder="+ Add card..."
                      style={{ flex: 1, minWidth: 0, padding: "6px 10px", borderRadius: 6, border: "1px solid " + C.border, background: C.bg, color: C.text, fontSize: 13, lineHeight: 1.4, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "none", overflow: "hidden", maxHeight: 160 }}
                    />
                  </div>

                  <ColumnDropZone colId={col.id}>
                  <SortableContext items={colItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8, minHeight: 20 }}>
                    {colItems.map((item) => {
                      const isEditingThisCard = editingCardId === item.id;
                      return (
                      <SortableCard key={item.id} id={item.id} disabled={isEditingThisCard}>
                        {({ dragHandleProps }) => (
                      <div
                        ref={el => { if (el) cardRefs.current[item.id] = el; else delete cardRefs.current[item.id]; }}
                        className={"kanban-card" + (justMovedCardId === item.id ? " kanban-card-just-moved" : "") + (focusedCardId === item.id ? " kanban-card-focus-glow" : "") + (subtleFlashId === item.id ? " kanban-card-flash-subtle" : "")}
                        style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 8, padding: 10, paddingLeft: isEditingThisCard ? 10 : 4, display: "flex", flexDirection: "column", gap: 0, position: "relative" }}
                      >
                        {isEditingThisCard ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <textarea
                              autoFocus
                              ref={el => { if (el && !el.dataset.sized) { el.dataset.sized = "1"; autoGrow(el); } }}
                              value={cardTextInput}
                              onChange={e => { setCardTextInput(e.target.value); autoGrow(e.target); }}
                              onBlur={(e) => {
                                // Clicking the priority dropdown blurs the textarea. Without
                                // this check, edit mode would close and unmount the dropdown
                                // before the click could register.
                                const card = e.currentTarget.closest(".kanban-card");
                                if (card && e.relatedTarget && card.contains(e.relatedTarget)) {
                                  saveCardText(item.id);
                                  return;
                                }
                                saveCardText(item.id);
                                setEditingCardId(null);
                              }}
                              onClick={e => e.stopPropagation()}
                              onKeyDown={e => {
                                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveCardText(item.id); setEditingCardId(null); }
                                if (e.key === "Escape") setEditingCardId(null);
                              }}
                              style={{ width: "100%", color: C.text, fontSize: 13, lineHeight: 1.4, fontFamily: "inherit", background: C.bg, border: "1px solid " + C.accent, borderRadius: 6, padding: 6, outline: "none", resize: "none", overflow: "hidden", minHeight: 40, boxSizing: "border-box" }}
                            />
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {/* Send to Nest with a priority, same as Brain Dump */}
                              <select
                                value={item.in_nest ? (item.nest_priority || "next") : ""}
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                                onChange={e => {
                                  e.stopPropagation();
                                  setNestPriority(item, e.target.value || null);
                                }}
                                style={{
                                  flex: 1, minWidth: 0,
                                  background: C.bg,
                                  border: `1px solid ${item.in_nest ? C.accent + "80" : C.border}`,
                                  color: item.in_nest ? C.accentSoft : C.muted,
                                  borderRadius: 6, padding: "3px 4px",
                                  fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                                }}
                              >
                                <option value="">+ Task</option>
                                {Object.entries(PRIORITIES).map(([key, p]) => (
                                  <option key={key} value={key}>{p.label}</option>
                                ))}
                              </select>
                              <button
                                onMouseDown={e => { e.preventDefault(); saveCardText(item.id); setEditingCardId(null); }}
                                style={{ flexShrink: 0, background: C.accent, border: "none", color: "#fff", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                              >Done</button>
                            </div>
                          </div>
                        ) : (
                          <>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
                            {/* Drag grip — the only draggable part, so clicks elsewhere still work */}
                            <span
                              {...dragHandleProps}
                              className="card-drag-handle"
                              onClick={e => e.stopPropagation()}
                              title="Drag to reorder"
                              style={{ lineHeight: "18px", minHeight: 18, display: "flex", alignItems: "center", marginLeft: -3 }}
                            >⠿</span>
                            <span
                              onClick={(e) => { e.stopPropagation(); setCardTextInput(item.text); setEditingCardId(item.id); }}
                              style={{ color: C.text, fontSize: 13, lineHeight: "18px", wordBreak: "break-word", flex: 1, cursor: "text", minHeight: 18 }}
                            >{item.text}</span>
                            {item.in_nest && (
                              <span
                                title={`In your Nest — ${(PRIORITIES[item.nest_priority] || PRIORITIES.next).label}`}
                                style={{
                                  position: "absolute", top: 6, right: 7,
                                  fontSize: 9, lineHeight: 1,
                                  color: (PRIORITIES[item.nest_priority] || PRIORITIES.next).color,
                                }}
                              >●</span>
                            )}
                          </div>

                          {/* Pencil + delete float in the corner, revealed on card hover */}
                          {confirmDeleteId !== item.id && (
                            <div className="card-hover-actions" style={{ position: "absolute", bottom: 6, right: 6, display: "flex", alignItems: "center", gap: 6, background: C.card, paddingLeft: 10, paddingRight: 2, borderRadius: 4, boxShadow: `-8px 0 8px -2px ${C.card}` }}>
                              <button
                                className="card-action-icon"
                                onClick={(e) => { e.stopPropagation(); setCardTextInput(item.text); setEditingCardId(item.id); }}
                                title="Edit text"
                                style={{ flexShrink: 0, fontSize: 12, lineHeight: 1, opacity: 0.6, padding: 0, display: "flex", alignItems: "center" }}
                              >✏️</button>
                              <button
                                className="card-action-icon card-action-icon-danger"
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(item.id); }}
                                title="Delete card"
                                style={{ flexShrink: 0, fontSize: 15, lineHeight: 1, color: C.red, opacity: 0.7, padding: 0, display: "flex", alignItems: "center" }}
                              >×</button>
                            </div>
                          )}

                          {/* Delete confirmation — own row below the text so the text keeps full width */}
                          {confirmDeleteId === item.id && (
                            <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginTop: 8, paddingTop: 8, borderTop: "1px solid " + C.border }}>
                              <span style={{ color: C.red, fontSize: 11, fontWeight: 600 }}>Delete?</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteItem(item.id); setConfirmDeleteId(null); }}
                                style={{ background: C.red, border: "none", color: "#fff", borderRadius: 4, padding: "3px 9px", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.3, flexShrink: 0 }}
                              >Yes</button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                style={{ background: "none", border: "1px solid " + C.border, color: C.muted, borderRadius: 4, padding: "3px 9px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", lineHeight: 1.3, flexShrink: 0 }}
                              >No</button>
                            </div>
                          )}
                          </>
                        )}
                      </div>
                        )}
                      </SortableCard>
                      );
                    })}
                    {colItems.length === 0 && (
                      <div style={{ color: C.muted, fontSize: 11, fontStyle: "italic", textAlign: "center", padding: "16px 0" }}>Empty column</div>
                    )}
                  </div>
                  </SortableContext>
                  </ColumnDropZone>
                </>
              )}

              {/* Collapsed columns get their grip at the bottom, well clear of the collapse arrow */}
              {isCollapsed && (
                <>
                  <CollapsedColumnDrop colId={col.id} />
                  {/* Negative bottom margin eats into the column's 12px padding so the grip
                      hugs the bottom edge; rotated 90° so it reads horizontally across the bar */}
                  <div style={{ marginTop: "auto", marginBottom: -8, display: "flex", justifyContent: "center" }}>
                    <span
                      {...colDragHandleProps}
                      className="col-drag-handle"
                      onClick={e => e.stopPropagation()}
                      title="Drag to reorder column"
                      style={{ display: "inline-block", transform: "rotate(90deg)" }}
                    >⠿</span>
                  </div>
                </>
              )}
            </div>
              )}
            </SortableColumn>
          );
        })}
        </SortableContext>

        {/* ── GHOST COLUMN — adds to the right ── */}
        {isAddingColumn ? (
          <div style={{ background: C.surface, border: `1px solid ${C.accentSoft}`, borderRadius: 12, padding: 12, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 12, flex: "0 0 190px", width: 190, flexShrink: 0 }}>
            <input
              type="text"
              placeholder="Column name..."
              value={newColumnTitle}
              onChange={e => setNewColumnTitle(e.target.value)}
              onBlur={() => { if (!newColumnTitle.trim()) { setIsAddingColumn(false); setNewColumnTitle(""); } }}
              onKeyDown={e => {
                if (e.key === "Enter" && newColumnTitle.trim()) {
                  const usedColors = new Set(columns.map(c => c.color));
                  const availableColor = KANBAN_COLOR_OPTIONS.find(opt => !usedColors.has(opt.key))?.key || "accentSoft";
                  persistColumns([...columns, { id: "col_" + Date.now(), label: newColumnTitle.trim(), color: availableColor }]);
                  setNewColumnTitle("");
                  setIsAddingColumn(false);
                }
                if (e.key === "Escape") { setNewColumnTitle(""); setIsAddingColumn(false); }
              }}
              autoFocus
              style={{ fontSize: 13, fontWeight: 700, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", outline: "none", width: "100%", boxSizing: "border-box" }}
            />
            <div style={{ color: C.muted, fontSize: 11, textAlign: "center", padding: "16px 0", border: `1px dashed ${C.border}`, borderRadius: 6, fontStyle: "italic" }}>
              Press Enter to save
            </div>
          </div>
        ) : (
          <div
            style={{ minWidth: 120, padding: "8px 14px", borderRadius: 10, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, cursor: "pointer", fontSize: 12, fontWeight: 600, height: 34, boxSizing: "border-box", flexShrink: 0, transition: "border-color 0.15s, color 0.15s" }}
            onClick={() => setIsAddingColumn(true)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accentSoft; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}
          >
            ＋ Add Column
          </div>
        )}
      </div>

      {/* Floating preview of whatever is being dragged */}
      <DragOverlay dropAnimation={{ duration: 180, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
        {activeDragId ? (() => {
          // Column preview
          if (isColumnDragId(activeDragId)) {
            const draggedCol = columns.find(c => "col-sort-" + c.id === activeDragId);
            if (!draggedCol) return null;
            const count = filteredItems.filter(i => i.status === draggedCol.id).length;
            const draggedCollapsed = collapsedColumns[draggedCol.id];

            // Collapsed columns keep their narrow, vertical-text appearance while dragging
            if (draggedCollapsed) {
              return (
                <div style={{ background: C.surface, border: "1px solid " + C.accent, borderRadius: 12, padding: "12px 6px", width: 42, height: 200, boxSizing: "border-box", boxShadow: "0 14px 36px rgba(0,0,0,0.65)", transform: "rotate(1.5deg)", cursor: "grabbing", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <span style={{ color: C.muted, fontSize: 10, transform: "rotate(-90deg)", flexShrink: 0 }}>▼</span>
                  <span style={{ color: colColor(draggedCol.color), fontWeight: 700, fontSize: 13, writingMode: "vertical-lr", transform: "rotate(180deg)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{draggedCol.label}</span>
                  <span style={{ color: C.muted, fontSize: 11, marginTop: "auto" }}>({count})</span>
                </div>
              );
            }

            return (
              <div style={{ background: C.surface, border: "1px solid " + C.accent, borderRadius: 12, padding: 12, width: 190, boxSizing: "border-box", boxShadow: "0 14px 36px rgba(0,0,0,0.65)", transform: "rotate(1.5deg)", cursor: "grabbing" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                  <span style={{ color: colColor(draggedCol.color), fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{draggedCol.label}</span>
                  <span style={{ color: C.muted, fontSize: 11 }}>({count})</span>
                </div>
              </div>
            );
          }
          // Card preview
          const dragged = boardItems.find(i => i.id === activeDragId);
          if (!dragged) return null;
          return (
            <div style={{ background: C.card, border: "1px solid " + C.accent, borderRadius: 8, padding: 10, width: 190, boxSizing: "border-box", boxShadow: "0 10px 30px rgba(0,0,0,0.6)", transform: "rotate(1.5deg)", cursor: "grabbing" }}>
              <span style={{ color: C.text, fontSize: 13, lineHeight: 1.4, wordBreak: "break-word" }}>{dragged.text}</span>
            </div>
          );
        })() : null}
      </DragOverlay>
      </DndContext>

      {/* ── STICKY HORIZONTAL SCROLLBAR — pinned to viewport bottom, mirrors the board ── */}
      {scrollMeta.overflowing && (
        <div
          ref={stickyScrollRef}
          onScroll={syncFromSticky}
          className="kanban-sticky-scroll"
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, height: 16,
            overflowX: "auto", overflowY: "hidden", zIndex: 60,
            background: C.bg, borderTop: "1px solid " + C.border,
          }}
        >
          <div style={{ width: scrollMeta.width, height: 1 }} />
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [user, setUser]           = useState(null);
  const [tab, setTab]             = useState("today");
  const [focusCard, setFocusCard] = useState(null); // {id, project} to open on the Boards tab
  const openBoardCard = (card) => { setFocusCard({ id: card.id, project: card.project }); setTab("kanban"); };
  const [tasks, setTasks]         = useState(makeSampleTasks);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loginStreak, setLoginStreak] = useState(0);
  const [appBoards, setAppBoards] = useState([]);
  const [showAuthPanel, setShowAuthPanel] = useState(false);
  // Set when a signInWithOAuth redirect comes back with an error — see the
  // effect below. Passed into AuthScreen so it opens showing what happened
  // instead of landing back on a silent guest view.
  const [oauthError, setOauthError] = useState(null);
  const [mergeToast, setMergeToast] = useState(""); // holds the message text, "" when hidden
  // Bumped after a guest→account merge so every per-user load-effect refetches
  // in place — no page reload, so the "sign in to save" popup never has to
  // navigate away from the app.
  const [dataEpoch, setDataEpoch] = useState(0);
  const [timerState, setTimerState] = useState(() => {
    try {
      const saved = localStorage.getItem("birdie_timer");
      if (saved) {
        const parsed = JSON.parse(saved);
        // If timer was running, figure out how much time has passed
        if (parsed.running && !parsed.paused && parsed.savedAt) {
          const elapsed = Math.floor((Date.now() - parsed.savedAt) / 1000);
          const remaining = Math.max(0, parsed.remaining - elapsed);
          return { ...parsed, remaining, confetti: [] };
        }
        // Reset sessions if it's a new day
        const savedDate = parsed.sessionDate || (parsed.savedAt
          ? new Date(parsed.savedAt).toISOString().slice(0, 10)
          : null);
        const sessions = savedDate === todayStr() ? parsed.sessions : 0;
        return { ...parsed, sessions, sessionDate: todayStr(), confetti: [] };
      }
    } catch (e) {}
    return {
      selected: 1, customMins: 20, task: "",
      running: false, paused: false, remaining: null,
      sessions: 0, sessionDate: todayStr(), muted: false, confetti: [],
    };
  });
  const timerInterval = useRef(null);

  // ── Timer tick — lives in App so it survives tab switches ────────────────
  useEffect(() => {
    const { running, paused } = timerState;
    if (running && !paused) {
      timerInterval.current = setInterval(() => {
        setTimerState(prev => {
          if (prev.remaining <= 1) {
            clearInterval(timerInterval.current);
            // Chime
            if (!prev.muted) {
              try {
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                [523.25, 659.25, 783.99, 1046.50].forEach((freq, i) => {
                  const osc = ctx.createOscillator(), g = ctx.createGain();
                  osc.connect(g); g.connect(ctx.destination);
                  osc.type = "sine";
                  osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.18);
                  g.gain.setValueAtTime(0.08, ctx.currentTime + i * 0.18);
                  g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.6);
                  osc.start(ctx.currentTime + i * 0.18);
                  osc.stop(ctx.currentTime + i * 0.18 + 0.6);
                });
              } catch (e) {}
            }
            // Confetti
            const pieces = Array.from({ length: 60 }, (_, i) => ({
              id: i, vx: (Math.random() - 0.5) * 140, vy: (Math.random() - 0.9) * 120,
              color: ["#7c6af7","#4ade80","#fb923c","#f87171","#fbbf24","#a99bf9","#fff"][Math.floor(Math.random() * 7)],
              size: Math.random() * 8 + 4, rotation: Math.random() * 360,
              shape: Math.random() > 0.5 ? "circle" : "rect",
            }));
            setTimeout(() => setTimerState(p => ({ ...p, confetti: [] })), 2800);
            return { ...prev, running: false, remaining: 0, sessions: prev.sessions + 1, sessionDate: todayStr(), confetti: pieces };
          }
          return { ...prev, remaining: prev.remaining - 1 };
        });
      }, 1000);
    } else {
      clearInterval(timerInterval.current);
    }
    return () => clearInterval(timerInterval.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerState.running, timerState.paused, timerState.muted]);

  // ── Stream overlay sync ──────────────────────────────────────────────────
  // Pushes an end-timestamp to Supabase so the OBS overlay can count down
  // locally. Only fires on meaningful state changes, not every tick.
  const [overlayKey, setOverlayKey] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      // Upsert-ignore-conflicts + a follow-up read, instead of select-then-insert:
      // this effect can legitimately double-fire right after sign-in (our own
      // setUser call and the auth listener's can each trigger it), and a plain
      // check-then-insert isn't atomic — two near-simultaneous inserts for the
      // same user_id could otherwise race. This never overwrites an existing
      // share_key (that URL is pasted into OBS and must stay stable).
      const key = (crypto.randomUUID?.() || String(Math.random()).slice(2)).replace(/-/g, "").slice(0, 16);
      const { error: upsertErr } = await supabase
        .from("birdie_timer_share")
        .upsert(
          { user_id: user.id, share_key: key, running: false, paused: false, remaining: 0, task: "" },
          { onConflict: "user_id", ignoreDuplicates: true }
        );
      if (upsertErr) { console.error("mint overlay key:", upsertErr.message); return; }
      const { data, error } = await supabase
        .from("birdie_timer_share")
        .select("share_key")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) { console.error("load overlay key:", error.message); return; }
      if (data?.share_key) setOverlayKey(data.share_key);
    })();
  }, [user]);

  useEffect(() => {
    if (!user || !overlayKey) return;
    const { running, paused, remaining, task } = timerState;
    const endsAt = running && !paused && remaining != null
      ? new Date(Date.now() + remaining * 1000).toISOString()
      : null;
    supabase.from("birdie_timer_share")
      .update({ running, paused, remaining, task: task || "", ends_at: endsAt, updated_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .then(({ error }) => { if (error) console.error("overlay sync:", error.message); });
  // Deliberately excludes `remaining` so this doesn't fire every second —
  // the overlay derives the countdown from ends_at instead.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, overlayKey, timerState.running, timerState.paused, timerState.task]);

  // ── Midnight reset — checks every minute if the date has rolled over ────
  useEffect(() => {
    const checkMidnight = () => {
      setTimerState(prev => {
        if (prev.sessionDate && prev.sessionDate !== todayStr()) {
          return { ...prev, sessions: 0, sessionDate: todayStr() };
        }
        return prev;
      });
    };
    const id = setInterval(checkMidnight, 60 * 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Catch OAuth errors returned via redirect ─────────────────────────────
  // signInWithOAuth() sends the whole page away to Twitch/Google and back —
  // if something goes wrong there (denied consent, a provider-side error),
  // Supabase reports it via error params on this return URL, not through
  // signInWithOAuth()'s own promise (which only ever reflects whether the
  // redirect *started*). Without this, such a failure was silent: the user
  // would land back on the app still a guest, with no explanation.
  useEffect(() => {
    const raw = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.search.slice(1);
    const err = new URLSearchParams(raw).get("error_description") || new URLSearchParams(raw).get("error");
    if (!err) return;
    setOauthError(decodeURIComponent(err.replace(/\+/g, " ")));
    setShowAuthPanel(true);
    // Drop the error params so refreshing doesn't keep re-showing this.
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  // ── Restore session ──────────────────────────────────────────────────────
  // No session at all (first-ever visit, or an expired one) → start a guest
  // session instead of blocking on the login screen. Anonymous users get a
  // real auth.uid(), so every per-user query/table below works unchanged.
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      let session = data.session;
      if (!session) {
        const { data: anonData, error } = await supabase.auth.signInAnonymously();
        if (error) console.error("anonymous sign-in failed:", error.message);
        session = anonData?.session ?? null;
      }
      setUser(session?.user ?? null);
      setLoadingAuth(false);
    });
    // Explicit sign-out drops `session` to null — rather than falling through
    // to the full-page login screen, quietly start a new guest session so
    // "Sign out" lands back on the regular app, not a login wall.
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT" && !session) {
        setLoadingAuth(true);
        supabase.auth.signInAnonymously().then(({ data: anonData, error }) => {
          if (error) console.error("anonymous sign-in failed:", error.message);
          setUser(anonData?.session?.user ?? null);
          setLoadingAuth(false);
        });
        return;
      }
      setUser(session?.user ?? null);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // A "log in to a different, already-existing account" merge leaves a ticket
  // token in sessionStorage (see AuthScreen). Once that account's session is
  // actually live, redeem it — reassigning the guest's board/tasks/etc. to
  // this uid — then bump dataEpoch so every load-effect below refetches in
  // place. No page reload, so the "sign in to save" popup never has to
  // navigate away from the app.
  useEffect(() => {
    if (!user || user.is_anonymous) return;
    const token = sessionStorage.getItem("birdie_merge_token");
    if (!token) return;
    sessionStorage.removeItem("birdie_merge_token");
    supabase.rpc("redeem_merge_ticket", { p_token: token }).then(async ({ data, error }) => {
      if (error) { console.error("merge guest data:", error.message); return; }
      const boards = data?.boards ?? 0;
      const taskIds = data?.task_ids ?? [];

      if (taskIds.length > 0) {
        // Move the merged tasks to the top of their priority group, preserving
        // their relative order from the guest session — same minOrder-1
        // pattern the "add task" input already uses, just applied to a batch.
        // Done here in JS (not in the SQL function) so it's something that
        // can actually be tested end-to-end rather than reasoned about blind.
        const [{ data: mergedTasks }, { data: allTasks }] = await Promise.all([
          supabase.from("tasks").select("id, priority, sort_order").in("id", taskIds),
          supabase.from("tasks").select("id, priority, sort_order").eq("user_id", user.id),
        ]);
        if (mergedTasks && allTasks) {
          const minByPriority = {};
          allTasks.forEach(t => {
            const cur = minByPriority[t.priority];
            minByPriority[t.priority] = cur === undefined ? (t.sort_order ?? 0) : Math.min(cur, t.sort_order ?? 0);
          });
          const grouped = {};
          mergedTasks.forEach(t => { (grouped[t.priority] = grouped[t.priority] || []).push(t); });
          const updates = [];
          Object.entries(grouped).forEach(([priority, group]) => {
            // Guest's own topmost task (smallest sort_order) first, so it
            // ends up closest to the very top after merging.
            group.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
            let next = (minByPriority[priority] ?? 0) - group.length;
            group.forEach(t => { updates.push({ id: t.id, sort_order: next }); next += 1; });
          });
          await Promise.all(updates.map(u =>
            supabase.from("tasks").update({ sort_order: u.sort_order }).eq("id", u.id)
          ));
        }
      }

      setDataEpoch(e => e + 1);
      const parts = [];
      if (boards > 0) parts.push(`${boards} board${boards === 1 ? "" : "s"}`);
      if (taskIds.length > 0) parts.push(`${taskIds.length} task${taskIds.length === 1 ? "" : "s"}`);
      // Only claim what actually happened — a guest who never created a
      // board (or never added a task) shouldn't see either called out.
      if (parts.length === 0) return;
      setMergeToast(`✅ ${parts.join(" and ")} added to your account`);
      setTimeout(() => setMergeToast(""), 6000);
    });
  }, [user]);

  // ── Load boards for "send to board" features (Brain Dump + task list) ──
  // Fires only once `user` is confirmed, so RLS on birdie_kanban_boards works
  // correctly. Selects * so a missing column can never silently kill the query,
  // logs any error, and refreshes when the window regains focus so boards
  // created (or renamed/re-iconed) on the Boards tab show up here without a
  // reload. Window focus alone misses switching tabs within the app itself —
  // no real browser blur/refocus happens then — so this also re-fires on
  // `tab`, keeping the "today" and "dump" tabs (the two that show this list)
  // current right after visiting Boards.
  useEffect(() => {
    if (!user) return;
    const loadBoards = () => {
      supabase.from("birdie_kanban_boards")
        .select("*")
        .order("created_at", { ascending: true })
        .then(({ data, error }) => {
          if (error) { console.error("load boards:", error.message); return; }
          setAppBoards(data || []);
        });
    };
    loadBoards();
    window.addEventListener("focus", loadBoards);
    return () => window.removeEventListener("focus", loadBoards);
  }, [user, dataEpoch, tab]);

  // ── Load tasks from Supabase + reset stale habits in one pass ───────────
  useEffect(() => {
    if (!user) return;
    supabase.from("tasks").select("*").eq("user_id", user.id)
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        if (!data) return;
        if (data.length === 0) {
          // A genuinely empty account (new user, new guest, or a guest
          // identity picked up right after signing out of a real one) — show
          // a fresh set of local sample tasks as a friendly empty state. The
          // sync effect below never writes them to Supabase, so they stay
          // purely local. Explicitly resetting here (rather than leaving
          // `tasks` untouched) matters because this identity may not be the
          // same one `tasks` was last loaded for — e.g. right after signing
          // out, without this the previous account's or guest session's
          // tasks would just keep sitting in state, looking like they'd
          // carried over into the new guest session.
          setTasks(makeSampleTasks());
          initialLoad.current = false;
          return;
        }
        const today = todayStr();
        const reset = data.map(t => {
          if (t.is_habit && t.done && t.last_completed_date && t.last_completed_date < today) {
            return { ...t, done: false, last_completed_date: null };
          }
          return t;
        });
        setTasks(reset);
        initialLoad.current = false;
      });
  }, [user, dataEpoch]);

  // ── Track login streak ────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const key = `birdie_streak_${user.id}`;
    const raw = localStorage.getItem(key);
    const today = todayStr();
    const yesterday = yesterdayStr();

    if (!raw) {
      // First ever login
      const data = { streak: 1, lastDate: today };
      localStorage.setItem(key, JSON.stringify(data));
      setLoginStreak(1);
    } else {
      const { streak, lastDate } = JSON.parse(raw);
      if (lastDate === today) {
        // Already logged in today — just restore streak
        setLoginStreak(streak);
      } else if (lastDate === yesterday) {
        // Consecutive day — increment
        const next = streak + 1;
        localStorage.setItem(key, JSON.stringify({ streak: next, lastDate: today }));
        setLoginStreak(next);
      } else {
        // Streak broken
        localStorage.setItem(key, JSON.stringify({ streak: 1, lastDate: today }));
        setLoginStreak(1);
      }
    }
  }, [user]);

  // ── Save timer state to localStorage ─────────────────────────────────────
  useEffect(() => {
    const { confetti, ...toSave } = timerState;
    localStorage.setItem("birdie_timer", JSON.stringify({
      ...toSave,
      savedAt: Date.now(),
    }));
  }, [timerState]);

  // ── Sync tasks to Supabase (debounced) ────────────────────────────────────
  const syncTimeout = useRef(null);
  const initialLoad = useRef(true);
  useEffect(() => {
    if (!user || initialLoad.current) return;
    clearTimeout(syncTimeout.current);
    syncTimeout.current = setTimeout(async () => {
      const toSync = tasks.filter(t => !t.sample);
      if (toSync.length === 0) return;
      await supabase.from("tasks").upsert(
        toSync.map(t => ({ ...t, user_id: user.id })),
        { onConflict: "id" }
      );
    }, 1000);
  }, [tasks, user]);

  const addTask = (text, priority) => {
    setTasks(prev => [...prev, {
      id: Date.now().toString(), text, priority, done: false,
      project: "General", is_habit: false, last_completed_date: null,
      created_at: new Date().toISOString(),
    }]);
  };

  const signOut = async () => { await supabase.auth.signOut(); setUser(null); };

  // Overlay must be reachable without auth, so it comes before the login gate
  if (window.location.pathname === "/timer-overlay") return <TimerOverlay />;
  if (window.location.pathname === "/community-overlay") return <CommunityOverlay />;

  if (loadingAuth) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ color: C.muted, fontFamily: "sans-serif" }}>Loading...</span>
    </div>
  );

  if (window.location.pathname === "/reset-password") return <ResetPasswordScreen />;
  // Only reachable if anonymous sign-in itself failed (e.g. disabled in the
  // Supabase project) — everyone else already has a session, guest or real.
  if (!user) return <AuthScreen fullPage />;

  const current      = TABS.find(t => t.id === tab);
  const doneToday    = tasks.filter(t => t.done && t.last_completed_date === todayStr()).length;

  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      fontFamily: "'DM Sans','Segoe UI',sans-serif", color: C.text,
      display: "flex", flexDirection: "column",
    }}>
      {/* Sticky top: header + tabs stay pinned together so the tabs never scroll away */}
      <div style={{ position: "sticky", top: 0, zIndex: 10 }}>

        {/* Header */}
        <div style={{
          background: C.surface, borderBottom: `1px solid ${C.border}`,
          padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 11, color: C.muted, letterSpacing: 2, textTransform: "uppercase" }}>
              🐦 Birdie Brains
            </div>
            {/* A fixed line height, so every tab's title sits the same distance below "Birdie Brains"
                (an emoji in the title, like Mission Control's 🚀, used to push it lower than the rest) */}
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 3, lineHeight: "26px" }}>{current.title}</div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {/* Persistent timer pill — visible when running, click to jump to Focus tab */}
            {timerState.running && (
              <button onClick={() => tab !== "focus" && setTab("focus")} style={{
                background: C.accent + "20",
                border: `1px solid ${C.accent + "60"}`,
                borderRadius: 10, padding: "5px 12px",
                cursor: tab !== "focus" ? "pointer" : "default",
                display: "flex", alignItems: "center", gap: 7, fontFamily: "inherit",
              }}>
                <span style={{ fontSize: 13 }}>⏰</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                    {String(Math.floor((timerState.remaining ?? 0) / 60)).padStart(2,"0")}:{String((timerState.remaining ?? 0) % 60).padStart(2,"0")}
                  </div>
                  {timerState.task && <div style={{ fontSize: 10, color: C.muted, lineHeight: 1, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{timerState.task}</div>}
                </div>
              </button>
            )}

            {/* Streak pills */}
            <StreakPill emoji="🔥" value={loginStreak} label="day streak" color={C.orange} />
            <StreakPill emoji="✅" value={doneToday}   label="done today" color={C.green}  />

            <span style={{ color: C.muted, fontSize: 12 }}>
              {tasks.filter(t => t.done).length}/{tasks.length} total
            </span>
            {user?.is_anonymous ? (
              <button onClick={() => setShowAuthPanel(true)} style={{ ...btnStyle(C.accent), padding: "6px 12px", fontSize: 12 }}>
                Sign in to save
              </button>
            ) : (
              <button onClick={signOut} style={{ ...btnStyle(C.muted), padding: "6px 12px", fontSize: 12 }}>
                Sign out
              </button>
            )}
          </div>

          {showAuthPanel && (
            <div
              onClick={() => setShowAuthPanel(false)}
              style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.97)", zIndex: 1000,
                display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
              }}
            >
              <div onClick={e => e.stopPropagation()}>
                <AuthScreen
                  onClose={() => { setShowAuthPanel(false); setOauthError(null); }}
                  initialError={oauthError}
                />
              </div>
            </div>
          )}

          {mergeToast && (
            <div style={{
              position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)",
              background: C.green, color: "#04210f", padding: "10px 18px", borderRadius: 10,
              fontWeight: 700, fontSize: 13, zIndex: 1100,
            }}>
              {mergeToast}
            </div>
          )}

          {/* Global confetti — fires regardless of active tab */}
          {timerState.confetti.length > 0 && (
            <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 999, overflow: "hidden" }}>
              {timerState.confetti.map(p => (
                <div key={p.id} style={{
                  position: "absolute",
                  left: `calc(50% + ${p.vx * 1.2}px)`, top: `calc(40% + ${p.vy * 1.2}px)`,
                  width: p.shape === "circle" ? p.size : p.size * 1.5, height: p.size,
                  borderRadius: p.shape === "circle" ? "50%" : 2, background: p.color,
                  transform: `rotate(${p.rotation}deg)`,
                  animation: "confettiFall 2.6s ease-out forwards",
                }} />
              ))}
              <style>{`@keyframes confettiFall { 0% { transform:translate(0,0) rotate(0deg); opacity:1; } 100% { transform:translate(0,200px) rotate(720deg); opacity:0; } }`}</style>
            </div>
          )}
        </div>

        {/* Tabs */}
        <style>{`
          .tab-scroll::-webkit-scrollbar { display: none; }
          .tab-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
        <div className="tab-scroll" style={{
          display: "flex", background: C.surface, borderBottom: `1px solid ${C.border}`,
          padding: "0 16px", overflowX: "auto", overflowY: "hidden",
        }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "transparent", border: "none", padding: "9px 14px",
              color: tab === t.id ? C.accentSoft : C.muted,
              borderBottom: `2px solid ${tab === t.id ? C.accent : "transparent"}`,
              cursor: "pointer", fontSize: 13, fontWeight: tab === t.id ? 700 : 400, whiteSpace: "nowrap",
            }}>{t.label}</button>
          ))}
        </div>

      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: tab === "kanban" ? "24px 0" : tab === "canvas" ? "0" : "24px 20px", maxWidth: (tab === "kanban" || tab === "canvas") ? "100%" : 680, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        {tab === "today"     && <DailyPlanner tasks={tasks} setTasks={setTasks} user={user} boards={appBoards} onOpenBoard={openBoardCard} />}
        {tab === "dump"      && <BrainDump onConvert={addTask} boards={appBoards} user={user} />}
        {tab === "focus"     && <FocusTimer timerState={timerState} setTimerState={setTimerState} overlayKey={overlayKey} />}
        {tab === "community" && <CommunityTasks user={user} />}
        {tab === "about"     && <AboutTab />}
        {tab === "kanban" && <KanbanBoard user={user} focusCard={focusCard} onFocusHandled={() => setFocusCard(null)} dataEpoch={dataEpoch} />}
        {tab === "canvas" && <Canvas supabase={supabase} user={user} />}
      </div>
    </div>
  );
}